import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import React, {useState, useEffect,useContext, useRef, DetailedHTMLProps, HTMLAttributes, memo, useLayoutEffect, useCallback, useMemo } from 'react';
import { styled as MUIStyled} from '@mui/system';
import { useMediaQuery } from '@mui/material';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import CssBaseline from '@mui/material/CssBaseline';
import ArticleIcon from '@mui/icons-material/Article';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Typography from '@mui/material/Typography';
import { pdfjs } from "react-pdf";
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { muiLight, muiDark, scLight, scDark } from '../shared/Theme';
import Cutebot from "../static/images/cute_bot_transparent.png"
 
import Box from '@mui/material/Box';
import ChatControlContext, { ChatControlProviderProps } from '../shared/context/ChatControlContext';
import {
    API_BASE, TOAST_CONFIG,
    codeLanguageSubset, PLATFORM_WEBSITE
} from '../shared/Constants';
import { useAuth0 } from "@auth0/auth0-react";
import { ToastContainer,toast } from 'react-toastify';

import styled, { ThemeProvider as StyledThemeProvider, keyframes } from "styled-components";
import { SSE } from "../utils/sse.js";
import { reportHandledError } from "../utils/reportError";
import { shouldReportSseError } from "../utils/sseError";
import {
  parseSSEFrames,
  parseStreamData,
  buildBgStreamUrl,
  INACTIVITY_TIMEOUT_MS,
  getReconnectDelayMs,
  MAX_RECONNECT_ATTEMPTS,
  isDeepResearchEvent,
} from "../utils/streamConsumer";
import ReactMarkdown, { uriTransformer } from 'react-markdown'
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import CodeBlock from '../components/code/CodeBlock';
import TableBlock from '../components/code/TableBlock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import maritacaLogo from '../static/images/maritaca_logo.png';
import maritacaLogoPreta from '../static/images/maritaca_logo_preta.png';
import maritacaLogoBranca from '../static/images/maritaca_logo_branca.png';
import MaritacaBirdLogo from '../components/shared/MaritacaBirdLogo';
import { getModelColor } from '../shared/Constants';
// Removed fixed examples to simplify landing page
import { ChatMessage, Reference, GeneratedFile, FileRef, ContentItem, TextContentItem, FileRefContentItem, FileContentItem, MessageComposerContentItem, ToolCallEntry } from '../shared/interfaces';
import { downloadChatFileBlob } from '../shared/services/chatFilesApi';
// react-media-recorder removed — replaced by streaming STT via ElevenLabs WebSocket
import 'katex/dist/katex.min.css';
import SubscriptionModal from '../components/subscription/SubscriptionModal';
import HelpForm from '../components/help/HelpForm';
import { AccountModal, FilePreviewModal, ERPConnectionModal } from '../components/modals';
import { FeedbackModal as FeedbackModalExtracted } from '../components/feedback';
import { PlaygroundProvider, PlaygroundContextValue } from '../shared/context/PlaygroundContext';
import { PreciseLocation, requestPreciseLocation, geolocationPermissionState } from '../shared/utils/preciseLocation';
import LocationPromptModal from '../components/playground/LocationPromptModal';
import { mergeUniqueChats } from '../components/playground/utils';
import { useCustomModels } from '../shared/context/CustomModelsContext';
import { useFeatureFlags } from '../shared/context/FeatureFlagsContext';

// Playground refactored components
import {
  SideMenu,
  ChatHeader,
  ChatInputArea,
  MessageList,
  stopAllTts,
} from '../components/playground';
import { CategoryCards, PromptBankModal, PromptFillerView, Prompt } from '../components/prompts';
import InlineCitation from '../components/chat/InlineCitation';
import ProjectsView from '../components/projects/ProjectsView';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CustomTooltip = styled(({ className, ...props }) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
});

type ExtractedFileInfo = {
  file_id: string;
  filename: string;
  file_content: string;
};

type PlaceholderVariant = 'pdf' | 'image' | 'file' | 'textCsv' | 'textTxt' | 'textMd' | 'docx' | 'xlsx';

type TextPreview = {
  content: string;
  truncated: boolean;
};

type LogoVariant = 'light' | 'dark';
type LogoLoadState = Record<LogoVariant, boolean>;

const SIDEBAR_EXPANDED_WIDTH = 368;
const SIDEBAR_COLLAPSED_WIDTH = 70;
const SIDEBAR_EXPANDED_MIN_WIDTH = 220;
const SIDEBAR_COLLAPSED_MIN_WIDTH = 70;
const SIDEBAR_EXPANDED_WIDTH_RATIO = 0.22;
const SIDEBAR_COLLAPSED_WIDTH_RATIO = 0.06;
const DEFAULT_VIEWPORT_WIDTH = 1280;
const LOGO_SOURCES = [maritacaLogo, maritacaLogoPreta, maritacaLogoBranca];

let sidebarLogosPreloaded = false;
const ensureSidebarLogosPreloaded = () => {
  if (sidebarLogosPreloaded || typeof window === 'undefined' || typeof Image === 'undefined') {
    return;
  }

  sidebarLogosPreloaded = true;
  LOGO_SOURCES.forEach((src) => {
    const img = new Image();
    img.src = src;
    // img.decode() removed — causes "Can't open this page" crash on iOS WebKit
    // after 2-3 page reloads (see PR #571). Setting img.src is sufficient.
  });
};

if (typeof window !== 'undefined') {
  ensureSidebarLogosPreloaded();
}

const getViewportWidth = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEWPORT_WIDTH;
  }

  return (
    window.visualViewport?.width ??
    window.innerWidth ??
    document.documentElement?.clientWidth ??
    DEFAULT_VIEWPORT_WIDTH
  );
};

const getResponsiveSidebarWidth = (collapsed: boolean, viewportWidth: number) => {
  const ratio = collapsed ? SIDEBAR_COLLAPSED_WIDTH_RATIO : SIDEBAR_EXPANDED_WIDTH_RATIO;
  const minWidth = collapsed ? SIDEBAR_COLLAPSED_MIN_WIDTH : SIDEBAR_EXPANDED_MIN_WIDTH;
  const maxWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const scaledWidth = viewportWidth * ratio;
  return Math.round(Math.min(maxWidth, Math.max(minWidth, scaledWidth)));
};

const SIDEBAR_CONTENT_GAP = 40;

const MAX_ATTACHMENT_ITEMS = 100;
const MAX_ATTACHMENT_PAGES = 1000;
const CONDENSED_ATTACHMENT_THRESHOLD = 9;
const TEXT_PREVIEW_CHAR_LIMIT = 5000;
// Upload size limits — checked client-side so the user gets immediate feedback
// instead of a late backend 400 (the backend only caps the 1 GB aggregate).
const MAX_FILE_BYTES = 50 * 1024 * 1024;          // 50 MB por arquivo
const MAX_TOTAL_FILES_BYTES = 100 * 1024 * 1024;  // 100 MB somando os anexos da mensagem
// Cap for the preview MODAL only (the lightweight cached preview stays at
// TEXT_PREVIEW_CHAR_LIMIT). Opening a preview reads the full file; this just
// keeps a huge file from freezing the browser. Covers normal uploads in full.
const PREVIEW_FULL_MAX_CHARS = 5_000_000;
const MAX_AUDIO_DURATION_MS = 60 * 60 * 1000;
const MAX_AUDIO_DURATION_TEXT = '1 hora';
type QuotaInfoVariant = 'default' | 'remaining';
const DEFAULT_CHAT_MODEL = 'sabia-4-thinking';
const GREETING_TYPING_INTERVAL_MS = 75;
const INPUT_BASE_BOTTOM_OFFSET_PX = 32;

function App() {
  
  const [dark, setDark] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefers-dark');
    return saved ? JSON.parse(saved) : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const toggleDark = () =>
    setDark(d => { localStorage.setItem('prefers-dark', JSON.stringify(!d)); return !d; });
  useLayoutEffect(() => {
    ensureSidebarLogosPreloaded();
  }, []);
  const [sidebarLogosLoaded, setSidebarLogosLoaded] = useState<LogoLoadState>({
    light: false,
    dark: false,
  });

  // Preload both sidebar logo variants with high priority to avoid flicker when collapsing after a theme switch
  useEffect(() => {
    let cancelled = false;

    const preloadLogo = (variant: LogoVariant, src: string) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);

      const img = new Image();
      let settled = false;

      const markLoaded = () => {
        if (settled || cancelled) return;
        settled = true;
        setSidebarLogosLoaded((prev) => (prev[variant] ? prev : { ...prev, [variant]: true }));
      };

      img.onload = markLoaded;
      img.onerror = markLoaded;
      img.src = src;

      // img.decode() removed — causes iOS WebKit crash on repeated reloads (see PR #571)
      if (img.complete) {
        markLoaded();
      }

      return () => {
        link.remove();
        img.onload = null;
        img.onerror = null;
      };
    };

    const cleanups: Array<() => void> = [
      preloadLogo('light', maritacaLogoPreta),
      preloadLogo('dark', maritacaLogoBranca),
    ];

    return () => {
      cancelled = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  // Ref to track if we've already redirected to avoid multiple redirects
  useEffect(() => {
    const checkStatus = async () => {
      const endpoint= API_BASE + "/api/status";

      try {
        const response = await fetch(endpoint);
        if (response.status === 200) {
          const data= await response.json();
          if (data.message !== ""){

            toast.info(data.message, TOAST_CONFIG);
          }
        }
      } catch (error) {
        console.warn("Não foi possível consultar o status da API.", error);
      }
    };
    if (isAuthenticated){
      checkStatus();
    }
  }, []);

  const MessageAreaDivRef = useRef<HTMLDivElement>(null);

  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently} = useAuth0();
  const featureFlags = useFeatureFlags();
  const { activeModel, activeModelId, getActiveSystemPrompt, getActiveModelName } = useCustomModels();

  // Handle URL errors like the platform does
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCode = params.has('code');
    const hasError = params.has('error');

    if (!isLoading && !isAuthenticated) {
      if (hasError) {
        const search = params.toString();
        window.location.href = `/auth${search ? `?${search}` : ''}`;
        return;
      }
      if (!hasCode) {
        window.location.href = "/auth";
      }
    }
  }, [isLoading, isAuthenticated]);

  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [showProjectsView, setShowProjectsView] = useState(false);
  useEffect(() => {
    const handler = () => setShowProjectsView(true);
    window.addEventListener("openProjectsView", handler);
    const detailHandler = (e: any) => {
      setShowProjectsView(true);
      setTimeout(() => window.dispatchEvent(new CustomEvent("navigateToProject", { detail: e.detail })), 100);
    };
    window.addEventListener("openProjectDetail", detailHandler);
    return () => {
      window.removeEventListener("openProjectsView", handler);
      window.removeEventListener("openProjectDetail", detailHandler);
    };
  }, []);

  useEffect(() => {
    const prefillHandler = (e: any) => {
      const msg = e.detail;
      if (msg) setCurrentMessage(msg);
    };
    window.addEventListener("prefillChatMessage", prefillHandler);
    return () => window.removeEventListener("prefillChatMessage", prefillHandler);
  }, []);

  useEffect(() => {
    const handler = (e: any) => {
      const chatData = e.detail;
      if (chatData && chatData.chat_id) {
        setUserChats((prev: any[]) => [{ ...chatData, updated_at: new Date().toISOString() }, ...prev]);
      }
    };
    window.addEventListener("addChatToList", handler);
    return () => window.removeEventListener("addChatToList", handler);
  }, []);


  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const [messageBeingGenerated, setMessageBeingGenerated] = useState<string>("");
  const [generatingChatId, setGeneratingChatId] = useState<string>("");
  // ═══════════════════════════════════════════════════════════════════════
  // BACKGROUND GENERATION CONCURRENCY MODEL
  //
  // When a user navigates away from a chat while the model is generating:
  //
  // 1. USER CLICKS "NEW CHAT" or clicks another chat:
  //    - Frontend: abortCurrentGeneration() closes the SSE connection
  //    - Frontend: chatId is added to backgroundGeneratingChatIds
  //    - Backend: detects SSE disconnect in the finally block of _streaming_response()
  //    - Backend: checks Redis bg_gen:{chatId} — if NOT "done", spawns
  //      continue_generation_in_background() as an asyncio Task
  //    - Backend: the background task continues generating and saves to MongoDB
  //
  // 2. WHILE CHAT IS GENERATING IN BACKGROUND:
  //    - For chats NOT being viewed: a polling interval (3s) calls
  //      /api/chat/background-status/{chatId} to check Redis bg_gen:{chatId}
  //    - When status = "done", chatId is moved from backgroundGeneratingChatIds
  //      to completedBackgroundChatIds (shows a "completed" badge in sidebar)
  //
  // 3. USER RETURNS TO THE BACKGROUND-GENERATING CHAT:
  //    - Frontend detects backgroundGeneratingChatIds.has(chatId) === true
  //    - Frontend connects to /api/chat/bg-stream/{chatId} SSE endpoint
  //    - Backend: bg_stream() reads partial text from Redis bg_partial:{chatId}
  //      and sends periodic SSE updates with the current generated text
  //    - Frontend updates messageHistory with the streaming text
  //    - If no assistant message exists in messageHistory (only user message
  //      was saved), frontend creates one
  //    - When "end" event arrives: refresh full history from MongoDB
  //
  // 4. USER CLICKS "STOP" on a background-generating chat:
  //    - Frontend: handleStopClick() calls /api/chat/background-cancel/{chatId}
  //    - Backend: sets Redis bg_gen:{chatId} = "done"
  //    - Backend: the background task checks this flag and stops
  //    - Frontend: closes SSE, removes from backgroundGeneratingChatIds
  //
  // KEY INVARIANT: a chatId should be in exactly one of these states:
  //   - generatingChatId (foreground SSE, user is watching)
  //   - backgroundGeneratingChatIds (SSE closed, backend task running)
  //   - completedBackgroundChatIds (backend done, user hasn't seen yet)
  //   - none (idle)
  //
  // REDIS KEYS:
  //   bg_gen:{chatId}     = "generating" | "done" (TTL 300s)
  //   bg_partial:{chatId} = latest generated text (updated by background task)
  // ═══════════════════════════════════════════════════════════════════════
  const [backgroundGeneratingChatIds, setBackgroundGeneratingChatIds] = useState<Set<string>>(new Set());
  const [completedBackgroundChatIds, setCompletedBackgroundChatIds] = useState<Set<string>>(new Set());
  const [cancellingBackground, setCancellingBackground] = useState(false);

  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [pendingSystemPrefix, setPendingSystemPrefix] = useState<string | undefined>(undefined);
  const [openSidebar, setOpenSidebar] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [sidebarWidth, setSidebarWidth] = useState<number>(() =>
    getResponsiveSidebarWidth(
      (() => {
        if (typeof window === 'undefined') return false;
        const saved = localStorage.getItem('sidebar-collapsed');
        return saved ? JSON.parse(saved) : false;
      })(),
      getViewportWidth()
    )
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  const [isAtBottom, setIsAtBottom] = useState(true);

  const [hasMoreChats, setHasMoreChats] = useState<boolean>(true);
  const [loadingChatHistory, setLoadingChatHistory] = useState<boolean>(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<object[] | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [numPages, setNumPages] = useState<{ [key: string]: number } | null>(null);

  //Variables for rename chat functionality
  const [isFirstMessage, setIsFirstMessage] = useState<boolean>(true);
  const [animatedTitle, setAnimatedTitle] = useState("");

  //Variables for new chat functionality
  const [chatStatus, setChatStatus] = useState<{ [key: string]: boolean }>({}); 
  
  // Turn on/off web search
  const [webSearch, setWebSearch] = useState<boolean>(true);

  // Turn on/off Data Ocean tools (default on; only effective for models in
  // DATA_OCEAN_TOGGLE_MODELS — competidores always have it).
  const [dataOcean, setDataOcean] = useState<boolean>(true);

  // Turn on/off the reasoning scratchpad tool (default on).
  const [reasoning, setReasoning] = useState<boolean>(true);

  // Turn on/off code execution
  const [codeExecution, setCodeExecution] = useState<boolean>(true);

  // "Usar competidor": route the turn to the competitor model (Opus 4.8 via
  // OpenRouter). Default off. The toggle is only rendered when the build flag
  // REACT_APP_ENABLE_COMPETITOR === 'true', and the backend only honors it when
  // enable_competitor_model is set — so it never reaches prod active.
  const [useCompetitor, setUseCompetitor] = useState<boolean>(false);

  // Deep Research — `deepResearch` is the per-input toggle; the *running*
  // state is per-chat (see the DeepResearchState map below, declared once
  // activeChatID is available) so switching chats can't leak one chat's
  // progress into another or wipe the in-flight panel when you return.
  const [deepResearch, setDeepResearch] = useState<boolean>(false);
  const [deepResearchMode, setDeepResearchMode] = useState<"full" | "light">("full");

  // Precise location (opt-in). IP geolocation is coarse/unreliable; when the
  // user enables this we ask the browser for GPS-level location (one permission
  // prompt), reverse-geocode it client-side, and send the resolved
  // city/neighborhood with each message. The backend prefers it over IP.
  const [usePreciseLocation, setUsePreciseLocation] = useState<boolean>(() => {
    return typeof window !== 'undefined' && localStorage.getItem('maritalk-use-precise-location') === 'true';
  });
  const [preciseLocation, setPreciseLocation] = useState<PreciseLocation | null>(() => {
    if (typeof window === 'undefined') return null;
    try { const s = localStorage.getItem('maritalk-precise-location-v3'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [locatingUser, setLocatingUser] = useState<boolean>(false);
  // Fresh value for SSE handlers / request bodies built at send time.
  const preciseLocationRef = useRef<PreciseLocation | null>(preciseLocation);
  useEffect(() => { preciseLocationRef.current = preciseLocation; }, [preciseLocation]);
  const usePreciseLocationRef = useRef<boolean>(usePreciseLocation);
  useEffect(() => { usePreciseLocationRef.current = usePreciseLocation; }, [usePreciseLocation]);

  const togglePreciseLocation = useCallback(async () => {
    if (usePreciseLocationRef.current) {
      // Turning OFF — drop the stored location + preference (fall back to IP).
      setUsePreciseLocation(false);
      setPreciseLocation(null);
      localStorage.setItem('maritalk-use-precise-location', 'false');
      localStorage.removeItem('maritalk-precise-location-v3');
      localStorage.removeItem('maritalk-precise-location-v2'); // drop legacy resolved-name caches
      localStorage.removeItem('maritalk-precise-location');
      return;
    }
    // Turning ON — request permission + resolve. Stays off on denial/failure.
    setLocatingUser(true);
    const loc = await requestPreciseLocation();
    setLocatingUser(false);
    if (loc) {
      setPreciseLocation(loc);
      setUsePreciseLocation(true);
      localStorage.setItem('maritalk-use-precise-location', 'true');
      localStorage.setItem('maritalk-precise-location-v3', JSON.stringify(loc));
    } else {
      setUsePreciseLocation(false);
      localStorage.setItem('maritalk-use-precise-location', 'false');
    }
  }, []);

  // On load, if the user previously enabled precise location AND the browser
  // permission is still granted, silently refresh it (no prompt).
  useEffect(() => {
    if (!usePreciseLocation) return;
    geolocationPermissionState().then((state) => {
      if (state === 'granted') {
        requestPreciseLocation().then((loc) => {
          if (loc) { setPreciseLocation(loc); localStorage.setItem('maritalk-precise-location-v3', JSON.stringify(loc)); }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Soft pre-prompt: once after login, offer precise location (much higher
  // adoption than the hidden "+" toggle). Shown only if not already enabled and
  // not dismissed within the last ~30 days. "Permitir" triggers the real browser
  // prompt (tied to the click), "Agora não" snoozes for 30 days.
  const [showLocationPrompt, setShowLocationPrompt] = useState<boolean>(false);
  useEffect(() => {
    if (usePreciseLocation) return; // already on — never nag
    try {
      const dismissedAt = Number(localStorage.getItem('maritalk-location-prompt-dismissed-at') || 0);
      if (dismissedAt && Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000) return;
    } catch { /* ignore */ }
    const t = setTimeout(() => setShowLocationPrompt(true), 1500); // let the app settle first
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleLocationPromptAllow = useCallback(async () => {
    setShowLocationPrompt(false);
    await togglePreciseLocation(); // triggers the browser permission prompt + enables
  }, [togglePreciseLocation]);
  const handleLocationPromptDismiss = useCallback(() => {
    setShowLocationPrompt(false);
    try { localStorage.setItem('maritalk-location-prompt-dismissed-at', String(Date.now())); } catch { /* ignore */ }
  }, []);

  // Debug mode — toggle with Ctrl+Shift+D or ?debug=1
  const [debugMode, setDebugMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1') return true;
    return typeof window !== 'undefined' && localStorage.getItem('maritalk-debug-mode') === 'true';
  });
  const debugModeRef = useRef(debugMode);
  useEffect(() => { debugModeRef.current = debugMode; }, [debugMode]);
  // Mirror the reasoning toggle into a ref so SSE event handlers (set up per
  // request) read the current value without stale-closure issues.
  const reasoningRef = useRef(reasoning);
  useEffect(() => { reasoningRef.current = reasoning; }, [reasoning]);
  const toggleDebugMode = useCallback(() => {
    setDebugMode(d => {
      const next = !d;
      localStorage.setItem('maritalk-debug-mode', String(next));
      return next;
    });
  }, []);
  const [currentToolCalls, setCurrentToolCalls] = useState<ToolCallEntry[]>([]);
  const toolCallsAccRef = useRef<ToolCallEntry[]>([]);

  // Keyboard shortcut: Ctrl+Shift+D / Cmd+Shift+D
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        toggleDebugMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleDebugMode]);

  //Variables for feedback functionality
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null); // Stores the index of the message receiving a like or dislike
  const [showFeedback, setShowFeedback] = useState(false); // Controls the visibility of the feedback modal with reasons
  const [messageFeedback, setMessageFeedback] = useState<{[messageId: number]: string}>({});

  
  // SSE loading message (e.g. "Generating response...")
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  // Filenames in the current upload batch, shown in the collapsible
  // "Extraindo arquivos" block while they're uploaded/OCR'd on the backend.
  const [extractingFiles, setExtractingFiles] = useState<string[]>([]);

  // OCR/rendering control flags
  const [inOcrPhase, setInOcrPhase] = useState<boolean>(false);
  // Drop the extracting-files block once both the upload and OCR phases end
  // (one effect instead of clearing at every uploadingFiles/inOcrPhase reset).
  useEffect(() => {
    if (!uploadingFiles && !inOcrPhase && extractingFiles.length > 0) {
      setExtractingFiles([]);
    }
  }, [uploadingFiles, inOcrPhase, extractingFiles.length]);
  const [currentUploadHasPdf, setCurrentUploadHasPdf] = useState<boolean>(false);

  // Context for streaming STT via ElevenLabs WebSocket
  const [isRecording, setIsRecording] = useState(false);
  const [sendingAudio, setSendingAudio] = useState(false);
  const [audioTooltipOpen, setAudioTooltipOpen] = useState(false);
  const [partialTranscript, setPartialTranscript] = useState("");
  const [sttTranscribing, setSttTranscribing] = useState(false);
  const sttWsRef = useRef<WebSocket | null>(null);
  const sttAudioContextRef = useRef<AudioContext | null>(null);
  const sttMicStreamRef = useRef<MediaStream | null>(null);
  const sttProcessorRef = useRef<ScriptProcessorNode | null>(null);
  // status is still used by ChatInputArea — derive from isRecording/sendingAudio
  const status = isRecording ? "recording" : "idle";

  // Error message state for chat display
  const [chatErrorMessage, setChatErrorMessage] = useState<string>("");
  const [allowErrorRetry, setAllowErrorRetry] = useState<boolean>(true);
  const [showPlanUpgradeOption, setShowPlanUpgradeOption] = useState<boolean>(false);

  // Prompt Bank state for landing page
  const [promptBankOpen, setPromptBankOpen] = useState(false);
  const [promptBankInitialCategory, setPromptBankInitialCategory] = useState<string | undefined>(undefined);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [promptsRefreshTrigger, setPromptsRefreshTrigger] = useState(0);

  const updateChatStatus = (chatId: string) => {
    setChatStatus((prevStatus) => ({
        ...prevStatus,
        [chatId]: true,
    }));
  };

  const animateTitle = (title: string) => {
    let index = 0;
    const interval = setInterval(() => {
      setAnimatedTitle(title.slice(0, index + 1));
      
      index++;
      if (index === title.length) {
        clearInterval(interval);
      }
    }, 100);
  };

  const formatChatTitle = (raw: string): string => {
    if (!raw) return '';
    // Remove most emoji and pictographs
    const noEmoji = raw.replace(/[\u{1F300}-\u{1FAFF}\u{1F900}-\u{1F9FF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '');
    // Collapse whitespace
    let t = noEmoji.replace(/\s+/g, ' ').trim();
    // Remove excessive trailing punctuation
    t = t.replace(/[.,;:!?…—-]+$/u, '').trim();
    // Capitalize first letter, keep the rest as-is
    if (t.length > 0) t = t.charAt(0).toUpperCase() + t.slice(1);
    // Truncate to a clean length
    const limit = 50;
    if (t.length > limit) t = t.slice(0, limit).trimEnd() + '…';
    return t;
  };

  const modelsWithFileUploadAllowed = ["competidor", "competidor-2", "competidor-3", 'sabiazinho-4', 'sabia-4', 'sabia-4-thinking'];
  const modelsWithWebSearchAllowed = ["competidor", "competidor-2", "competidor-3", 'sabiazinho-4', 'sabia-4', 'sabia-4-thinking'];
  const modelsWithCodeExecutionAllowed = ["competidor", "competidor-2", "competidor-3", 'sabiazinho-4', 'sabia-4', 'sabia-4-thinking'];
  // Models that can use Data Ocean. sabia-4/sabiazinho-4 expose it as a toggle
  // (default on); the competidores always have it (no toggle).
  const DATA_OCEAN_MODEL_SET = new Set(['sabia-4', 'sabia-4-thinking', 'sabiazinho-4', "competidor", "competidor-2", "competidor-3"]);
  // Models for which Data Ocean is a user toggle (vs always-on for competidores).
  const DATA_OCEAN_TOGGLE_MODELS = new Set(['sabia-4', 'sabia-4-thinking', 'sabiazinho-4']);
  // Competidores (GPT/Opus/Qwen, benchmark models) are NOT offered in the chat
  // dropdown — only Sabiá-4 / Sabiazinho-4. They stay in modelDescriptions +
  // capability arrays so chats saved on a competidor still resolve.
  const allDropdownMenuOptions: string[] = ["sabia-4-thinking", "sabia-4"];  // seletor removido da UI; o toggle Pensamento alterna entre estes dois (sabiazinho-4 saiu do chat)
  const dropdownMenuOptions = allDropdownMenuOptions;
  // gen-3 models (sabia-3, sabia-3.1, sabiazinho-3) were removed from the web
  // chat dropdown — sabia-3 and sabia-3.1 are soft-redirected to the
  // sabiazinho-4 worker and would only confuse users by self-identifying
  // differently; sabiazinho-3 still has its own backend but is no longer
  // promoted in the UI. They remain accessible via the API for backwards
  // compatibility.
  const modelDescriptions: {[key: string]: [string, string[]]} = {
    'competidor': ['Competidor', ['GPT-5.2 via OpenAI', 'Comparação de performance']],
    'competidor-2': ['Competidor 2', ['Claude Opus 4.6 via Anthropic', 'Comparação de performance']],
    'competidor-3': ['Competidor 3', ['Qwen3.5-397B via OpenRouter', 'Comparação de performance']],
    'sabiazinho-4': ['Sabiazinho-4', ['Mais rápido — Data Ocean + web', 'Versão 2026-01-06']],
    'sabia-4': ['Sabiá-4', ['Data Ocean + web (ajustáveis no +)', 'Versão 2026-01-06']],
    'sabia-4-thinking': ['Sabiá-4 Thinking', ['Raciocina antes de responder', 'Mesma base do Sabiá-4']],
  };
  const [openDropdownMenu, setOpenDropdownMenu] = useState<boolean>(false);

  const {
    activeChatID,
    messageHistory,
    userChats,
    modelChat,
    loadingHistory,
    loadingInitialChats,
    setMessageHistory,
    setUserChats,
    setActiveChatID,
    setModelChat } = useContext(ChatControlContext) as ChatControlProviderProps;

  // Reasoning é exclusivo do sabia-4-thinking: o estado deixou de ser um
  // toggle do usuário (removido do menu +) e passa a derivar do modelo
  // selecionado — o backend também ignora body.reasoning e deriva do modelo;
  // aqui mantemos o estado em sincronia para os hints de loading e o body.
  useEffect(() => {
    setReasoning(modelChat === 'sabia-4-thinking');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelChat]);


  // Stop TTS playback when switching conversations
  useEffect(() => {
    stopAllTts();
  }, [activeChatID.chatID]);

  // Force webSearch=true for the competidores (they always run all tools, no
  // toggle). sabia-4/sabiazinho-4 keep web search as a user toggle.
  React.useEffect(() => {
    if (['competidor', 'competidor-2', 'competidor-3'].includes(modelChat)) {
      setWebSearch(true);
    }
  }, [modelChat]);

  // Competidores were removed from the dropdown — if a chat is still on one
  // (e.g. an old conversation), switch to the default Sabiá-4.
  useEffect(() => {
    if (["competidor", "competidor-2", "competidor-3"].includes(modelChat)) {
      setModelChat(DEFAULT_CHAT_MODEL);
    }
  }, [modelChat]);

  // Derived boolean — true when the active chat is generating (foreground SSE or background task).
  const loadingGeneration = (generatingChatId !== "" && generatingChatId === activeChatID.chatID)
    || backgroundGeneratingChatIds.has(activeChatID.chatID);

  // Per-chat Deep Research state. Each chat keeps its own DR snapshot so
  // switching conversations (e.g. while a DR runs in chat A) can't leak chat
  // A's progress into chat B or wipe it when we come back. Both the live SSE
  // listeners and the bg-stream resume path write here, keyed by chatId.
  type DeepResearchState = {
    phase: string;
    progress: number;
    plan: any[];
    steps: Record<number, any>;
    tokens: { input: number; output: number };
  };
  const EMPTY_DR_STATE: DeepResearchState = { phase: "", progress: 0, plan: [], steps: {}, tokens: { input: 0, output: 0 } };
  const [deepResearchByChat, setDeepResearchByChat] = useState<Record<string, DeepResearchState>>({});
  const updateDeepResearchForChat = useCallback(
    (chatId: string, updater: (prev: DeepResearchState) => DeepResearchState) => {
      if (!chatId) return;
      setDeepResearchByChat(prev => ({ ...prev, [chatId]: updater(prev[chatId] || EMPTY_DR_STATE) }));
    },
    []
  );
  const clearDeepResearchForChat = useCallback((chatId: string) => {
    if (!chatId) return;
    setDeepResearchByChat(prev => {
      if (!prev[chatId]) return prev;
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  }, []);
  // [restored] DR display state comes from the per-chat map (main's working path).
  // Handlers write via updateDeepResearchForChat(chatId, ...); the UI must read
  // from deepResearchByChat. The prior global useState was never populated, so
  // DeepResearchProgress never rendered.
  const activeDeepResearch = deepResearchByChat[activeChatID.chatID] || EMPTY_DR_STATE;
  const deepResearchPhase = activeDeepResearch.phase;
  const deepResearchProgress = activeDeepResearch.progress;
  const deepResearchPlan = activeDeepResearch.plan;
  const deepResearchSteps = activeDeepResearch.steps;
  const deepResearchTokens = activeDeepResearch.tokens;

  // Panorama Juridico: progresso da pesquisa (espelha o padrao do Deep Research, por-chat).
  type PanoramaState = { phase: string; progress: number; nSearches: number; nDocs: number; nTheses: number; inputTokens: number; outputTokens: number; detail: string };
  const EMPTY_PANORAMA_STATE: PanoramaState = { phase: "", progress: 0, nSearches: 0, nDocs: 0, nTheses: 0, inputTokens: 0, outputTokens: 0, detail: "" };
  const [panoramaByChat, setPanoramaByChat] = useState<Record<string, PanoramaState>>({});
  const updatePanoramaForChat = useCallback(
    (chatId: string, updater: (prev: PanoramaState) => PanoramaState) => {
      if (!chatId) return;
      setPanoramaByChat(prev => ({ ...prev, [chatId]: updater(prev[chatId] || EMPTY_PANORAMA_STATE) }));
    },
    []
  );
  const clearPanoramaForChat = useCallback((chatId: string) => {
    if (!chatId) return;
    setPanoramaByChat(prev => {
      if (!prev[chatId]) return prev;
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  }, []);
  const activePanorama = panoramaByChat[activeChatID.chatID] || EMPTY_PANORAMA_STATE;
  const panoramaPhase = activePanorama.phase;
  const panoramaProgress = activePanorama.progress;
  const panoramaNSearches = activePanorama.nSearches;
  const panoramaNDocs = activePanorama.nDocs;
  const panoramaNTheses = activePanorama.nTheses;
  const panoramaInputTokens = activePanorama.inputTokens;
  const panoramaOutputTokens = activePanorama.outputTokens;
  const panoramaDetail = activePanorama.detail;

  // Refs mirroring state so SSE event listeners (whose closures capture values
  // once at attach time) can read fresh values without re-binding.
  const generatingChatIdRef = useRef<string>("");
  useEffect(() => { generatingChatIdRef.current = generatingChatId; }, [generatingChatId]);
  const activeChatIDRef = useRef<string>("");
  useEffect(() => { activeChatIDRef.current = activeChatID.chatID; }, [activeChatID.chatID]);

  // Extract citation numbers from text content (handles both single and multiple citations)
  const extractCitedReferenceNumbers = useCallback((content: string): Set<number> => {
    const citedNumbers = new Set<number>();
    // Match both [REFERENCIA_1] and [REFERENCIA_1, REFERENCIA_2, REFERENCIA_5]
    const citationPattern = /\[(REFER[EÊ]NCIA_\d+(?:\s*,\s*REFER[EÊ]NCIA_\d+)*)\]/g;
    let match;
    while ((match = citationPattern.exec(content)) !== null) {
      // Extract all REFERENCIA_N from the match
      const innerPattern = /REFER[EÊ]NCIA_(\d+)/g;
      let innerMatch;
      while ((innerMatch = innerPattern.exec(match[1])) !== null) {
        citedNumbers.add(parseInt(innerMatch[1], 10));
      }
    }
    return citedNumbers;
  }, []);

  // Collect all references from the entire message history
  // This allows messages to cite references from previous messages
  const getAllReferencesFromHistory = useCallback((): Reference[] => {
    const allRefs: Reference[] = [];
    messageHistory.forEach((msg: any) => {
      if (msg.references && Array.isArray(msg.references)) {
        allRefs.push(...msg.references);
      }
    });
    return allRefs;
  }, [messageHistory]);

  // Resolve a `sandbox:/workspace/...` link (written by the model after a
  // code_exec_show) to the actual delivered file in THIS conversation and
  // download it. Files arrive as content items on the assistant message:
  // 'file_ref' (persisted, has file_id → GCS) or 'input_file' (streaming,
  // carries base64). We match by basename, newest message first so the most
  // recent delivery wins if names collide. Returns true if a file was found.
  const downloadSandboxFile = useCallback(async (href: string): Promise<boolean> => {
    const raw = href.replace(/^sandbox:/i, '');
    const basename = (raw.split('/').pop() || raw).trim();
    if (!basename) return false;
    const baseOf = (s: string) => (s || '').split('/').pop();

    const saveBlob = (blob: Blob, name: string) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    try {
      for (let i = messageHistory.length - 1; i >= 0; i--) {
        const content = (messageHistory[i] as any)?.content;
        if (!Array.isArray(content)) continue;
        for (const item of content) {
          if (item?.type === 'file_ref' && baseOf(item.title) === basename) {
            const token = await getAccessTokenSilently();
            const blob = await downloadChatFileBlob(item.file_id, token);
            saveBlob(blob, item.title || basename);
            return true;
          }
          if (item?.type === 'input_file' && baseOf(item.filename) === basename && item.file_data) {
            const bytes = Uint8Array.from(atob(item.file_data), c => c.charCodeAt(0));
            saveBlob(new Blob([bytes], { type: item.mime_type || 'application/octet-stream' }), basename);
            return true;
          }
        }
      }
    } catch (err) {
      console.error('[sandbox link] download falhou:', err);
      return false;
    }
    console.warn('[sandbox link] arquivo não encontrado na conversa:', basename);
    return false;
  }, [messageHistory, getAccessTokenSilently]);

  // Filter references to only show those that were actually cited in the content
  const getVisibleReferences = useCallback((references: Reference[] | undefined, currentMessageIndex: number, content?: string): Reference[] => {
    const refs = references ?? [];
    if (!content) {
      return refs;
    }

    // Extract which references were actually cited in the text
    const citedNumbers = extractCitedReferenceNumbers(content);

    // If no citations found, return empty (don't show uncited references)
    if (citedNumbers.size === 0) {
      return [];
    }

    // Filter to only include cited references from this message's own references
    return refs.filter(ref => {
      const refIndex = ref.index ?? (refs.indexOf(ref) + 1);
      return citedNumbers.has(refIndex);
    });
  }, [extractCitedReferenceNumbers]);

  // Get all cited references including those from history (for messages that cite previous searches)
  const getCitedReferencesWithHistory = useCallback((
    messageReferences: Reference[] | undefined,
    content?: string
  ): { references: Reference[]; hasHistoryRefs: boolean } => {
    if (!content) {
      return { references: messageReferences ?? [], hasHistoryRefs: false };
    }

    // Extract which references were actually cited in the text
    const citedNumbers = extractCitedReferenceNumbers(content);

    if (citedNumbers.size === 0) {
      return { references: [], hasHistoryRefs: false };
    }

    const ownRefs = messageReferences ?? [];
    const allHistoryRefs = getAllReferencesFromHistory();

    // First, collect refs from the message's own references
    const ownRefIndices = new Set(ownRefs.map(r => r.index));
    const citedOwnRefs = ownRefs.filter(ref => {
      const refIndex = ref.index ?? (ownRefs.indexOf(ref) + 1);
      return citedNumbers.has(refIndex);
    });

    // Then, find any citations that reference historical refs (not in own refs)
    const citedHistoryRefs: Reference[] = [];
    citedNumbers.forEach(num => {
      if (!ownRefIndices.has(num)) {
        // This citation references something not in own refs, look in history
        const histRef = allHistoryRefs.find(r => r.index === num);
        if (histRef) {
          citedHistoryRefs.push(histRef);
        }
      }
    });

    const allCitedRefs = [...citedOwnRefs, ...citedHistoryRefs];
    // Deduplicate by link to avoid showing the same source multiple times
    const seenLinks = new Set<string>();
    const dedupedRefs = allCitedRefs.filter(ref => {
      if (!ref.link || seenLinks.has(ref.link)) return false;
      seenLinks.add(ref.link);
      return true;
    });
    const hasHistoryRefs = citedHistoryRefs.length > 0;

    return { references: dedupedRefs, hasHistoryRefs };
  }, [extractCitedReferenceNumbers, getAllReferencesFromHistory]);

  const endOfChatMessageMarkerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [inputBottomOffset, setInputBottomOffset] = useState<number>(INPUT_BASE_BOTTOM_OFFSET_PX);
  const effectiveSidebarCollapsed = isMobile ? false : sidebarCollapsed;

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [messageBeingEdited, setMessageBeingEdited] = useState<string>("");
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  
  const [openUserMenu, setOpenUserMenu] = useState<boolean>(false);

  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [fileTextPreviews, setFileTextPreviews] = useState<Record<string, TextPreview>>({});
  const [fileOriginalUrls, setFileOriginalUrls] = useState<Record<string, string>>({});
  const [fileDownloadProgress, setFileDownloadProgress] = useState<Record<string, number>>({});
  const filePreviewsRef = useRef(filePreviews);
  const fileOriginalUrlsRef = useRef(fileOriginalUrls);
  const [apiError, setApiError] = useState<boolean>(false);
  const [chatExtractedFiles, setChatExtractedFiles] = useState<{ [chatId: string]: ExtractedFileInfo[] }>({});
  const [isOcrModalOpen, setIsOcrModalOpen] = useState<boolean>(false);
  const [ocrModalFiles, setOcrModalFiles] = useState<ExtractedFileInfo[]>([]);
  const [ocrModalTitle, setOcrModalTitle] = useState<string>('');
  const [loadingOcrModal, setLoadingOcrModal] = useState<boolean>(false);
  const [copiedOcrFileId, setCopiedOcrFileId] = useState<string | null>(null);
  const [selectedOcrFileIndex, setSelectedOcrFileIndex] = useState<number>(0);
  const [pendingOcrRefresh, setPendingOcrRefresh] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setSidebarWidth(getResponsiveSidebarWidth(effectiveSidebarCollapsed, getViewportWidth()));
    };

    handleResize();

    if (typeof window === 'undefined') return;

    window.addEventListener('resize', handleResize);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      viewport?.removeEventListener('resize', handleResize);
    };
  }, [effectiveSidebarCollapsed]);

  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebar-collapsed', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  useEffect(() => {
    filePreviewsRef.current = filePreviews;
  }, [filePreviews]);

  useEffect(() => {
    fileOriginalUrlsRef.current = fileOriginalUrls;
  }, [fileOriginalUrls]);

  useEffect(() => {
    setInputBottomOffset(INPUT_BASE_BOTTOM_OFFSET_PX);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleViewportChange = () => {
      const layoutHeight = window.innerHeight || viewport.height || 0;
      const viewportHeight = viewport.height || layoutHeight;
      const viewportOffsetTop = viewport.offsetTop || 0;
      const keyboardOffset = Math.max(0, layoutHeight - viewportHeight - viewportOffsetTop);
      setInputBottomOffset(INPUT_BASE_BOTTOM_OFFSET_PX + keyboardOffset);
    };

    handleViewportChange();
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);

    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
    };
  }, [isMobile]);

  /* dynamically adjusts the height of a textarea element to fit its content when a specific message is being edited */
  useEffect(() => {
    if (editingMessageId !== null) {
      const textarea = document.getElementById(`edit-textarea-${editingMessageId}`);
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, [messageBeingEdited, editingMessageId]);

  useEffect(() => {
    setSelectedOcrFileIndex((currentIndex) => {
      if (ocrModalFiles.length === 0) {
        return 0;
      }

      const boundedIndex = Math.min(currentIndex, ocrModalFiles.length - 1);
      return boundedIndex < 0 ? 0 : boundedIndex;
    });
  }, [ocrModalFiles]);

  const pendingNewCycleRef = useRef<boolean>(false); // set true when a tool signal is received
  const sseConnectionRef = useRef<any>(null);
  const storeSseConnection = (connection: any) => {
    if (sseConnectionRef.current && sseConnectionRef.current !== connection) {
      sseConnectionRef.current.close();
    }
    sseConnectionRef.current = connection;
  };
  const keyboardOffset = useMemo(
    () => Math.max(0, inputBottomOffset - INPUT_BASE_BOTTOM_OFFSET_PX),
    [inputBottomOffset]
  );

  // Lightbox preview state
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [previewType, setPreviewType] = useState<'pdf' | 'image' | 'text' | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string>("");
  const [previewTextTruncated, setPreviewTextTruncated] = useState<boolean>(false);
  // OCR/extracted-text tab for the file-preview modal.
  const [previewOcrText, setPreviewOcrText] = useState<string | null>(null);
  const [previewOcrAvailable, setPreviewOcrAvailable] = useState<boolean>(false);
  const [previewOcrLoading, setPreviewOcrLoading] = useState<boolean>(false);
  const textPreviewLimitLabel = PREVIEW_FULL_MAX_CHARS.toLocaleString('pt-BR');

  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  const isImageFile = (filename: string) => {
    const lower = filename.toLowerCase();
    return lower.endsWith('.png')
      || lower.endsWith('.jpg')
      || lower.endsWith('.jpeg')
      || lower.endsWith('.gif')
      || lower.endsWith('.bmp')
      || lower.endsWith('.webp')
      || lower.endsWith('.tiff')
      || lower.endsWith('.svg');
  };

  const getTextPlaceholderVariant = (filename: string): 'textCsv' | 'textTxt' | 'textMd' | null => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.csv')) {
      return 'textCsv';
    }
    if (lower.endsWith('.txt')) {
      return 'textTxt';
    }
    if (lower.endsWith('.md')) {
      return 'textMd';
    }
    return null;
  };

  const getPlaceholderVariant = (filename: string): PlaceholderVariant => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.docx')) {
      return 'docx';
    }
    if (lower.endsWith('.xlsx')) {
      return 'xlsx';
    }
    if (isPdfFile(filename)) {
      return 'pdf';
    }
    if (isImageFile(filename)) {
      return 'image';
    }
    const textVariant = getTextPlaceholderVariant(filename);
    if (textVariant) {
      return textVariant;
    }
    return 'file';
  };

  const isTextLikeFile = (file: File) => {
    const type = file.type?.toLowerCase?.() ?? '';
    if (type === 'text/plain') {
      return true;
    }
    if (type === 'text/markdown') {
      return true;
    }
    if (type === 'text/csv' || type === 'application/csv' || type === 'application/vnd.ms-excel') {
      return true;
    }
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return true;
    }
    if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return true;
    }
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.docx') || lowerName.endsWith('.xlsx')) {
      return true;
    }
    if (lowerName.endsWith('.html') || lowerName.endsWith('.htm')
        || lowerName.endsWith('.xml') || lowerName.endsWith('.rtf')) {
      return true;
    }
    return getTextPlaceholderVariant(file.name) !== null;
  };

  const placeholderTypeLabels: Record<PlaceholderVariant, string> = {
    pdf: 'PDF',
    image: 'Imagem',
    file: 'Arquivo',
    textCsv: 'CSV',
    textTxt: 'TXT',
    textMd: 'MD',
    docx: 'DOCX',
    xlsx: 'XLSX',
  };

  const formatPageCountLabel = (count: number) => `${count} página${count === 1 ? '' : 's'}`;

  const getFileKey = (file: File) => `${file.name}-${file.lastModified}`;

  const getVariantIcon = (variant: PlaceholderVariant) => {
    if (variant === 'pdf') {
      return <PictureAsPdfIcon fontSize="small" />;
    }
    if (variant === 'image') {
      return <InsertPhotoIcon fontSize="small" />;
    }
    if (variant === 'textCsv' || variant === 'textTxt' || variant === 'textMd') {
      return <ArticleIcon fontSize="small" />;
    }
    if (variant === 'docx') {
      return <ArticleIcon fontSize="small" />;
    }
    if (variant === 'xlsx') {
      return <InsertDriveFileIcon fontSize="small" />;
    }
    return <InsertDriveFileIcon fontSize="small" />;
  };

  const isPageCountableFile = (filename: string) => {
    const variant = getPlaceholderVariant(filename);
    return variant === 'pdf' || variant === 'image' || variant === 'textCsv' || variant === 'textTxt' || variant === 'textMd' || variant === 'xlsx';
  };

  const shouldShowPageCount = (filename: string, pageCount?: number) => {
    if (!isPageCountableFile(filename) || typeof pageCount !== 'number') {
      return false;
    }

    const variant = getPlaceholderVariant(filename);
    const shouldHideSinglePage = variant === 'xlsx' || variant === 'textTxt' || variant === 'textMd';

    if (shouldHideSinglePage && pageCount <= 1) {
      return false;
    }

    return true;
  };

  const countAttachmentPages = useCallback((
    filesInput: File[] | null = selectedFiles,
    pageMapInput: { [key: string]: number } | null = numPages
  ) => {
    if (!filesInput?.length) {
      return 0;
    }

    return filesInput.reduce((total, file) => {
      if (!isPageCountableFile(file.name)) {
        return total;
      }

      const pageCount = pageMapInput?.[file.name];
      return total + (pageCount && pageCount > 0 ? pageCount : 1);
    }, 0);
  }, [selectedFiles, numPages]);

  const totalSelectedPages = useMemo(() => countAttachmentPages(), [countAttachmentPages]);

  const currentOcrFile = ocrModalFiles.length > 0
    ? ocrModalFiles[Math.min(selectedOcrFileIndex, ocrModalFiles.length - 1)]
    : null;
  const currentOcrFileKey = currentOcrFile
    ? currentOcrFile.file_id || currentOcrFile.filename || 'ocr-current'
    : null;
  const isCurrentOcrFileCopied = currentOcrFileKey ? copiedOcrFileId === currentOcrFileKey : false;
  const totalOcrFiles = ocrModalFiles.length;
  const canNavigatePrevOcrFile = totalOcrFiles > 1 && selectedOcrFileIndex > 0;
  const canNavigateNextOcrFile = totalOcrFiles > 1 && selectedOcrFileIndex < totalOcrFiles - 1;

  const selectOcrFileAtIndex = useCallback((index: number) => {
    const bounded = Math.max(0, Math.min(index, Math.max(0, totalOcrFiles - 1)));
    setSelectedOcrFileIndex(bounded);
    setCopiedOcrFileId(null);
  }, [totalOcrFiles, setCopiedOcrFileId, setSelectedOcrFileIndex]);

  const goToPreviousOcrFile = useCallback(() => {
    if (!canNavigatePrevOcrFile) {
      return;
    }
    selectOcrFileAtIndex(selectedOcrFileIndex - 1);
  }, [canNavigatePrevOcrFile, selectOcrFileAtIndex, selectedOcrFileIndex]);

  const goToNextOcrFile = useCallback(() => {
    if (!canNavigateNextOcrFile) {
      return;
    }
    selectOcrFileAtIndex(selectedOcrFileIndex + 1);
  }, [canNavigateNextOcrFile, selectOcrFileAtIndex, selectedOcrFileIndex]);

  const fetchOriginalFileUrl = async (filename: string): Promise<boolean> => {
    const chatId = activeChatID?.chatID;
    if (!chatId) return false;
    const extractedFiles = chatExtractedFiles[chatId];
    const extracted = extractedFiles?.find((f: ExtractedFileInfo) => f.filename === filename);
    if (!extracted?.file_id) return false;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/chat/files/${chatId}/${extracted.file_id}/download`, {
        method: 'GET',
        headers: { Authorization: `Auth ${token}` },
      });
      if (!response.ok) return false;
      const data = await response.json();
      const downloadUrl = data?.download_url;
      if (!downloadUrl) return false;

      const mime = (data?.mime_type || '').toLowerCase();
      const isPdf = isPdfFile(filename) || mime === 'application/pdf';
      const isImage = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename);

      if (isPdf) {
        setFileOriginalUrls((prev) => ({ ...prev, [filename]: downloadUrl }));
        setPreviewSrc(downloadUrl);
        setPreviewTitle(filename);
        setPreviewType('pdf');
        setPreviewTextContent("");
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
        return true;
      }

      if (isImage) {
        setFilePreviews((prev) => ({ ...prev, [filename]: downloadUrl }));
        setPreviewSrc(downloadUrl);
        setPreviewTitle(filename);
        setPreviewType('image');
        setPreviewTextContent("");
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
        return true;
      }

      // For text-like files, show OCR content
      if (extracted.file_content) {
        setPreviewSrc(null);
        setPreviewTitle(filename);
        setPreviewType('text');
        setPreviewTextContent(extracted.file_content);
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const handleOpenPreview = async (filename: string) => {
    const src = filePreviews[filename];
    const textPreview = fileTextPreviews[filename];
    const isPdf = isPdfFile(filename);

    if (isPdf) {
      const file = selectedFiles?.find((f) => f.name === filename);
      let pdfUrl = fileOriginalUrls[filename];

      if (!pdfUrl && file) {
        const createdUrl = URL.createObjectURL(file);
        pdfUrl = createdUrl;
        setFileOriginalUrls((prev) => {
          const next = { ...prev };
          const existing = next[filename];
          if (existing && existing.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(existing);
            } catch {}
          }
          next[filename] = createdUrl;
          return next;
        });
      }

      if (pdfUrl) {
        setPreviewSrc(pdfUrl);
        setPreviewTitle(filename);
        setPreviewType('pdf');
        setPreviewTextContent("");
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
        return;
      }

      if (src) {
        setPreviewSrc(src);
        setPreviewTitle(filename);
        setPreviewType('image');
        setPreviewTextContent("");
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
      } else if (!(await fetchOriginalFileUrl(filename))) {
        toast.info("Pré-visualização indisponível.", TOAST_CONFIG);
      }
      return;
    }

    if (src) {
      setPreviewSrc(src);
      setPreviewTitle(filename);
      setPreviewType('image');
      setPreviewTextContent("");
      setPreviewTextTruncated(false);
      setPreviewOpen(true);
      return;
    }

    if (textPreview) {
      // The cached preview is capped at TEXT_PREVIEW_CHAR_LIMIT for perf. When the
      // user actually opens it, show the FULL text: read it from the in-memory File
      // (pending upload) or the backend-extracted text (already sent). Only cap the
      // modal at PREVIEW_FULL_MAX_CHARS so a huge file can't freeze the browser.
      let fullText = textPreview.content;
      const pendingFile = selectedFiles?.find((f) => f.name === filename);
      if (pendingFile) {
        try { fullText = await pendingFile.text(); } catch { /* keep cached slice */ }
      } else {
        const chatId = activeChatID?.chatID;
        const extracted = chatId
          ? chatExtractedFiles[chatId]?.find((f: ExtractedFileInfo) => f.filename === filename)
          : undefined;
        if (extracted?.file_content) fullText = extracted.file_content;
      }
      const overCap = fullText.length > PREVIEW_FULL_MAX_CHARS;
      setPreviewSrc(null);
      setPreviewTitle(filename);
      setPreviewType('text');
      setPreviewTextContent(overCap ? fullText.slice(0, PREVIEW_FULL_MAX_CHARS) : fullText);
      setPreviewTextTruncated(overCap);
      setPreviewOpen(true);
    } else {
      const lower = filename.toLowerCase();
      const isOffice = lower.endsWith('.docx') || lower.endsWith('.xlsx') || lower.endsWith('.rtf');
      if (isOffice) {
        // Office formats have no client-side preview (reading raw bytes dumps OOXML
        // garbage). Show the backend-extracted text DIRECTLY from chatExtractedFiles.
        // The old path (fetchOriginalFileUrl) gated the extracted text behind a
        // file_id + /download call that bails for these files, so the readable text
        // never reached the modal. If there's no extraction yet (file attached but
        // not sent), show a clear note instead of doing nothing.
        const chatId = activeChatID?.chatID;
        const extracted = chatId
          ? chatExtractedFiles[chatId]?.find((f: ExtractedFileInfo) => f.filename === filename)
          : undefined;
        setPreviewSrc(null);
        setPreviewTitle(filename);
        setPreviewType('text');
        setPreviewTextContent(
          extracted?.file_content ||
          "A pré-visualização do conteúdo deste arquivo ficará disponível após o envio (o texto é extraído no servidor)."
        );
        setPreviewTextTruncated(false);
        setPreviewOpen(true);
      } else if (!(await fetchOriginalFileUrl(filename))) {
        toast.info("Pré-visualização indisponível.", TOAST_CONFIG);
      }
    }
  };
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewSrc(null);
    setPreviewTitle("");
    setPreviewType(null);
    setPreviewTextContent("");
    setPreviewTextTruncated(false);
    setPreviewOcrText(null);
    setPreviewOcrAvailable(false);
    setPreviewOcrLoading(false);
  };

  const fetchChatOcrFiles = useCallback(async (chatId: string, force = false): Promise<ExtractedFileInfo[]> => {
    if (!chatId) {
      return [];
    }

    if (!force && chatExtractedFiles[chatId]) {
      return chatExtractedFiles[chatId];
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/chat/files/${chatId}`, {
        method: 'GET',
        headers: {
          Authorization: `Auth ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('failed_to_load_ocr');
      }

      const data = await response.json();
      const normalized: ExtractedFileInfo[] = (data?.files || []).map((file: any) => ({
        file_id: file?.file_id,
        filename: file?.filename,
        file_content: file?.file_content ?? '',
      }));

      setChatExtractedFiles((prev) => ({
        ...prev,
        [chatId]: normalized,
      }));

      return normalized;
    } catch (error) {
      reportHandledError(error, { category: 'api', feature: 'chat.ocr.load' });
      toast.error('Não foi possível carregar o conteúdo da conversa.', TOAST_CONFIG);
      return [];
    }
  }, [chatExtractedFiles, getAccessTokenSilently]);

  // When the file-preview modal opens, resolve the file's extracted text (OCR)
  // so the modal can show a "Texto extraído (OCR)" tab next to the original.
  // The OCR is stored per chat in Mongo (files collection) and fetched (cached)
  // via fetchChatOcrFiles — we match the previewed file by its filename.
  useEffect(() => {
    if (!previewOpen) return;
    const filename = previewTitle;
    const chatId = activeChatID?.chatID;
    setPreviewOcrText(null);
    setPreviewOcrAvailable(false);
    if (!chatId || !filename) {
      setPreviewOcrLoading(false);
      return;
    }
    let cancelled = false;
    setPreviewOcrLoading(true);
    fetchChatOcrFiles(chatId)
      .then((files) => {
        if (cancelled) return;
        const match = files.find((f) => f.filename === filename);
        setPreviewOcrAvailable(!!match);
        setPreviewOcrText(match ? (match.file_content ?? '') : null);
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewOcrAvailable(false);
          setPreviewOcrText(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewOcrLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, previewTitle]);

  const handleCloseOcrModal = () => {
    setIsOcrModalOpen(false);
    setOcrModalFiles([]);
    setOcrModalTitle('');
    setCopiedOcrFileId(null);
    setSelectedOcrFileIndex(0);
  };

  const handleOpenOcrModal = async (message: any) => {
    if (loadingOcrModal) {
      return;
    }

    const chatId = activeChatID?.chatID;
    if (!chatId) {
      return;
    }

    setLoadingOcrModal(true);
    setCopiedOcrFileId(null);
    try {
      const files = await fetchChatOcrFiles(chatId);
      const messageFiles = Array.isArray(message?.files) ? message.files : [];
      const messageFileIds = new Set(
        messageFiles
          .map((file: any) => file?.file_id)
          .filter((fileId: string | undefined): fileId is string => Boolean(fileId))
      );

      let matchingFiles = files.filter((file) => file.file_id && messageFileIds.has(file.file_id));

      if (matchingFiles.length === 0 && Array.isArray(message?.filenames)) {
        const filenameSet = new Set(message.filenames);
        matchingFiles = files.filter((file) => filenameSet.has(file.filename));
      }

      if (matchingFiles.length === 0) {
        toast.info('Texto extraído ainda não disponível para estes arquivos.', TOAST_CONFIG);
        return;
      }

      const modalTitle = matchingFiles.length === 1
        ? matchingFiles[0].filename
        : (Array.isArray(message?.filenames) && message.filenames.length > 1
            ? 'Textos extraídos dos arquivos enviados'
            : 'Texto extraído do arquivo enviado');

      setOcrModalFiles(matchingFiles);
      setOcrModalTitle(modalTitle);
      setSelectedOcrFileIndex(0);
      setCopiedOcrFileId(null);
      setIsOcrModalOpen(true);
    } catch (error) {
      // fetchChatOcrFiles handles toast notification on failure
    } finally {
      setLoadingOcrModal(false);
    }
  };

  const handleCopyOcrContent = async (content: string, fileKey: string) => {
    if (!content || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      setCopiedOcrFileId(fileKey);
      setTimeout(() => setCopiedOcrFileId((current) => (current === fileKey ? null : current)), 2000);
    } catch {
      // Silently fail if clipboard is unavailable
    }
  };

  const hasOcrContentForMessage = useCallback((message: any) => {
    const chatId = activeChatID?.chatID;
    if (!chatId) {
      return false;
    }

    const extractedFiles = chatExtractedFiles[chatId];
    if (!extractedFiles || extractedFiles.length === 0) {
      return false;
    }

    const messageFiles = Array.isArray(message?.files) ? message.files : [];
    const filenames = Array.isArray(message?.filenames) ? message.filenames : [];

    if (messageFiles.length === 0 && filenames.length === 0) {
      return false;
    }

    const hasMatchById = messageFiles.some((file: any) => {
      const fileId = file?.file_id;
      if (!fileId) {
        return false;
      }
      const extracted = extractedFiles.find((f) => f.file_id === fileId);
      return Boolean(extracted);
    });

    if (hasMatchById) {
      return true;
    }

    return filenames.some((filename: string) => {
      if (!filename) {
        return false;
      }
      const extracted = extractedFiles.find((f) => f.filename === filename);
      return Boolean(extracted);
    });
  }, [activeChatID?.chatID, chatExtractedFiles]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      try {
        Object.values(filePreviewsRef.current).forEach((url) => {
          if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
        Object.values(fileOriginalUrlsRef.current).forEach((url) => {
          if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
      } catch {}
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClosePreview();
      }
    };
    if (previewOpen) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen]);

  // Free plan status and quota (badge-in-message)
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [freeQuota, setFreeQuota] = useState<{messages: number} | null>(null);
  const [quotaInfoAnchorEl, setQuotaInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [quotaInfoVariant, setQuotaInfoVariant] = useState<QuotaInfoVariant>('default');
  const [quotaBadgeIndex, setQuotaBadgeIndex] = useState<number | null>(null);

  // Cache plan_status in localStorage scoped by user_id so reloads do not flash
  // a wrong 'Gratuito' badge to active subscribers. The cache is a fast hint;
  // the API response is always authoritative.
  const PLAN_STATUS_CACHE_KEY = 'maritaca:planStatus';

  const readCachedPlanStatus = useCallback((userId?: string): string | null => {
    if (!userId) return null;
    try {
      const cache = JSON.parse(localStorage.getItem(PLAN_STATUS_CACHE_KEY) || '{}');
      const entry = cache[userId];
      // We only cache 'active' (see writeCachedPlanStatus); ignore any stale
      // 'free' entry written by older builds so it cannot resurrect a flash.
      return entry === 'active' ? entry : null;
    } catch {
      return null;
    }
  }, []);

  const writeCachedPlanStatus = useCallback((userId: string | undefined, status: string | null) => {
    if (!userId) return;
    try {
      const cache = JSON.parse(localStorage.getItem(PLAN_STATUS_CACHE_KEY) || '{}');
      // Only cache 'active'. Caching 'free' would briefly flash 'Gratuito'
      // on a premium user who upgraded via a different channel (mobile/Pix)
      // since the last visit on this device — exactly the symptom this PR
      // is meant to remove. A free user gains nothing from a cache hit.
      if (status === 'active') {
        cache[userId] = status;
      } else {
        delete cache[userId];
      }
      localStorage.setItem(PLAN_STATUS_CACHE_KEY, JSON.stringify(cache));
    } catch {}
  }, []);

  const fetchPlanStatus = useCallback(async (): Promise<boolean> => {
    try {
      const access_token = await getAccessTokenSilently();
      const resp = await fetch(`${API_BASE}/api/billing/status`, {
        method: 'GET',
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        const status: string | null = data?.plan_status === 'active' || data?.plan_status === 'free'
          ? data.plan_status
          : null;
        setPlanStatus(status);
        writeCachedPlanStatus(user?.sub, status);
        return true;
      }
    } catch {}
    return false;
  }, [getAccessTokenSilently, user?.sub, writeCachedPlanStatus]);

  // Hydrate badge from cache as soon as we know who the user is, so reloads
  // show the last-known plan instead of the default null (which would render
  // 'Gratuito').
  useEffect(() => {
    if (!user?.sub) return;
    const cached = readCachedPlanStatus(user.sub);
    if (cached) setPlanStatus((prev) => prev ?? cached);
  }, [user?.sub, readCachedPlanStatus]);

  // Fetch plan status with exponential backoff so a transient network failure
  // does not leave the badge stuck. Stops as soon as a fetch succeeds.
  useEffect(() => {
    let canceled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tryFetch = async (attempt: number) => {
      if (canceled) return;
      const ok = await fetchPlanStatus();
      if (!ok && !canceled && attempt < 10) {
        const delay = Math.min(1000 * 2 ** attempt, 60_000);
        timer = setTimeout(() => tryFetch(attempt + 1), delay);
      }
    };

    tryFetch(0);

    return () => {
      canceled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchPlanStatus]);

  // Refetch when the tab becomes visible and we still do not know the status
  // (e.g. user opened the tab while offline, then came back).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && planStatus === null) {
        void fetchPlanStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [planStatus, fetchPlanStatus]);

  // Ensure quota badge always targets the last assistant message
  // even after switching conversations or loading history.
  useEffect(() => {
    if (!messageHistory || messageHistory.length === 0) {
      setQuotaBadgeIndex(null);
      return;
    }
    for (let i = messageHistory.length - 1; i >= 0; i--) {
      const m: any = messageHistory[i];
      if (m && m.role === 'assistant') {
        setQuotaBadgeIndex(i);
        return;
      }
    }
    setQuotaBadgeIndex(null);
  }, [activeChatID, messageHistory]);

  const handleQuotaInfoClose = useCallback(() => {
    setQuotaInfoAnchorEl(null);
  }, []);

  const handleQuotaInfoClick = useCallback((event: React.MouseEvent<HTMLElement>, variant: QuotaInfoVariant) => {
    if (!isMobile) {
      return;
    }
    if (quotaInfoAnchorEl === event.currentTarget && quotaInfoVariant === variant) {
      setQuotaInfoAnchorEl(null);
      return;
    }
    setQuotaInfoVariant(variant);
    setQuotaInfoAnchorEl(event.currentTarget);
  }, [isMobile, quotaInfoAnchorEl, quotaInfoVariant]);

  useEffect(() => {
    if (!isMobile && quotaInfoAnchorEl) {
      setQuotaInfoAnchorEl(null);
    }
  }, [isMobile, quotaInfoAnchorEl]);

  
  useEffect(() => {
    return () => {
      if (sseConnectionRef.current) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
      }
    };
  }, []);


  // The chat messages scroll inside an inner container (not the window), and
  // scrollIntoView() on the end-marker does NOT scroll that container (a layout
  // quirk — it silently no-ops, leaving the chat pinned at the top). So we walk
  // up from the marker to its nearest scrollable ancestor and set scrollTop
  // directly. scrollIntoView is kept as a fallback for layouts where the page
  // (window) is the scroller.
  // The chat messages scroll in an inner container (not the window), and there
  // are NESTED scrollables (the outer page + the inner messages list). Find the
  // right one by walking up from the end-marker to the FIRST ancestor whose
  // overflow-y is auto/scroll BY STYLE — not by "is it currently overflowing",
  // which is false until content lays out and would make us skip the inner
  // container and grab the outer page (scrolling the wrong thing). scrollIntoView
  // no-ops on this container; setting scrollTop is what actually moves it.
  const getChatScroller = (): HTMLElement | null => {
    const marker = endOfChatMessageMarkerRef.current;
    if (!marker) return null;
    let el: HTMLElement | null = marker.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
      el = el.parentElement;
    }
    return null;
  };

  const smoothScrollToEnd = () => {
    const c = getChatScroller();
    if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
    else endOfChatMessageMarkerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setIsAtBottom(true);
  };

  const instantScrollToEnd = () => {
    const c = getChatScroller();
    if (c) c.scrollTop = c.scrollHeight;
    else endOfChatMessageMarkerRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  };

  // When a chat finishes loading from the history endpoint, smoothly
  // scroll to the bottom so the user lands on the latest message —
  // same animation as the floating "go to end" button. We watch
  // activeChatID + messageHistory + loadingHistory together because
  // setLoadingHistory(false) fires BEFORE setMessageHistory (the latter
  // is wrapped in startTransition), so a single edge-trigger on
  // loadingHistory misses the moment the content actually lands.
  const scrolledForChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    const chatId = activeChatID?.chatID;
    if (!chatId) return;
    if (chatId === scrolledForChatIdRef.current) return;
    if (loadingHistory) return;
    if (messageHistory.length === 0) return;
    scrolledForChatIdRef.current = chatId;
    // Pin to the bottom when opening a chat. Content height grows AFTER first
    // paint (markdown, code blocks, charts/images load lazily), so we re-pin the
    // scroll container directly until its scrollHeight has been STABLE for a few
    // ticks (content settled) or a ~2.3s cap. Pinning every tick (not "stop on
    // first at-bottom") is the point: an early tick can land at bottom while the
    // container is still short, then content grows and would leave us stranded.
    let ticks = 0;
    let lastSH = -1;
    let stable = 0;
    const pin = () => {
      const sc = getChatScroller();
      if (sc) sc.scrollTop = sc.scrollHeight;
      setIsAtBottom(true);
      ticks += 1;
      // Only count "settled" once we actually have the scroller AND its height
      // has stopped changing. While the marker/container isn't mounted yet
      // (sc null) we keep retrying instead of declaring success — otherwise we'd
      // stop before the messages even render.
      if (sc && sc.scrollHeight === lastSH) stable += 1;
      else { stable = 0; lastSH = sc ? sc.scrollHeight : -1; }
      if (stable < 3 && ticks < 25) setTimeout(pin, 90);
    };
    pin();
  }, [activeChatID?.chatID, loadingHistory, messageHistory.length]);


  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const stopStreamingSTT = useCallback(() => {
    // Send final commit and close WebSocket
    if (sttWsRef.current && sttWsRef.current.readyState === WebSocket.OPEN) {
      try {
        sttWsRef.current.send(JSON.stringify({
          message_type: "input_audio_chunk",
          audio_base_64: "",
          commit: true,
        }));
      } catch { /* ignore */ }
      // Close after a short delay to allow final transcript to arrive
      setTimeout(() => {
        if (sttWsRef.current) {
          sttWsRef.current.close();
          sttWsRef.current = null;
        }
      }, 1500);
    }
    // Stop audio processing
    if (sttProcessorRef.current) {
      sttProcessorRef.current.disconnect();
      sttProcessorRef.current = null;
    }
    if (sttAudioContextRef.current) {
      sttAudioContextRef.current.close();
      sttAudioContextRef.current = null;
    }
    if (sttMicStreamRef.current) {
      sttMicStreamRef.current.getTracks().forEach(t => t.stop());
      sttMicStreamRef.current = null;
    }

    setIsRecording(false);
    setAudioTooltipOpen(false);
    setSttTranscribing(false);
    setPartialTranscript("");

    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const startStreamingSTT = useCallback(async () => {
    // Reset transcription state so waveform shows until first text arrives
    setSttTranscribing(false);
    setPartialTranscript("");

    try {
      // 1. Get single-use token from backend
      const token = await getAccessTokenSilently();
      const tokenResp = await fetch(`${API_BASE}/api/stt-token`, {
        headers: { Authorization: `Auth ${token}` },
      });
      if (!tokenResp.ok) {
        if (tokenResp.status === 402) {
          const msg = await tokenResp.json();
          toast.error(msg?.detail?.detail || msg?.detail || "Limite de áudio excedido.", TOAST_CONFIG);
        } else {
          toast.error("Falha ao iniciar transcrição em tempo real.", TOAST_CONFIG);
        }
        return;
      }
      const { token: sttToken } = await tokenResp.json();

      // 2. Get microphone access
      let micStream: MediaStream;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
        });
      } catch {
        toast.error("Microfone não encontrado ou permissão negada.", TOAST_CONFIG);
        return;
      }
      sttMicStreamRef.current = micStream;

      // 3. Open WebSocket to ElevenLabs
      const wsUrl = `wss://api.elevenlabs.io/v1/speech-to-text/realtime?token=${encodeURIComponent(sttToken)}&model_id=scribe_v2_realtime&language_code=pt&audio_format=pcm_16000&commit_strategy=vad`;
      const ws = new WebSocket(wsUrl);
      sttWsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.message_type === "partial_transcript" && data.text) {
            setPartialTranscript(data.text);
          } else if (data.message_type === "committed_transcript" && data.text) {
            const committedText = data.text.trim();
            if (committedText) {
              setCurrentMessage(prev => prev.length > 0 ? `${prev} ${committedText}` : committedText);
            }
            setPartialTranscript("");
          }
        } catch { /* ignore malformed messages */ }
      };

      ws.onclose = () => {
        // If still recording when WS closes unexpectedly, clean up
        if (sttWsRef.current === ws) {
          sttWsRef.current = null;
        }
      };

      // 4. Wait for WebSocket to open, then start sending audio
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("WebSocket timeout")), 10000);
        ws.addEventListener("open", () => { clearTimeout(timeout); resolve(); }, { once: true });
        ws.addEventListener("error", (e) => { clearTimeout(timeout); reject(e); }, { once: true });
      });

      // Set error handler after connection is established
      ws.onerror = () => {
        toast.error("Erro na conexão de transcrição.", TOAST_CONFIG);
        stopStreamingSTT();
      };

      // 5. Set up audio processing: mic → PCM 16kHz → base64 → WebSocket
      // Use native sample rate and downsample to 16kHz (browsers may not support 16kHz natively)
      const audioContext = new AudioContext();
      sttAudioContextRef.current = audioContext;
      const nativeSampleRate = audioContext.sampleRate;
      const targetSampleRate = 16000;
      const source = audioContext.createMediaStreamSource(micStream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      sttProcessorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (!sttWsRef.current || sttWsRef.current.readyState !== WebSocket.OPEN) return;
        const inputData = e.inputBuffer.getChannelData(0);

        // Downsample from native rate to 16kHz
        let float32: Float32Array;
        if (nativeSampleRate === targetSampleRate) {
          float32 = inputData;
        } else {
          const ratio = nativeSampleRate / targetSampleRate;
          const newLength = Math.round(inputData.length / ratio);
          float32 = new Float32Array(newLength);
          for (let i = 0; i < newLength; i++) {
            float32[i] = inputData[Math.round(i * ratio)];
          }
        }

        // Convert Float32 [-1,1] → Int16 PCM
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const s = Math.max(-1, Math.min(1, float32[i]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        // Base64 encode
        const bytes = new Uint8Array(int16.buffer);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        try {
          sttWsRef.current.send(JSON.stringify({
            message_type: "input_audio_chunk",
            audio_base_64: base64,
          }));
        } catch { /* ignore if WS closed */ }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);

      // 6. Set max duration timer
      recordingTimerRef.current = setTimeout(() => {
        stopStreamingSTT();
        toast.info(`Gravação interrompida após ${MAX_AUDIO_DURATION_TEXT}.`, TOAST_CONFIG);
      }, MAX_AUDIO_DURATION_MS);

    } catch (err) {
      toast.error("Falha ao iniciar transcrição em tempo real.", TOAST_CONFIG);
      stopStreamingSTT();
    }
  }, [getAccessTokenSilently, stopStreamingSTT, setCurrentMessage]);

  const handleAudioButtonClick = () => {
    if (sendingAudio) return;

    if (isRecording) {
      stopStreamingSTT();
    } else {
      startStreamingSTT();
    }
  };


  useEffect(() => {
    const handleScroll = () => {
      if (MessageAreaDivRef.current) {
        const buffer = 50;
        const container = MessageAreaDivRef.current;
        const isBottom = container.scrollTop + container.clientHeight + buffer >= container.scrollHeight;
        setIsAtBottom(isBottom);
      }
    };

    if (MessageAreaDivRef.current) {
      MessageAreaDivRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (MessageAreaDivRef.current) {
        MessageAreaDivRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);


  // useEffect to ensure audio tooltip closes when sendingAudio or recording status changes
  useEffect(() => {
    if (sendingAudio || status === "recording") {
      // Keep tooltip state as is when actively recording or sending
      return;
    }
    // Close tooltip when not recording and not sending audio
    const timer = setTimeout(() => {
      setAudioTooltipOpen(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [sendingAudio, status]);

  if (!isLoading && !isAuthenticated) {
    logout({
      logoutParams: { returnTo: window.location.origin + "/auth" } 
    });
  }

  const userPicture = user?.picture || Cutebot;

  const handleNewMessageInputChange = (event: any) => {
    setCurrentMessage(event.target.value);
  };
  
  const handleEditMessageInputChange = (event: any) => {
    setMessageBeingEdited(event.target.value);
  };

  const code = memo((props: any) => {
    const { inline, className, children } = props;
    const match = /language-(\w+)/.exec(className || '');
    const lang = match && match[1];

    if (inline) {
      return <code className={className}>{children}</code>;
    }

    return <CodeBlock lang={lang || 'text'} codeChildren={children} />;
  });


  const preprocessingLatex = (text: string | any) => {
    if (!text) return "";
    if (typeof text !== 'string') {
      if (Array.isArray(text)) {
        text = text.filter((item: any) => item.type === 'text').map((item: any) => item.text).join('\n');
      } else {
        text = String(text);
      }
    }
    return text
      .replace(/\\\\\[/g, '$$$$')
      .replace(/\\\\\]/g, '$$$$')
      .replace(/\\\\\(/g, '$$$$')
      .replace(/\\\\\)/g, '$$$$')
      .replace(/\\\[/g, '$$$$')
      .replace(/\\\]/g, '$$$$')
      .replace(/\\\(/g, '$$$$')
      .replace(/\\\)/g, '$$$$');
  };

  const p = memo(
    (
      props?: Omit<
        DetailedHTMLProps<
          HTMLAttributes<HTMLParagraphElement>,
          HTMLParagraphElement
        >,
        'ref'
      > &
        any
    ) => {
      return <p className='whitespace-pre-wrap'>{props?.children}</p>;
    }
  );



  const formatAssistantMessage = (message: string | any, references?: Reference[], isStreaming?: boolean) => {
    // if message is null, return null
    if (!message) return "";
    // Handle ContentItem[] from code_executor — extract text content
    if (typeof message !== 'string') {
      if (Array.isArray(message)) {
        message = message
          .filter((item: any) => item.type === 'text')
          .map((item: any) => item.text)
          .join('\n');
      } else {
        message = String(message);
      }
    }
    const preprocessedMessage = preprocessingLatex(message);

    // Get all references from history to support citations from previous messages
    const allHistoryRefs = getAllReferencesFromHistory();
    // Combine current message references with history references
    // Current references take priority (added first), then history for fallback
    const allAvailableRefs = [...(references || []), ...allHistoryRefs];

    let keyIndex = 0;

    // Helper to process text and replace raw URLs, markdown links, and legacy [REFERENCIA_N] citations with InlineCitation components
    const processTextWithCitations = (text: string): React.ReactNode => {
      if (allAvailableRefs.length === 0) return text;

      // Match: raw URLs | markdown links [text](url) | legacy [REFERENCIA_N] patterns
      const citationPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\[(REFER[EÊ]NCIA_\d+(?:\s*,\s*REFER[EÊ]NCIA_\d+)*)\]|(https?:\/\/[^\s<>)]+)/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = citationPattern.exec(text)) !== null) {
        // Add text before citation
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
          // Markdown link: [text](url)
          const linkText = match[1];
          const linkUrl = match[2];
          const matchedRef = allAvailableRefs.find(ref => ref.link === linkUrl);
          parts.push(
            <InlineCitation
              key={`cite-${keyIndex++}-link`}
              citationNumber={matchedRef?.index}
              reference={matchedRef}
              linkText={linkText}
              linkUrl={linkUrl}
            />
          );
        } else if (match[3]) {
          // Legacy [REFERENCIA_N] pattern (backward compat with old conversations)
          const citationContent = match[3];
          const individualCitations = citationContent.split(/\s*,\s*/);
          individualCitations.forEach((citation: string) => {
            const numMatch = citation.match(/REFER[EÊ]NCIA_(\d+)/);
            if (numMatch) {
              const citationNumber = parseInt(numMatch[1], 10);
              const reference = allAvailableRefs.find(ref => ref.index === citationNumber);
              parts.push(
                <InlineCitation
                  key={`cite-${keyIndex++}-${citationNumber}`}
                  citationNumber={citationNumber}
                  reference={reference}
                />
              );
            }
          });
        } else if (match[4]) {
          // Raw URL in text (fallback for URLs not autolinked by remarkGfm)
          const rawUrl = match[4];
          const matchedRef = allAvailableRefs.find(ref => ref.link === rawUrl);
          parts.push(
            <InlineCitation
              key={`cite-${keyIndex++}-rawurl`}
              citationNumber={matchedRef?.index}
              reference={matchedRef}
              linkText={matchedRef?.title || rawUrl}
              linkUrl={rawUrl}
            />
          );
        }

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      return parts.length > 0 ? parts : text;
    };

    // Recursively process React children to find and replace citation text
    const processChildren = (children: React.ReactNode): React.ReactNode => {
      if (typeof children === 'string') {
        return processTextWithCitations(children);
      }
      if (Array.isArray(children)) {
        return children.map((child, idx) => {
          if (typeof child === 'string') {
            return <React.Fragment key={idx}>{processTextWithCitations(child)}</React.Fragment>;
          }
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { key: idx } as any, processChildren((child.props as any).children));
          }
          return child;
        });
      }
      if (React.isValidElement(children)) {
        return React.cloneElement(children, {}, processChildren((children.props as any).children));
      }
      return children;
    };

    // Split message into interleaved segments of text and tables, preserving
    // original order. Tables are rendered as stable elements outside ReactMarkdown
    // to prevent flickering caused by ReactMarkdown rebuilding its component tree.
    const workingMessage = preprocessedMessage;

    // Segments: [{type: 'text' | 'table', content}, ...]
    // Tables are rendered as stable elements outside ReactMarkdown.
    const segments: Array<{type: 'text' | 'table', content: string}> = [
      {type: 'text', content: workingMessage},
    ];
    let hasIncompleteTable = false;

    // Further split text segments to extract completed markdown tables.
    // A complete table = header row + separator row + 1+ data rows.
    const tableRegex = /(?:^|\n)((?:\|[^\n]+\|\s*\n)\|[-:\s|]+\|\s*\n(?:\|[^\n]+\|\s*\n?)+)/g;
    const withTables: typeof segments = [];
    for (const seg of segments) {
      if (seg.type !== 'text') {
        withTables.push(seg);
        continue;
      }
      let tLastIndex = 0;
      let tMatch;
      let foundTable = false;
      tableRegex.lastIndex = 0;
      while ((tMatch = tableRegex.exec(seg.content)) !== null) {
        foundTable = true;
        const matchStart = tMatch.index + (tMatch[0].startsWith('\n') ? 1 : 0);
        if (matchStart > tLastIndex) {
          const before = seg.content.slice(tLastIndex, matchStart).trim();
          if (before) withTables.push({type: 'text', content: before});
        }
        withTables.push({type: 'table', content: tMatch[1].trim()});
        tLastIndex = tMatch.index + tMatch[0].length;
      }
      if (foundTable) {
        if (tLastIndex < seg.content.length) {
          let remaining = seg.content.slice(tLastIndex);
          // During streaming, detect incomplete table at end of text
          if (isStreaming) {
            const incompletePattern = /(?:^|\n)(\|[^\n]+\|\s*\n(?:\|[-:\s|]+\|\s*\n(?:\|[^\n]+\|\s*\n?)*)?)$/;
            const incMatch = remaining.match(incompletePattern);
            if (incMatch && incMatch[1].trim().split('\n')[0].includes('|')) {
              const beforeIncomplete = remaining.slice(0, remaining.length - incMatch[0].length + (incMatch[0].startsWith('\n') ? 1 : 0)).trim();
              if (beforeIncomplete) withTables.push({type: 'text', content: beforeIncomplete});
              hasIncompleteTable = true;
            } else {
              const trimmed = remaining.trim();
              if (trimmed) withTables.push({type: 'text', content: trimmed});
            }
          } else {
            const trimmed = remaining.trim();
            if (trimmed) withTables.push({type: 'text', content: trimmed});
          }
        }
      } else {
        // No tables found — check for incomplete table during streaming
        if (isStreaming) {
          const incompletePattern = /(?:^|\n)(\|[^\n]+\|\s*\n(?:\|[-:\s|]+\|\s*\n(?:\|[^\n]+\|\s*\n?)*)?)$/;
          const incMatch = seg.content.match(incompletePattern);
          if (incMatch && incMatch[1].trim().split('\n')[0].includes('|')) {
            const beforeIncomplete = seg.content.slice(0, seg.content.length - incMatch[0].length + (incMatch[0].startsWith('\n') ? 1 : 0)).trim();
            if (beforeIncomplete) withTables.push({type: 'text', content: beforeIncomplete});
            hasIncompleteTable = true;
          } else {
            withTables.push(seg);
          }
        } else {
          withTables.push(seg);
        }
      }
    }
    // Replace segments with table-aware version
    segments.length = 0;
    segments.push(...withTables);

    const components = {
      code: code,
      table: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => (
        <TableBlock><table {...props}>{children}</table></TableBlock>
      ),
      p: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => {
        const hasDisplayMath = node.children.some((child: any, index: number, array: any[]) => {
          if (child.type === 'element' && child.properties?.className?.includes('math')) {
            const prevNode = array[index - 1];
            const nextNode = array[index + 1];

            const hasLeadingNewline = !prevNode ||
              (prevNode.type === 'text' && /\n\s*$/.test(prevNode.value));

            const hasTrailingNewline = !nextNode ||
              (nextNode.type === 'text' && /^\s*\n/.test(nextNode.value));

            return hasLeadingNewline && hasTrailingNewline;
          }
          return false;
        });

        return (
          <p
            className={`whitespace-pre-wrap ${hasDisplayMath ? 'katex-centered' : ''}`}
            {...props}
          >
            {processChildren(children)}
          </p>
        );
      },
      li: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => (
        <li {...props}>{processChildren(children)}</li>
      ),
      td: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => (
        <td {...props}>{processChildren(children)}</td>
      ),
      th: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => (
        <th {...props}>{processChildren(children)}</th>
      ),
      a: ({
          node,
          href,
          children,
          ...props
        }: {
          node: any;
          href?: string;
          children: React.ReactNode;
          [key: string]: any;
        }) => {
          if (href && /^https?:\/\//.test(href)) {
            const childText = typeof children === "string" ? children : "";
            // Look up the URL in available references for rich metadata (title, snippet)
            const matchedRef = allAvailableRefs.find(ref => ref.link === href);
            return (
              <InlineCitation
                citationNumber={matchedRef?.index}
                reference={matchedRef}
                linkText={childText || href}
                linkUrl={href}
              />
            );
          }
          // Sandbox file link written by the model after code_exec_show
          // (e.g. [arquivo.pdf](sandbox:/workspace/arquivo.pdf)). The href has
          // no real navigable target — clicking it triggers the actual download
          // of the delivered file. Falls back to plain text if it can't be
          // resolved, so a stale/invented link never looks like a dead hyperlink.
          if (href && /^sandbox:/i.test(href)) {
            return (
              <a
                href={href}
                onClick={async (e) => {
                  e.preventDefault();
                  await downloadSandboxFile(href);
                }}
                style={{ cursor: 'pointer' }}
                {...props}
              >
                {children}
              </a>
            );
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          );
        },
      img: ({
          node,
          src,
          alt,
          ...props
        }: {
          node: any;
          src?: string;
          alt?: string;
          [key: string]: any;
        }) => {
          // Generated charts/images written as ![](sandbox:/workspace/x.png) are
          // delivered as a separate file block (file_ref) — don't also render the
          // raw markdown <img> (its sandbox/workspace src can't load on the web).
          if (src && (src.startsWith('sandbox:') || src.startsWith('/workspace/'))) {
            return null;
          }
          return (
          <img
            src={src}
            alt={alt || ''}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              marginBlock: '0.75rem',
              display: 'block',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
            {...props}
          />
          );
        },
    };


    const markdownProps = {
      linkTarget: "_blank" as const,
      // Preserve `sandbox:` file links (resolved client-side to a download in
      // the `a:` component) while keeping the default sanitisation — which
      // would otherwise strip the href as an unsafe protocol — for everything
      // else (blocks javascript:, etc.).
      transformLinkUri: (uri: string) =>
        /^sandbox:/i.test(uri) ? uri : uriTransformer(uri),
      remarkPlugins: [
        remarkGfm,
        [remarkMath, { singleDollarTextMath: false }],
      ] as any,
      rehypePlugins: [
        rehypeKatex,
        [
          rehypeHighlight,
          {
            detect: true,
            ignoreMissing: true,
            subset: codeLanguageSubset,
          },
        ],
      ] as any,
      components,
    };

    return (
      <StyledKatexContainer>
        {segments.map((seg, i) =>
          seg.type === 'table' ? (
            <TableBlock key={`table-${i}-${seg.content.length}`}><StyledReactMarkdown remarkPlugins={[remarkGfm]} components={{table: ({ node, ...props }: any) => <table {...props} />,td: ({ node, children, ...props }: any) => <td {...props}>{processChildren(children)}</td>,th: ({ node, children, ...props }: any) => <th {...props}>{processChildren(children)}</th>,}}>{seg.content}</StyledReactMarkdown></TableBlock>
          ) : (
            <StyledReactMarkdown key={`text-${i}`} {...markdownProps}>
              {seg.content}
            </StyledReactMarkdown>
          )
        )}
        {hasIncompleteTable && <TableLoadingPlaceholder />}
      </StyledKatexContainer>
    )

  }

  const isQuotaInfoOpen = Boolean(quotaInfoAnchorEl);
  const quotaPopoverId = isQuotaInfoOpen ? 'quota-info-popover' : undefined;

  const quotaInfoContent = (variant: QuotaInfoVariant) => (
    <Box sx={{ p: '8px 12px', maxWidth: 260 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        {variant === 'remaining' ? 'Cota gratuita restante:' : 'Cotas gratuitas'}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        <li>Mensagens: {freeQuota?.messages ?? '—'}</li>
      </Box>
    </Box>
  );

  const renderQuotaInfoTrigger = (variant: QuotaInfoVariant) => {
    const sharedProps = {
      'aria-label': 'Cotas gratuitas',
      className: 'quota-icon assistant-action-button',
    };

    if (isMobile) {
      return (
        <ActionIconButton
          {...sharedProps}
          aria-describedby={quotaPopoverId}
          onClick={(event) => handleQuotaInfoClick(event, variant)}
        >
          <InfoOutlinedIcon />
        </ActionIconButton>
      );
    }

    return (
      <CustomTooltip placement="top" title={quotaInfoContent(variant)}>
        <ActionIconButton {...sharedProps}>
          <InfoOutlinedIcon />
        </ActionIconButton>
      </CustomTooltip>
    );
  };

  const hasActiveChat = Boolean(activeChatID["chatID"]);
  const shouldShowLandingPrompt = !loadingHistory && (!hasActiveChat || messageHistory.length === 0);
  const shouldShowHistoryOverlay =
    loadingHistory &&
    hasActiveChat &&
    !isFirstMessage &&
    !activeChatID["skipUpdate"];

  let displayedName = user?.given_name && user?.family_name
    ? `${user.given_name} ${user.family_name}`
    : user?.name ?? user?.email ?? "";
  const hasName = Boolean(displayedName);
  const greetingText = hasName ? `Olá, ${displayedName}` : 'Olá';
  const [typedGreeting, setTypedGreeting] = useState('');

  useEffect(() => {
    if (!shouldShowLandingPrompt) {
      setTypedGreeting(greetingText);
      return;
    }

    if (!hasName) {
      setTypedGreeting(greetingText);
      return;
    }

    let currentIndex = 0;
    setTypedGreeting('');

    const intervalId = window.setInterval(() => {
      currentIndex += 1;
      setTypedGreeting(greetingText.slice(0, currentIndex));

      if (currentIndex >= greetingText.length) {
        window.clearInterval(intervalId);
      }
    }, GREETING_TYPING_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [greetingText, shouldShowLandingPrompt, hasName]);

  const renderSelectedFilesPreview = (context: 'landing' | 'input') => {
    if (!selectedFiles || selectedFiles.length === 0 || uploadingFiles) {
      return null;
    }

    const fileCount = selectedFiles.length;
    const shouldCondense = fileCount >= CONDENSED_ATTACHMENT_THRESHOLD;
    const filesArray = Array.from(selectedFiles);

    const renderGridPreview = () => (
      <AllFilesContainer fileCount={fileCount} $isMobile={isMobile}>
        {filesArray.map((file: File) => {
          const variant = getPlaceholderVariant(file.name);
          const isTextVariant = variant === 'textCsv' || variant === 'textTxt';
          const hasTextPreview = Boolean(fileTextPreviews[file.name]);
          const fileKey = getFileKey(file);
          const pageCount = numPages?.[file.name];
          const showPageBadge = shouldShowPageCount(file.name, pageCount);

          return (
            <FileContainer key={fileKey}>
              <ThumbWrapper>
                <CustomTooltip title={file.name} placement="top">
                  {filePreviews[file.name] ? (
                    <Clickable onClick={() => handleOpenPreview(file.name)}>
                      <FileThumb src={filePreviews[file.name]} alt={file.name} />
                    </Clickable>
                  ) : isTextVariant && hasTextPreview ? (
                    <Clickable onClick={() => handleOpenPreview(file.name)}>
                      <FileTextPreviewThumb $variant={variant}>
                        <TextPlaceholderIcon />
                        <FileTextPreviewName title={file.name}>{file.name}</FileTextPreviewName>
                        <FileTextPreviewType>{placeholderTypeLabels[variant]}</FileTextPreviewType>
                      </FileTextPreviewThumb>
                    </Clickable>
                  ) : (
                    <FileTextPreviewThumb $variant={variant}>
                      {getVariantIcon(variant)}
                      <FileTextPreviewName title={file.name}>{file.name}</FileTextPreviewName>
                      <FileTextPreviewType>{placeholderTypeLabels[variant]}</FileTextPreviewType>
                    </FileTextPreviewThumb>
                  )}
                </CustomTooltip>
                {showPageBadge && numPages && (
                  <PageCountBadge>
                    {formatPageCountLabel(pageCount!)}
                  </PageCountBadge>
                )}
                <RemoveOverlayButton
                  aria-label={`Remover ${file.name}`}
                  onClick={(e) => { e.stopPropagation(); handleFileDelete(file); }}
                  title="Remover"
                >
                  <RemoveXIcon />
                </RemoveOverlayButton>
              </ThumbWrapper>
            </FileContainer>
          );
        })}
      </AllFilesContainer>
    );

    const renderCondensedPreview = () => (
      <CondensedAttachmentList $context={context} fileCount={fileCount}>
        {filesArray.map((file: File) => {
          const variant = getPlaceholderVariant(file.name);
          const fileKey = getFileKey(file);
          const pageCount = numPages?.[file.name];
          const showPageInfo = shouldShowPageCount(file.name, pageCount);
          const _lowerName = file.name.toLowerCase();
          const _isOffice = _lowerName.endsWith('.docx') || _lowerName.endsWith('.xlsx') || _lowerName.endsWith('.rtf');
          const canPreview =
            Boolean(filePreviews[file.name]) ||
            Boolean(fileTextPreviews[file.name]) ||
            isPdfFile(file.name) ||
            isImageFile(file.name) ||
            _isOffice;

          return (
            <CondensedAttachmentRow
              key={fileKey}
              $clickable={canPreview}
              onClick={canPreview ? () => handleOpenPreview(file.name) : undefined}
            >
              <CondensedAttachmentMain>
                <CondensedAttachmentIcon $variant={variant}>
                  {getVariantIcon(variant)}
                </CondensedAttachmentIcon>
                <CondensedAttachmentInfo>
                  <CustomTooltip title={file.name} placement="top">
                    <CondensedAttachmentName>{file.name}</CondensedAttachmentName>
                  </CustomTooltip>
                  <CondensedAttachmentMeta>
                    {placeholderTypeLabels[variant]}
                    {showPageInfo ? ` • ${formatPageCountLabel(pageCount!)}` : ''}
                  </CondensedAttachmentMeta>
                </CondensedAttachmentInfo>
              </CondensedAttachmentMain>
              <CondensedAttachmentActions>
                {showPageInfo && (
                  <AttachmentPagePill>{formatPageCountLabel(pageCount!)}</AttachmentPagePill>
                )}
                <CondensedAttachmentRemoveButton
                  aria-label={`Remover ${file.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleFileDelete(file);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </CondensedAttachmentRemoveButton>
              </CondensedAttachmentActions>
            </CondensedAttachmentRow>
          );
        })}
      </CondensedAttachmentList>
    );

    return (
      <AttachmentPreviewStack $context={context}>
        <AttachmentSummary>
          <AttachmentSummaryText>
            {fileCount === 1 ? '1 item anexado' : `${fileCount} itens anexados`}
            {totalSelectedPages > 0 && ` · ${formatPageCountLabel(totalSelectedPages)} (máx. ${MAX_ATTACHMENT_PAGES})`}
          </AttachmentSummaryText>
          <AttachmentSummaryHint>
            Limite de {MAX_ATTACHMENT_PAGES} páginas. Cada imagem conta como 1 página.
          </AttachmentSummaryHint>
        </AttachmentSummary>
        {shouldCondense ? renderCondensedPreview() : renderGridPreview()}
      </AttachmentPreviewStack>
    );
  };

  const handleCategorySelect = (categoryId: string) => {
    setPromptBankInitialCategory(categoryId);
    setPromptBankOpen(true);
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    setPromptBankOpen(false);
    setPromptBankInitialCategory(undefined);
    setSelectedPrompt(prompt);
  };

  const handleClosePromptFiller = () => {
    setSelectedPrompt(null);
  };

  const handleBackToPromptBank = () => {
    setSelectedPrompt(null);
    setPromptBankOpen(true);
  };

  const handlePromptSubmit = (filledPrompt: string, _files?: File[], systemPrefix?: string) => {
    setSelectedPrompt(null);
    setPendingSystemPrefix(systemPrefix);
    setCurrentMessage(filledPrompt);
  };

  const PromptSuggestionsComponent = () => {
    // Show landing only when there is no active chat and no messages
    if (!shouldShowLandingPrompt) return null;

    return (
      <LandingPromptWrapper $isMobile={isMobile} $keyboardOffset={keyboardOffset}>
        <PromptSection>
          <MaritacaBirdLogo size={80} color={getModelColor(modelChat, dark).primary} />
          <LandingHeadline aria-live="polite">{typedGreeting}</LandingHeadline>
          <LandingSearchBar>
            <ChatInputArea isLanding />
          </LandingSearchBar>
          <CategoryCards onSelectCategory={handleCategorySelect} refreshTrigger={promptsRefreshTrigger} modelColor={getModelColor(modelChat, dark).primary} modelAccent={getModelColor(modelChat, dark).accent} modelName={modelChat} />
        </PromptSection>
      </LandingPromptWrapper>
    );
  };

  const getBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // Send a message to our api.
  const sendMessage = async (chatId: string, message: string, token: string, opts: { panorama?: boolean; panorama_decline?: boolean; bodyContent?: string; panoramaPeticao?: string } = {}): Promise<any> => {
    // Use the personality's model if one is active, otherwise use the selected model
    const personalityModel = getActiveModelName();
    const effectiveModel = personalityModel
      ? personalityModel
      : (typeof modelChat === 'string' && modelChat.trim().length > 0
          ? modelChat
          : DEFAULT_CHAT_MODEL);
    if (selectedFiles) {
      setUploadingFiles(true);
      setLoadingMessage(selectedFiles.length > 1 ? "Fazendo upload dos arquivos..." : "Fazendo upload do arquivo...");
      if (selectedFiles.length > 0) {
        setPendingOcrRefresh(true);
        setExtractingFiles(selectedFiles.map((file) => file.name));
      }
    }
    const url = `${API_BASE}/api/chat/message`;
    let webSearchEnabled = false;
    if (webSearch && modelsWithWebSearchAllowed.includes(effectiveModel)) {
      webSearchEnabled = true;
    }
    // Data Ocean: honor the toggle for sabia-4/sabiazinho-4; always on for the
    // competidores; off for any other model (backend ignores it there anyway).
    const dataOceanEnabled = DATA_OCEAN_TOGGLE_MODELS.has(effectiveModel)
      ? dataOcean
      : DATA_OCEAN_MODEL_SET.has(effectiveModel);

    const data: Record<string, any> = {
      chat_id: chatId,
      content: opts.bodyContent ?? message,
      model: effectiveModel,
      position: messageHistory.length,
      is_user: true,
      files: selectedFiles ? await Promise.all(selectedFiles.map(async (file) => {
        const base64 = await getBase64(file) as string;
        return { filename: file.name, file_content: base64.toString().split(',')[1] };
      })) : [],
      web_search: webSearchEnabled,
      code_execution: codeExecution && modelsWithCodeExecutionAllowed.includes(effectiveModel),
      data_ocean: dataOceanEnabled,
      reasoning: reasoningRef.current,  // fresh value (state closure can be stale at send time)
      use_competitor: useCompetitor,
      source_interface: "web",
      deep_research: featureFlags.deep_research && deepResearch,
      deep_research_mode: deepResearchMode,
      panorama: opts.panorama || false,
      panorama_decline: opts.panorama_decline || false,
      panorama_peticao: opts.panoramaPeticao,
      // Opt-in precise location (browser geolocation); backend prefers it over IP.
      ...(usePreciseLocationRef.current && preciseLocationRef.current ? { user_location: preciseLocationRef.current } : {}),
      // Delta streaming: server sends only the new text chunk per message
      // event instead of the full accumulated answer. The client appends.
      // Massive bandwidth win for long responses on slow connections.
      delta: true,
    };

    // Reset deep research state for new request (scoped to this chat).
    if (deepResearch) {
      setDeepResearch(false);
      clearDeepResearchForChat(chatId);
    }
    // Reset panorama progress for new request (re-populado pelos eventos panorama_status).
    clearPanoramaForChat(chatId);

    // Add personality_id if an active model is selected
    if (activeModelId) {
      data.personality_id = activeModelId;
    }
    // Add system prefix from prompt template (invisible instructions for the LLM)
    if (pendingSystemPrefix) {
      data.system_prefix = pendingSystemPrefix;
      setPendingSystemPrefix(undefined);
    }
    handleStartResponse(chatId, message, selectedFiles ? selectedFiles.map((file) => file.name) : undefined);

    // Capture whether this upload contains any PDFs to control OCR text rendering
    try {
      const hasPdf = !!(selectedFiles && selectedFiles.some((f) => (
        (f as any)?.type === 'application/pdf' || f.name?.toLowerCase?.().endsWith('.pdf')
      )));
      setCurrentUploadHasPdf(hasPdf);
    } catch {}

    setSelectedFiles(null);
    // reset the number of pages
    setNumPages(null);
    return new SSE(url, {
      headers: {
        authorization: `Auth ${token}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      payload: JSON.stringify(data),
      method: 'POST',
      // Stall watchdog: if the stream goes silent (proxy/wifi blip) the
      // generation keeps running server-side, so a synthetic transient error
      // lets handleError resume it as a background stream instead of failing.
      inactivityTimeoutMs: INACTIVITY_TIMEOUT_MS,
    });
  };

  const handleOCRStart = () => {
    if (apiError){
      return
    }
    
    if (selectedFiles && selectedFiles.length > 1) {
      setLoadingMessage("Extraindo texto dos arquivos...");
    } else {
      setLoadingMessage("Extraindo texto do arquivo...");
    }
    // Enter OCR phase; messages received now may be OCR text
    setInOcrPhase(true);
  }

  // Set state for message being generated.
  const handleStartResponse = (chatId: string, message: string, filenames?: string[]) => {
    setCurrentMessage("");
    setMessageBeingGenerated("");
    pendingNewCycleRef.current = false;
    // Show an immediate loading hint so the assistant bubble isn't blank
    // during the 1-2s gap between send and the first server event.
    setLoadingMessage(reasoningRef.current ? 'Pensando...' : 'Gerando resposta...');
    setMessageHistory(prev => [
      ...prev,
      // position must match body.position (= messageHistory.length at send) so the
      // bg-stream resume's user_message frame dedups against it instead of duplicating.
      {"chat_id": chatId, "role":"user", "content": message, "filenames": filenames, "position": prev.length},
      {"role":"assistant", "content": "", references: []}
    ]);
    setGeneratingChatId(chatId);
    // A new FOREGROUND run now owns this chat's view. If a previous run left
    // the chat marked as background-generating, unmark it — otherwise the
    // bg-stream resume effect attaches as a SECOND consumer of the same run
    // alongside this tab's live POST stream (its lagged replay could append a
    // duplicate "Pensando" block that only the end-of-run refresh cleaned up).
    setBackgroundGeneratingChatIds(prev => {
      if (!prev.has(chatId)) return prev;
      const next = new Set(prev);
      next.delete(chatId);
      return next;
    });
    setAllowErrorRetry(true);
    setShowPlanUpgradeOption(false);
    setChatErrorMessage("");
    setQuotaBadgeIndex(null); // will be set on 'quota' event
    // Reset debug tool calls for new generation
    toolCallsAccRef.current = [];
    setCurrentToolCalls([]);
  };

  const handleMessage = (e: any) => {
    const rawText = e.data;
    if (!rawText || !rawText.length) return;

    // A dropped/stalled stream can flush a truncated frame here; guard the
    // parse so a malformed payload skips this frame instead of throwing an
    // uncaught SyntaxError that crashes the chat view (Sentry CHAT-MARITACA-1Z).
    const payload = parseStreamData(rawText);
    if (payload == null) return;
    const cycleText = payload["text"];
    // During OCR phase for PDFs, do not show extracted text in chat
    if (inOcrPhase && currentUploadHasPdf) return;

    const isNewCycle = pendingNewCycleRef.current;
    if (isNewCycle) {
      pendingNewCycleRef.current = false;
      // A new text cycle starts -> any preceding tool_action items just
      // finished. Flip their pending flag off so the pulse stops and they
      // settle into the static historical marker.
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = prev[prev.length - 1] as any;
        if (lastMsg.role !== 'assistant' || !Array.isArray(lastMsg.content)) return prev;
        let changed = false;
        const newContent = lastMsg.content.map((item: any) => {
          if (item?.type === 'tool_action' && item.pending) {
            changed = true;
            return { ...item, pending: false };
          }
          return item;
        });
        if (!changed) return prev;
        return [...prev.slice(0, -1), { ...lastMsg, content: newContent }];
      });
    }
    setLoadingMessage("");

    // We track the running accumulated text outside the setMessageHistory
    // callback so it can be fed into setMessageBeingGenerated WITHOUT a
    // setTimeout(0). The timeout broke React's automatic batching and forced
    // two re-renders per text chunk — the screen visibly shook during
    // streaming on fast networks.
    let runningText: string | null = null;

    setMessageHistory(prev => {
      const lastMsg = prev[prev.length - 1];
      if (!lastMsg || lastMsg.role !== 'assistant') return prev;

      let content: ContentItem[];
      if (!Array.isArray(lastMsg.content)) {
        // First message event: initialize as ContentItem[]
        content = [{ type: 'text', text: cycleText }];
      } else if (isNewCycle) {
        // New cycle after a tool: push a new text item
        content = [...lastMsg.content, { type: 'text', text: cycleText }];
      } else {
        // Same cycle: APPEND the delta to the last text item — UNLESS the last item
        // isn't text (e.g. a chart/image was just delivered inline). In that case
        // start a NEW text item so text written after the image doesn't merge back
        // into the block before it. Deep research emits accumulated text, so we
        // replace in that mode to preserve current behavior.
        content = [...lastMsg.content];
        const lastItem = content[content.length - 1];
        if (lastItem && lastItem.type === 'text') {
          const prevText = (lastItem as TextContentItem).text || '';
          const newText = deepResearch ? cycleText : prevText + cycleText;
          content[content.length - 1] = { ...(lastItem as TextContentItem), text: newText };
        } else {
          content.push({ type: 'text', text: cycleText });
        }
      }
      const updatedAssistant = { ...lastMsg, content };
      const lastText = (content[content.length - 1] as any)?.text;
      if (typeof lastText === 'string') {
        runningText = lastText;
      }
      return [...prev.slice(0, -1), updatedAssistant];
    });

    // Same-tick setter: React 18 batches this with the setMessageHistory
    // above into a single re-render (the same way two setStates in any
    // event handler are batched).
    if (runningText !== null) {
      setMessageBeingGenerated(runningText);
    }
  };

  // Receive streamed quota updates from backend (only sent for free users)
  const handleQuota = (e: any) => {
    try {
      const payload = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (payload && typeof payload === 'object') {
        setFreeQuota({
          messages: Number(payload.messages ?? 0),
        });
        // attach badge to the last assistant message placeholder
        setQuotaBadgeIndex(Math.max(0, messageHistory.length - 1));
      }
    } catch {}
  };



  // Handle files generated by code execution — append in-place to ContentItem[]
  const handleCodeExecFile = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
        if (lastMsg.role !== 'assistant') return prev;

        let content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);

        if (data.file_id) {
          content.push({
            type: 'file_ref',
            file_id: data.file_id,
            title: data.title || '',
            file_type: data.file_type || '',
            mime_type: data.mime_type || '',
            editable: data.editable || false,
            blob_size_bytes: data.blob_size_bytes || 0,
          } as FileRefContentItem);
        } else {
          content.push({
            type: 'input_file',
            filename: data.path || '',
            file_data: data.content_base64 || '',
            mime_type: data.mime_type || '',
            size_bytes: data.size_bytes || 0,
            content: data.content,  // preserve pre-parsed content (e.g. {text: "..."} for DOCX)
          } as FileContentItem);
        }
        return [...prev.slice(0, -1), { ...lastMsg, content }];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.code_exec_file' });
      console.error('[Code Exec File] Failed to parse file data:', err);
    }
  };

  const handleMessageComposerSignal = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    const channelLabels: Record<string, string> = {
      email: 'email', message: 'mensagem', other: 'mensagem',
    };
    const label = channelLabels[payload.channel] || 'mensagem';
    setLoadingMessage(`Compondo ${label}...`);
    debugCapture('message_composer', payload);
  };

  const handleMessageComposer = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
        if (lastMsg.role !== 'assistant') return prev;

        let content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);

        content.push({
          type: 'message_composer',
          channel: data.channel || 'other',
          versions: data.versions || [],
        } as MessageComposerContentItem);

        return [...prev.slice(0, -1), { ...lastMsg, content }];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.message_composer' });
      console.error('[Message Composer] Failed to parse composer data:', err);
    }
  };

  // Panorama Juridico (DEBUG): dump dos arquivos do run dir -> painel colapsavel no chat.
  const handlePanoramaDebug = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
        if (lastMsg.role !== 'assistant') return prev;
        let content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
        content.push({ type: 'panorama_debug', dump: data.dump } as any);
        return [...prev.slice(0, -1), { ...lastMsg, content }];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.panorama_debug' });
    }
  };

  // Panorama Juridico: o modelo propos rodar (propor_panorama) -> card de confirmacao.
  const handlePanoramaConfirmation = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
        if (lastMsg.role !== 'assistant') return prev;
        let content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
        content.push({ type: 'panorama_confirmation', justificativa: data.justificativa || '', active: true } as any);
        return [...prev.slice(0, -1), { ...lastMsg, content }];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.panorama_confirmation' });
    }
  };

  // Panorama Juridico: progresso da pesquisa (body.panorama em andamento).
  const handlePanoramaStatus = (e: any) => {
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    const chatId = generatingChatIdRef.current;
    if (!chatId) return;
    updatePanoramaForChat(chatId, prev => ({
      phase: payload.phase || prev.phase,
      progress: typeof payload.progress === 'number' ? payload.progress : prev.progress,
      nSearches: typeof payload.n_searches === 'number' ? payload.n_searches : prev.nSearches,
      nDocs: typeof payload.n_docs === 'number' ? payload.n_docs : prev.nDocs,
      nTheses: typeof payload.n_theses === 'number' ? payload.n_theses : prev.nTheses,
      inputTokens: typeof payload.input_tokens === 'number' ? payload.input_tokens : prev.inputTokens,
      outputTokens: typeof payload.output_tokens === 'number' ? payload.output_tokens : prev.outputTokens,
      detail: payload.detail || prev.detail,
    }));
    if (chatId === activeChatIDRef.current) {
      setLoadingMessage(payload.detail || 'Rodando o Panorama Juridico...');
    }
  };

  // Final reasoning event (full draft, also persisted). If the live deltas
  // already built a reasoning block, finalize it with the complete text rather
  // than appending a duplicate.
  const handleReasoning = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      const full = data.reasoning || '';
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        const isAssistant = last.role === 'assistant';
        // Reasoning happens BEFORE the first text frame, so on a fresh turn the
        // assistant scaffold may not exist yet — create it instead of dropping
        // the event (dropping is what kept the thinking invisible until the
        // history refresh).
        const lastMsg = (isAssistant
          ? { ...last }
          : { role: 'assistant', content: [] as ContentItem[], references: [] }) as ChatMessage;
        const content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
        // The full `reasoning` event arrives at cycle end — sometimes AFTER the
        // cycle's text already streamed (when the worker emitted the reasoning
        // tool call before the text). Find the reasoning block the deltas were
        // building (its streamed text is a prefix of this full text) by scanning
        // back PAST any intervening text/tool, and finalize it IN PLACE — never
        // push a duplicate box. Only start a new block if this is genuinely a
        // different reasoning (no prefix-compatible block to attach to).
        let idx = -1;
        for (let i = content.length - 1; i >= 0; i--) {
          if ((content[i] as any).type !== 'reasoning') continue;
          const ex = ((content[i] as any).reasoning || '') as string;
          // ex.includes(full): the live block may hold MORE than this event's
          // draft — the routing pre-pass streams into the same block before
          // the cycle-0 rascunho (which is what `full` carries). Keep the
          // longer text; never truncate what the user already watched stream.
          if (!ex || full.startsWith(ex) || ex.startsWith(full) || ex.includes(full) || ex === full) idx = i;
          break; // only consider the MOST RECENT reasoning block
        }
        if (idx >= 0) {
          const exText = ((content[idx] as any).reasoning || '') as string;
          content[idx] = {
            ...(content[idx] as any),
            reasoning: exText.length >= full.length ? exText : full,
            pending: false,
          };
        } else {
          // No prefix-compatible block: this is a LATER cycle's reasoning. WHERE
          // it goes depends on what sits between it and the previous reasoning:
          //   • only tool/code steps (no visible answer text) → PUSH a new block
          //     at the END, so the thinking group renders reasoning→tool→reasoning
          //     IN ORDER (the tool stays where it was called, not shoved last).
          //   • answer TEXT already streamed → merge into the previous block, so
          //     we don't drop a second "Pensou" box below the answer.
          let lastReasoningIdx = -1;
          for (let i = content.length - 1; i >= 0; i--) {
            if ((content[i] as any).type === 'reasoning') { lastReasoningIdx = i; break; }
          }
          const GROUPABLE_TYPES = new Set(['reasoning', 'tool_action', 'code_execution']);
          let tailIsThinkingOnly = lastReasoningIdx >= 0;
          for (let i = lastReasoningIdx + 1; i < content.length; i++) {
            const it = content[i] as any;
            if (!it) { tailIsThinkingOnly = false; break; }
            if (it.type === 'text') {
              if (((it.text || '') as string).trim()) { tailIsThinkingOnly = false; break; }
              continue; // empty text is transparent
            }
            if (!GROUPABLE_TYPES.has(it.type)) { tailIsThinkingOnly = false; break; }
          }
          if (lastReasoningIdx >= 0 && !tailIsThinkingOnly) {
            const exText = ((content[lastReasoningIdx] as any).reasoning || '') as string;
            content[lastReasoningIdx] = {
              ...(content[lastReasoningIdx] as any),
              reasoning: exText ? exText + '\n\n' + full : full,
              pending: false,
            };
          } else {
            content.push({ type: 'reasoning', reasoning: full, pending: false } as any);
          }
        }
        const next = { ...lastMsg, content } as ChatMessage;
        return isAssistant ? [...prev.slice(0, -1), next] : [...prev, next];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.reasoning' });
      console.error('[Reasoning] Failed to parse reasoning data:', err);
    }
  };

  // Live reasoning stream: each delta appends to the (single) reasoning block on
  // the current assistant message, so "Raciocínio" fills in token-by-token during
  // the "Pensando..." phase, before the answer streams.
  const handleReasoningDelta = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      const delta = data.text || '';
      if (!delta) return;
      setLoadingMessage("Pensando...");
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastEntry = prev[prev.length - 1];
        const isAssistant = lastEntry.role === 'assistant';
        // The reasoning phase streams BEFORE the first text frame, so on a
        // fresh turn there is no assistant message yet. Create the scaffold
        // here — without it every reasoning_delta was silently dropped and
        // "Pensando" never appeared during generation.
        const lastMsg = (isAssistant
          ? { ...lastEntry }
          : { role: 'assistant', content: [] as ContentItem[], references: [] }) as ChatMessage;
        const content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
        // Stream the delta into the CURRENT reasoning burst (last item if it's a
        // reasoning block) — else start a NEW one. This way a second reasoning
        // pass (after text/tools) builds its own box instead of being appended
        // to the first one.
        const last = content[content.length - 1] as any;
        if (last && last.type === 'reasoning') {
          content[content.length - 1] = { ...last, reasoning: (last.reasoning || '') + delta, pending: true };
        } else {
          content.push({ type: 'reasoning', reasoning: delta, pending: true } as any);
        }
        const next = { ...lastMsg, content } as ChatMessage;
        return isAssistant ? [...prev.slice(0, -1), next] : [...prev, next];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.reasoning_delta' });
      console.error('[ReasoningDelta] Failed to parse reasoning delta:', err);
    }
  };

  // Stream the code/command of a code_exec_python|bash tool call LIVE as the
  // model writes it, instead of showing a bare spinner for the whole (often
  // long) generation. The deltas build a `streaming` code_execution row; the
  // later authoritative code_exec_python|bash signal reconciles it in place
  // (appendPendingCodeExec), and code_exec_result flips it done.
  const handleCodeExecDelta = (e: any) => {
    try {
      const data = JSON.parse(e.data);
      const tool = data.tool;
      const delta = data.text || '';
      if (!delta || (tool !== 'code_exec_python' && tool !== 'code_exec_bash')) return;
      const isPython = tool === 'code_exec_python';
      setLoadingMessage(isPython ? 'Escrevendo Python...' : 'Escrevendo comando...');
      const key = isPython ? 'code' : 'command';
      setMessageHistory(prev => {
        if (prev.length === 0) return prev;
        const lastEntry = prev[prev.length - 1];
        // Mirrors handleReasoningDelta: the tool-call args can stream before any
        // assistant text, so scaffold an assistant message if needed.
        const isAssistant = lastEntry.role === 'assistant';
        const lastMsg = (isAssistant
          ? { ...lastEntry }
          : { role: 'assistant', content: [] as ContentItem[], references: [] }) as ChatMessage;
        const content: ContentItem[] = Array.isArray(lastMsg.content)
          ? [...lastMsg.content]
          : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
        // Append to the current streaming row of the same tool (last item) — else
        // open a new one. A fresh tool call after reasoning/text starts its own box.
        const last = content[content.length - 1] as any;
        if (last && last.type === 'code_execution' && last.tool === tool && last.streaming) {
          const prevCode = (last.args && last.args[key]) || '';
          content[content.length - 1] = { ...last, args: { ...(last.args || {}), [key]: prevCode + delta } };
        } else {
          content.push({ type: 'code_execution', tool, args: { [key]: delta }, pending: true, streaming: true } as any);
          pendingNewCycleRef.current = true;
        }
        const next = { ...lastMsg, content } as ChatMessage;
        return isAssistant ? [...prev.slice(0, -1), next] : [...prev, next];
      });
    } catch (err) {
      reportHandledError(err, { category: 'parse', feature: 'chat.sse.code_exec_delta' });
    }
  };

  // Handle code execution tool events for loading messages
  const debugCapture = (toolName: string, payload: any) => {
    if (!debugModeRef.current) return;
    toolCallsAccRef.current = [...toolCallsAccRef.current, {
      toolName, args: payload, timestamp: Date.now(),
    }];
    setCurrentToolCalls(toolCallsAccRef.current);
  };

  // Live-append a pending code_execution content item to the last assistant
  // message so the user sees the code/path immediately. The matching
  // code_exec_result event later flips `pending: false` and fills stdout/stderr.
  const appendPendingCodeExec = (
    tool: 'code_exec_bash' | 'code_exec_python' | 'code_exec_write' | 'code_exec_read' | 'code_exec_show',
    args: { command?: string; code?: string; path?: string; content?: string }
  ) => {
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
      if (lastMsg.role !== 'assistant') return prev;
      const content: ContentItem[] = Array.isArray(lastMsg.content)
        ? [...lastMsg.content]
        : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);

      // If the most recent code_execution row is the same tool, still
      // pending, AND has empty/no args yet, this is a follow-up signal
      // for the SAME call with the real arguments — fill it in place
      // instead of stacking another empty row.
      const isEmptyArgs = (a: any) =>
        !a || (!a.command && !a.code && !a.path && !a.content);
      for (let i = content.length - 1; i >= 0; i--) {
        const it: any = content[i];
        if (it?.type !== 'code_execution') continue;
        if (it.tool !== tool) break; // older code_exec of different kind — push new
        // A row we live-streamed the code into (code_exec_delta): this signal is
        // the SAME call with the authoritative full args — replace in place and
        // clear `streaming` so it stops being treated as a delta target. Keeps
        // `pending` until the result arrives. Without this the non-empty streamed
        // args fall through and we'd stack a duplicate code block.
        if (it.streaming) {
          const merged = { ...(it.args || {}) };
          for (const k of Object.keys(args)) {
            if ((args as any)[k]) merged[k] = (args as any)[k]; // non-empty authoritative wins
          }
          content[i] = { ...it, args: merged, streaming: false };
          pendingNewCycleRef.current = true;
          return [...prev.slice(0, -1), { ...lastMsg, content }];
        }
        if (it.pending && isEmptyArgs(it.args)) {
          content[i] = { ...it, args: { ...(it.args || {}), ...args } };
          if (!isEmptyArgs(args)) {
            pendingNewCycleRef.current = true;
          }
          return [...prev.slice(0, -1), { ...lastMsg, content }];
        }
        break; // most recent same-tool item already has args — push new
      }

      // Also drop fresh-empty signals when no existing row to absorb them.
      // A bare 'code_exec_bash' event with no command is just streaming
      // noise; the next signal with the real args will create the row.
      // BUT — a malformed tool call (server rejects it for missing args) will
      // ONLY ever emit the empty signal, never a follow-up. We still need to
      // mark a cycle boundary so the next engine call's text becomes a new
      // text item instead of fusing with the previous one.
      if (isEmptyArgs(args)) {
        pendingNewCycleRef.current = true;
        return [...prev.slice(0, -1), { ...lastMsg, content }];
      }

      content.push({
        type: 'code_execution',
        tool,
        args,
        pending: true,
      } as any);
      pendingNewCycleRef.current = true;
      return [...prev.slice(0, -1), { ...lastMsg, content }];
    });
  };

  const handleCodeExecBash = (e: any) => {
    try {
      const payload = JSON.parse(e.data);
      const command = payload.command || '';
      const shortCmd = command.length > 80 ? command.substring(0, 80) + '...' : command;
      setLoadingMessage(`Executando: ${shortCmd}`);
      debugCapture('code_exec_bash', payload);
      appendPendingCodeExec('code_exec_bash', { command });
    } catch {
      setLoadingMessage("Executando código...");
    }
  };

  const handleCodeExecPython = (e: any) => {
    try {
      const payload = JSON.parse(e.data);
      const code = payload.code || '';
      const shortCode = code.length > 80 ? code.substring(0, 80) + '...' : code;
      setLoadingMessage(`Executando Python: ${shortCode}`);
      debugCapture('code_exec_python', payload);
      appendPendingCodeExec('code_exec_python', { code });
    } catch {
      setLoadingMessage("Executando Python...");
    }
  };

  const handleCodeExecWrite = (e: any) => {
    try {
      const payload = JSON.parse(e.data);
      const path = payload.path || '';
      setLoadingMessage(`Escrevendo arquivo: ${path}`);
      debugCapture('code_exec_write', payload);
      appendPendingCodeExec('code_exec_write', { path, content: payload.content || '' });
    } catch {
      setLoadingMessage("Escrevendo arquivo...");
    }
  };

  const handleCodeExecRead = (e: any) => {
    try {
      const payload = JSON.parse(e.data);
      const path = payload.path || '';
      setLoadingMessage(`Lendo arquivo: ${path}`);
      debugCapture('code_exec_read', payload);
      appendPendingCodeExec('code_exec_read', { path });
    } catch {
      setLoadingMessage("Lendo arquivo...");
    }
  };

  const handleCodeExecShow = (e: any) => {
    try {
      const payload = JSON.parse(e.data);
      const path = payload.path || '';
      setLoadingMessage(`Preparando arquivo: ${path}`);
      debugCapture('code_exec_show', payload);
      appendPendingCodeExec('code_exec_show', { path });
    } catch {
      setLoadingMessage("Preparando arquivo...");
    }
  };

  // Fill in the most-recent pending code_execution item with stdout/stderr/etc.
  const handleCodeExecResult = (e: any) => {
    let payload: any;
    try { payload = JSON.parse(e.data); } catch { return; }
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
      if (lastMsg.role !== 'assistant') return prev;
      if (!Array.isArray(lastMsg.content)) return prev;
      const content = [...lastMsg.content];
      // Match the latest pending entry of the same tool, by tool_call_id if available.
      let target = -1;
      for (let i = content.length - 1; i >= 0; i--) {
        const item: any = content[i];
        if (item.type !== 'code_execution') continue;
        if (item.tool !== payload.tool) continue;
        if (payload.tool_call_id && item.tool_call_id && item.tool_call_id !== payload.tool_call_id) continue;
        if (item.pending) { target = i; break; }
        if (target === -1) target = i;
      }
      if (target === -1) {
        content.push({
          type: 'code_execution',
          ...payload,
          pending: false,
        } as any);
      } else {
        const existing: any = content[target];
        content[target] = {
          ...existing,
          ...payload,
          // Preserve the args we showed live (server may have re-shaped them).
          args: { ...(existing.args || {}), ...(payload.args || {}) },
          pending: false,
        };
      }
      return [...prev.slice(0, -1), { ...lastMsg, content }];
    });
    // Result arrived — clear the per-tool transient loading message so
    // 'Preparando arquivo: ...' does not linger when the show completes
    // after the model has already finished writing.
    setLoadingMessage('');
  };

  // Live: search hits (web / jurisprudência / legislação) → append a
  // `search_results` content item to the current assistant message so the
  // result cards show as soon as the search returns (mirrors the persisted
  // content item the server stores for reload).
  const handleSearchResults = (e: any) => {
    let payload: any;
    try { payload = JSON.parse(e.data); } catch { return; }
    if (!payload || !Array.isArray(payload.results) || payload.results.length === 0) return;
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
      if (lastMsg.role !== 'assistant' || !Array.isArray(lastMsg.content)) return prev;
      const content = [...lastMsg.content, { type: 'search_results', ...payload } as any];
      return [...prev.slice(0, -1), { ...lastMsg, content }];
    });
  };

  // Set state for arrived message.
  const handleEndResponse = () => {
    setGeneratingChatId("");
    setLoadingMessage("");
    setMessageBeingGenerated("");
    // Reset OCR/PDF flags after a response completes
    setInOcrPhase(false);
    setCurrentUploadHasPdf(false);
    // Reset live DR state for the chat that just finished (history rendering uses message metadata).
    setDeepResearch(false);
    const endedChatId = generatingChatIdRef.current;
    if (endedChatId) clearDeepResearchForChat(endedChatId);
    // Safety net: the turn is over, so nothing can still be "running". Flip any
    // leftover pending content item to done — otherwise a thinking group (or a
    // tool box) whose result event never arrived/matched stays stuck shimmering
    // "Pensando…" even after later groups show "Pensou" and the answer is done.
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const last: any = prev[prev.length - 1];
      if (last.role !== 'assistant' || !Array.isArray(last.content)) return prev;
      if (!last.content.some((it: any) => it && it.pending)) return prev;
      const content = last.content.map((it: any) => (it && it.pending ? { ...it, pending: false, streaming: false } : it));
      return [...prev.slice(0, -1), { ...last, content }];
    });
    if (endedChatId) clearPanoramaForChat(endedChatId);
  };

  // Push (or merge into) a persistent tool_action ContentItem on the last
  // assistant message. If the most recent item is already a tool_action of
  // the same tool, append the new query to that item instead of creating a
  // new one — so multiple searches in the same turn show as ONE indicator
  // ('Buscou: <latest> +N') with the full list available on expand.
  const pushToolAction = (
    tool: 'web_search' | 'page_scraping' | 'data_ocean_query' | 'erp_query' | 'erp_create_invoice' | 'datajud_query' | 'thinking' | 'buscar_conversas',
    label: string,
    args?: Record<string, any>,
  ) => {
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
      if (lastMsg.role !== 'assistant') return prev;
      const content: ContentItem[] = Array.isArray(lastMsg.content)
        ? [...lastMsg.content]
        : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);

      // Find the most recent tool_action of the same tool to merge into — but
      // ONLY within the current "burst". Any text the model showed the user, a
      // reasoning block, or other tool/code work since then marks a NEW step in
      // the sequence, so the new tool call must open a NEW box below it (rather
      // than being prefixed into the earlier box).
      let mergeIdx = -1;
      for (let i = content.length - 1; i >= 0; i--) {
        const it: any = content[i];
        if (it?.type === 'tool_action' && it.tool === tool) {
          mergeIdx = i;
          break;
        }
        // Stop searching backwards once we hit a boundary: a different tool, a
        // code execution, a reasoning block, or text the user already saw.
        if (it?.type === 'tool_action' || it?.type === 'code_execution') break;
        if (it?.type === 'reasoning') break;
        if (it?.type === 'text' && ((it.text || '') as string).trim()) break;
      }

      if (mergeIdx >= 0) {
        const existing: any = content[mergeIdx];
        const queries = Array.isArray(existing.queries) ? [...existing.queries] : (existing.label ? [existing.label] : []);
        if (label && !queries.includes(label)) queries.push(label);
        const argsArr = Array.isArray(existing.args) ? [...existing.args] : (existing.args ? [existing.args] : []);
        if (args) argsArr.push(args);
        content[mergeIdx] = { ...existing, queries, args: argsArr, pending: true };
      } else {
        content.push({
          type: 'tool_action',
          tool,
          queries: label ? [label] : [],
          args: args ? [args] : [],
          pending: true,
        } as any);
      }
      return [...prev.slice(0, -1), { ...lastMsg, content }];
    });
  };

  const handleWebTrigger = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    // No chip for web search — the rich result-card block (search_results, via
    // the 'tool_results' event) is the indicator, with the query as its header.
    debugCapture('web_search', payload);
  };

  const handlePageScraping = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    const url = payload.url;
    pushToolAction('page_scraping', url || '', payload);
    debugCapture('page_scraping', payload);
  };

  const handleDataOceanQuery = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    // Search tools (jurisprudência/legislação) get result-card blocks instead of
    // the generic "Consultou base de dados" chip — don't also chip them.
    if (payload.tool === 'buscar_jurisprudencia' || payload.tool === 'buscar_legislacao') {
      debugCapture('data_ocean_query', payload);
      return;
    }
    const q = payload.query || payload.description || payload.label || '';
    pushToolAction('data_ocean_query', q, payload);
    debugCapture('data_ocean_query', payload);
  };

  // Non-prod debug (gated server-side by expose_data_ocean_internals): the SQL
  // result behind a data_ocean chip. Attach it to the most recent data_ocean_query
  // tool_action's latest args entry (tools run one-at-a-time, so this result pairs
  // with the most recent data_ocean signal). Renders only when the frontend build
  // flag REACT_APP_SHOW_DATA_OCEAN_INTERNALS is on.
  const handleDataOceanResult = (e: any) => {
    const payload = parseStreamData(e.data);
    if (!payload || !payload.content) return;
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const lastMsg = { ...prev[prev.length - 1] } as ChatMessage;
      if (lastMsg.role !== 'assistant' || !Array.isArray(lastMsg.content)) return prev;
      const content = [...lastMsg.content];
      for (let i = content.length - 1; i >= 0; i--) {
        const it: any = content[i];
        if (it?.type !== 'tool_action' || it.tool !== 'data_ocean_query') continue;
        const args = Array.isArray(it.args) ? [...it.args] : [];
        for (let j = args.length - 1; j >= 0; j--) {
          if (args[j] && !args[j]._do_result) {
            args[j] = { ...args[j], _do_result: payload.content, _do_aviso: payload.aviso || '' };
            content[i] = { ...it, args };
            return [...prev.slice(0, -1), { ...lastMsg, content }];
          }
        }
        break; // most recent data_ocean item already fully attached
      }
      return prev;
    });
  };

  const handleBuscarConversas = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = typeof e?.data === 'string' ? parseStreamData(e.data) : e?.data;
    const q = (payload && (payload.query || payload.label)) || '';
    pushToolAction('buscar_conversas', q, payload);
  };

  const handleERPQuery = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    const resource = payload.resource || 'dados';
    const provider = (payload.provider || 'ERP').toUpperCase();
    pushToolAction('erp_query', `${resource} (${provider})`, payload);
    debugCapture('erp_query', payload);
  };

  const handleERPCreateInvoice = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    const tipo = payload.tipo || 'produto';
    const tipoLabel = tipo === 'servico' ? 'NFS-e' : 'NF-e';
    pushToolAction('erp_create_invoice', tipoLabel, payload);
    debugCapture('erp_create_invoice', payload);
  };

  const handleDataJudQuery = (e: any) => {
    pendingNewCycleRef.current = true;
    const payload = parseStreamData(e.data);
    if (payload == null) return;
    // Prefer a concrete identifier (process number, party name) — fall back
    // to the consulta type so the expanded list still has something.
    const id = payload.numero_processo || payload.parte || payload.classe || payload.tipo_consulta || '';
    pushToolAction('datajud_query', id, payload);
    debugCapture('datajud_query', payload);
  };

  const handleToolResult = (e: any) => {
    if (!debugModeRef.current) return;
    try {
      const payload = JSON.parse(e.data);
      const calls = toolCallsAccRef.current;
      for (let i = calls.length - 1; i >= 0; i--) {
        if (!calls[i].result) {
          calls[i].result = payload.content;
          calls[i].truncated = payload.truncated;
          break;
        }
      }
      toolCallsAccRef.current = [...calls];
      setCurrentToolCalls(toolCallsAccRef.current);
    } catch {}
  };

  // Set event listeners for the inteire life cycle of a message call.
  const setupResponseHandlers = (response: any) => {
    response.addEventListener('message', handleMessage);
    response.addEventListener('web_search', handleWebTrigger);
    response.addEventListener('page_scraping', handlePageScraping);
    response.addEventListener('data_ocean_query', handleDataOceanQuery);
    response.addEventListener('data_ocean_result', handleDataOceanResult);
    response.addEventListener('buscar_conversas', handleBuscarConversas);
    response.addEventListener('erp_query', handleERPQuery);
    response.addEventListener('erp_create_invoice', handleERPCreateInvoice);
    response.addEventListener('datajud_query', handleDataJudQuery);
    response.addEventListener('tool_results', handleSearchResults);
    response.addEventListener('end', handleEndResponse);
    response.addEventListener('quota', handleQuota);
    response.addEventListener('error', handleError);

    // Deep Research SSE listeners — scope writes to the chat that is generating
    // (read the ref, not the captured value, to avoid stale closures).
    response.addEventListener('deep_research_status', (e: any) => {
      const chatId = generatingChatIdRef.current;
      const payload = parseStreamData(e.data);
      if (payload == null) return;
      if (chatId) {
        updateDeepResearchForChat(chatId, prev => ({
          ...prev,
          phase: payload.phase || "",
          progress: payload.progress || 0,
          tokens: payload.input_tokens !== undefined
            ? { input: payload.input_tokens, output: payload.output_tokens || 0 }
            : prev.tokens,
        }));
      }
      const labels: Record<string, string> = {
        planning: "Criando plano de pesquisa...",
        researching: "Pesquisando em multiplas fontes...",
        reviewing: "Verificando completude...",
        synthesizing: "Escrevendo relatorio...",
        done: "Pesquisa concluida!",
      };
      // Only steer the loading message if the user is viewing the generating chat.
      if (chatId && chatId === activeChatIDRef.current) {
        setLoadingMessage(labels[payload.phase] || "Pesquisando...");
      }
    });
    response.addEventListener('deep_research_plan', (e: any) => {
      const chatId = generatingChatIdRef.current;
      if (!chatId) return;
      const payload = parseStreamData(e.data);
      if (payload == null) return;
      updateDeepResearchForChat(chatId, prev => ({
        ...prev,
        plan: payload.is_gap_fill ? [...prev.plan, ...(payload.steps || [])] : (payload.steps || []),
      }));
    });
    response.addEventListener('deep_research_step', (e: any) => {
      const chatId = generatingChatIdRef.current;
      if (!chatId) return;
      const payload = parseStreamData(e.data);
      if (payload == null) return;
      updateDeepResearchForChat(chatId, prev => ({
        ...prev,
        steps: { ...prev.steps, [payload.step_id]: payload },
      }));
    });
    response.addEventListener('deep_research_metadata', (e: any) => {
      try {
        const payload = JSON.parse(e.data);
        const refs = payload.references;
        // Attach the FULL metadata to the in-memory message so the canonical
        // DeepResearchProgress strip (phase="done") renders right after the
        // turn finishes — without this, the progress card disappears as soon
        // as deepResearchPhase flips, and only reappears after a page reload
        // when message.deep_research_metadata is rehydrated from Mongo.
        setMessageHistory(prev => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          const lastMsg = { ...updated[lastIdx] } as any;
          lastMsg.deep_research_metadata = payload;
          if (Array.isArray(refs) && refs.length > 0) {
            lastMsg.references = refs;
          }
          updated[lastIdx] = lastMsg;
          return updated;
        });
      } catch {}
    });

    response.addEventListener('api_sse_error', (e: any) => {
      response.removeEventListener('error', handleError);

      handleApiSseError(e);
    });

    response.addEventListener('ocr_start', handleOCRStart);
    response.addEventListener('start', () => {
      // OCR phase ends when generation starts
      setInOcrPhase(false);
      // With reasoning on, the model thinks first (forced reasoning cycle) before
      // writing the answer — show "Pensando..." until the answer streams.
      setLoadingMessage(reasoningRef.current ? "Pensando..." : "Gerando resposta...");
      setUploadingFiles(false);
    });
    response.addEventListener('signal', (e: any) => {
      try {
        const payload = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (payload?.event === 'thinking') {
          setLoadingMessage("Pensando...");
        }
      } catch {}
    });

    // Code execution events
    response.addEventListener('file', handleCodeExecFile);
    response.addEventListener('code_exec_bash', handleCodeExecBash);
    response.addEventListener('code_exec_python', handleCodeExecPython);
    response.addEventListener('code_exec_write', handleCodeExecWrite);
    response.addEventListener('code_exec_read', handleCodeExecRead);
    response.addEventListener('code_exec_show', handleCodeExecShow);
    response.addEventListener('code_exec_delta', handleCodeExecDelta);
    response.addEventListener('code_exec_result', handleCodeExecResult);

    // Message composer events
    response.addEventListener('message_composer_signal', handleMessageComposerSignal);
    response.addEventListener('message_composer', handleMessageComposer);
    response.addEventListener('panorama_confirmation', handlePanoramaConfirmation);
    response.addEventListener('panorama_debug', handlePanoramaDebug);
    response.addEventListener('panorama_status', handlePanoramaStatus);
    response.addEventListener('reasoning', handleReasoning);
    response.addEventListener('reasoning_delta', handleReasoningDelta);

    // Debug: tool results
    response.addEventListener('tool_result', handleToolResult);
  };

  // Treat error ids during a message request.
  const handleError = (e: any) => {
    if (apiError) {
      return;
    }

    // Inactivity watchdog tripped on the foreground stream: the generation is
    // still running server-side (detached producer), so resume it as a
    // background stream from the durable event log instead of failing the turn.
    if (e?.data?.responseText === 'stall_timeout' && generatingChatIdRef.current) {
      const stalledChatId = generatingChatIdRef.current;
      setGeneratingChatId("");
      setBackgroundGeneratingChatIds(prev => new Set(prev).add(stalledChatId));
      return;
    }

    try {
      const payload = typeof e?.data === 'string' ? JSON.parse(e.data) : e?.data;
      console.error('[Chat SSE error]', payload);
    } catch {
      console.error('[Chat SSE error] raw payload', e?.data);
    }

    const status = e.data?.status;
    let errorMessage = "";
    const nonBlockingWebSearch = false;
    let shouldShowPlanOption = false;
    const historyWithoutAssistant = removePendingAssistantMessage(messageHistory);

    switch (status) {
      case 401:
        errorMessage = "Desculpe, você não parece estar autenticado. Por favor, faça login novamente.";
        break;
      case 402:
        let responseText = "";
        try {
          const parsed = JSON.parse(e.data["responseText"]);
          responseText = parsed?.detail?.detail || "";
          // Atualiza cotas, se presentes
          const quota = parsed?.detail?.quota;
          if (quota && typeof quota === 'object') {
            setFreeQuota({
              messages: Number(quota.messages ?? 0),
            });
          }
        } catch {
          responseText = "";
        }
        errorMessage = responseText;
        if (!nonBlockingWebSearch) {
          setAllowErrorRetry(false);
          shouldShowPlanOption = true;
        }
        break;
      case 403:
        errorMessage = "Desculpe, você não parece ter permissão de fazer essa operação.";
        break;
      case 400:
        responseText = "";
        try {
          responseText = JSON.parse(e.data["responseText"])["detail"];
        } catch {
          responseText = "";
        }

        if (responseText === "Message too long" || responseText === "The message is too long.") {
          errorMessage = "Desculpe, parece que sua mensagem é muito grande. Por favor, reduza a mensagem e tente novamente.";
        } else {
          errorMessage = "Desculpe, algo deu errado. Por favor tente novamente mais tarde.";
        }
        break;
      case 422:
        errorMessage = "Desculpe, ocorreu um erro ao processar seus arquivos, por favor verifique os mesmos.";
        setGeneratingChatId("");
        break;
      case 429:
        errorMessage = "Desculpe, parece que você mandou muitas mensagens em um curto período de tempo, aguarde um pouco e tente novamente.";
        break;
      case 503:
        errorMessage = "Desculpe, nossos servidores estão sob alta demanda, aguarde um pouco e tente novamente.";
        break;
      default: {
        // Prefer a specific message sent by the backend (e.g. context-window
        // overflow) over the generic fallback.
        let backendMsg = "";
        try {
          const p = typeof e?.data === 'string' ? JSON.parse(e.data) : e?.data;
          if (p && typeof p.message === 'string' && p.message.trim()) backendMsg = p.message.trim();
        } catch {}
        errorMessage = backendMsg || "Desculpe, aconteceu algo errado nos nossos servidores. Por favor tente mais tarde.";
        setGeneratingChatId("");
        break;
      }
    }

    if (status !== 402 || nonBlockingWebSearch) {
      shouldShowPlanOption = false;
    }

    // Set error message for chat display (não bloquear quando for 402 de web_search)
    if (!nonBlockingWebSearch) {
      setChatErrorMessage(errorMessage);
    }

    setShowPlanUpgradeOption(shouldShowPlanOption);

    // Only report failures carrying a real HTTP error status. Transport-level
    // drops (status 0 / no response: lost connection, navigation, mobile
    // network blip on a large upload) are benign client-side events — reporting
    // them flooded this issue (CHAT-MARITACA-25). See utils/sseError.
    if (shouldReportSseError(status)) {
      reportHandledError(new Error('chat_sse_error'), {
        category: 'sse',
        feature: 'chat.send',
        level: (status as number) >= 500 ? 'error' : 'warning',
        tags: { status: String(status) },
      });
    }

    // Se for erro de web_search, remover apenas o placeholder do assistente (última mensagem)
    if (nonBlockingWebSearch) {
      if (messageHistory.length >= 1) {
        const newHistory = messageHistory.slice(0, messageHistory.length - 1);
        setMessageHistory(newHistory);
      }
    }

    //reset variables
    setGeneratingChatId("");
    setMessageBeingGenerated("");
    setLoadingMessage("");
    setCurrentMessage("");
    setSelectedFiles(null);
    setNumPages(null);
    setUploadingFiles(false);
  };

  const removePendingAssistantMessage = (history: ChatMessage[]) => {
    if (!history.length) {
      return history;
    }
    const lastMessage = history[history.length - 1];
    if (lastMessage?.role === 'assistant' && (!lastMessage.content || (typeof lastMessage.content === 'string' && lastMessage.content.trim().length === 0))) {
      return history.slice(0, history.length - 1);
    }
    return history;
  };

  const handleApiSseError = (e: any) => {
    try {
      const payloadPreview = typeof e?.data === 'string' ? JSON.parse(e.data) : e?.data;
      console.error('[Chat API SSE error]', payloadPreview);
    } catch {
      console.error('[Chat API SSE error] raw payload', e?.data);
    }

    setGeneratingChatId("");
    setApiError(true);
    setMessageBeingGenerated("");
    setLoadingMessage("");
    // Ensure OCR flags are reset on API SSE errors
    setInOcrPhase(false);
    setCurrentUploadHasPdf(false);
    setUploadingFiles(false);
    // Parse payload antes para decidir como ajustar o histórico
    const payload = (typeof e.data === 'string' ? parseStreamData(e.data) : e.data) || {};

    const status = Number(payload.status);   // garante que seja número
    const detail = payload.detail ?? '';
    let errorMessage = "";
    const nonBlockingWebSearch = false;
    let parsedDetail: any = null;
    let shouldShowPlanOption = false;
    const historyWithoutAssistant = removePendingAssistantMessage(messageHistory);
    try {
      parsedDetail = typeof detail === 'string' ? JSON.parse(detail) : detail;
    } catch {
      parsedDetail = detail;
    }

    // Ajusta o histórico com base no tipo de erro 402/web_search
    if (historyWithoutAssistant !== messageHistory) {
      setMessageHistory(historyWithoutAssistant);
    }

    switch (status) {
      case 401:
        errorMessage = detail || "Erro de autenticação.";
        break;
      case 402: {
        // Quando a API envia 402 via SSE error compatível, o payload.detail pode conter o objeto completo
        let msg = "";
        try {
          const parsed = parsedDetail;
          msg = parsed?.detail ?? parsed?.detail?.detail ?? '';
          const quota = parsed?.quota ?? parsed?.detail?.quota;
          if (quota && typeof quota === 'object') {
            setFreeQuota({
              messages: Number(quota.messages ?? 0),
            });
          }
        } catch {
          msg = typeof detail === 'string' ? detail : '';
        }
        errorMessage = msg || "Pagamento necessário.";
        if (!nonBlockingWebSearch) {
          setAllowErrorRetry(false);
          shouldShowPlanOption = true;
        }
        break;
      }
      case 403:
        errorMessage = "Desculpe, você não parece ter permissão de fazer essa operação.";
        break;
      case 404:
        errorMessage = detail || "Recurso não encontrado.";
        break;
      case 400:
        errorMessage = detail || "Requisição inválida.";
        break;
      case 422:
        errorMessage = detail || "Erro ao processar dados.";
        break;
      case 429:
        errorMessage = "Desculpe, parece que você mandou muitas mensagens em um curto período de tempo, aguarde um pouco e tente novamente.";
        break;
      case 503:
        errorMessage = "Desculpe, nossos servidores estão sob alta demanda, aguarde um pouco e tente novamente.";
        break;
      default:
        errorMessage = "Desculpe, aconteceu algo errado nos nossos servidores. Por favor tente mais tarde.";
    }

    // Set error message for chat display (não bloquear quando for 402 de web_search)
    if (!nonBlockingWebSearch) {
      setChatErrorMessage(errorMessage);
    }
    setApiError(false);

    if (status !== 402 || nonBlockingWebSearch) {
      shouldShowPlanOption = false;
    }
    setShowPlanUpgradeOption(shouldShowPlanOption);

    // See handleError above / utils/sseError: skip benign transport-level drops
    // (status 0 / no HTTP response) so they don't flood Sentry (CHAT-MARITACA-25).
    if (shouldReportSseError(status)) {
      reportHandledError(new Error('chat_sse_error'), {
        category: 'sse',
        feature: 'chat.send',
        level: (status as number) >= 500 ? 'error' : 'warning',
        tags: { status: String(status) },
      });
    }
  };

  const renameChatBasedOnMessage = async (chatId: string, message: string) => {
    const url = `${API_BASE}/api/chat/rename_based_on_message`; 
    const data = {
      chat_id: chatId,
      model: modelChat,
      content: message,
    };
    try {
      const acessToken = await getAccessTokenSilently();
      let response: Response | null = null;

      for (let attempt = 0; attempt < 3; attempt++) {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Auth ${acessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          break;
        }
      }

      if (!response || !response.ok) {
        return;
      }
  
      const updatedChat = await response.json();
      animateTitle(updatedChat);  
      let find = false;
  
      const updatedChats = userChats.map((chat: any) => {
        if (chat.chat_id === chatId) {
          find = true;
          return { chat_id: chatId, title: updatedChat, updated_at: new Date().toISOString() };
        }
        return chat;
      });
      if (find === false) {
        updatedChats.splice(0, 0, { chat_id: chatId, title: updatedChat, updated_at: new Date().toISOString() });
      }  
      
      setUserChats(updatedChats);
      setIsFirstMessage(false);
    } catch (error) {
      console.warn('Falha ao renomear a conversa automaticamente.', error);
    }
  };
  
  // Panorama Juridico: decisao do usuario no card (evento de janela do botao).
  const handlePanoramaDecision = async (decision: 'run' | 'decline') => {
    const chatId = activeChatIDRef.current;
    if (!chatId || !decision) return;
    // Cancela qualquer geracao em andamento (ex.: o turno da PROPOSTA ainda
    // gerando quando o usuario clica em Rodar/Agora-nao) ANTES de seguir — senao
    // as duas rodam em paralelo e misturam o conteudo no chat.
    try {
      const _ct = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/background-cancel/${chatId}`, {
        method: 'POST', headers: { authorization: `Auth ${_ct}` },
      });
    } catch {}
    if (sseConnectionRef.current) {
      try { sseConnectionRef.current.close(); } catch {}
      sseConnectionRef.current = null;
    }
    setBackgroundGeneratingChatIds(prev => {
      if (!prev.has(chatId)) return prev;
      const next = new Set(prev); next.delete(chatId); return next;
    });
    setMessageBeingGenerated("");
    setLoadingMessage("");
    let peticao = '';
    const _isPanoramaBubble = (t: string) => t.startsWith('\u25B6 Rodar Panorama') || t.startsWith('Agora nao \u2014 responder');
    for (let i = messageHistory.length - 1; i >= 0; i--) {
      const m: any = messageHistory[i];
      if (m.role === 'user' || m.is_user) {
        const c = m.content;
        const text = typeof c === 'string' ? c
          : (Array.isArray(c) ? c.filter((x: any) => x.type === 'text').map((x: any) => x.text).join('\n') : '');
        // pula as bolhas de acao do panorama (senao a peticao vira a bolha de 25 chars)
        if (!text || _isPanoramaBubble(text)) continue;
        peticao = text;
        break;
      }
    }
    const bubble = decision === 'run' ? '\u25B6 Rodar Panorama Juridico' : 'Agora nao \u2014 responder direto, sem o Panorama';
    const opts = decision === 'run'
      ? { panorama: true, panoramaPeticao: peticao }
      : { panorama_decline: true };
    setGeneratingChatId(chatId);
    try {
      const token = await getAccessTokenSilently();
      const response = await sendMessage(chatId, bubble, token, opts);
      if (!response) { setGeneratingChatId(''); return; }
      setupResponseHandlers(response);
      updateChatStatus(chatId);
      response.stream();
      storeSseConnection(response);
    } catch (err) {
      setGeneratingChatId('');
    }
  };

  useEffect(() => {
    const h = (e: any) => { handlePanoramaDecision(e?.detail?.decision); };
    window.addEventListener('panoramaDecision', h);
    return () => window.removeEventListener('panoramaDecision', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHistory]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    
    setIsEditing(false);
    setEditingMessageId(null);

    if (isRecording || sendingAudio) {
      return;
    }

    // Prevent multiple submissions on the same chat
    if (generatingChatId !== "" && generatingChatId === activeChatID.chatID) {
      return;
    }
    // Cancel any background generation for this chat before sending new message
    if (backgroundGeneratingChatIds.has(activeChatID.chatID)) {
      const chatId = activeChatID.chatID;
      try {
        const token = await getAccessTokenSilently();
        await fetch(`${API_BASE}/api/chat/background-cancel/${chatId}`, {
          method: 'POST',
          headers: { authorization: `Auth ${token}` },
        });
      } catch {}
      setBackgroundGeneratingChatIds(prev => {
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
    }
    setMessageBeingGenerated("");
    // Clear any existing error message
    setChatErrorMessage("");
    setShowPlanUpgradeOption(false);

    let processedMessage = currentMessage.trim();

    const hasFiles = selectedFiles && selectedFiles.length > 0;

    if (processedMessage.length === 0 && !hasFiles) {
      return;
    }

    let newConversation = false;
    let chatId = activeChatID["chatID"];

    // Note: personality_id is now sent directly to the backend via sendMessage
    // The backend applies the system prompt based on the personality_id

    if (!chatId) {
      chatId = await createNewChat();
      newConversation = true;
      if (!chatId) {
        return;
      }
    } else if (messageHistory.length === 0) {
      newConversation = true;
    }

    // Mark this chat as generating (after chatId is known)
    setGeneratingChatId(chatId);

    const totalPages = countAttachmentPages();
    if (totalPages > MAX_ATTACHMENT_PAGES) {
      toast.error(`Desculpe, você só pode enviar até ${MAX_ATTACHMENT_PAGES} páginas/imagens por vez. Você tentou enviar ${totalPages}.`, TOAST_CONFIG);
      setGeneratingChatId("");
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await sendMessage(chatId, processedMessage, accessToken);
      if (!response) {
        setGeneratingChatId("");
        return;
      }

      setupResponseHandlers(response);
      updateChatStatus(chatId);
      response.stream();
      storeSseConnection(response);
      setIsFirstMessage(false);
      if (newConversation) await renameChatBasedOnMessage(chatId, processedMessage);
    } catch (error) {
      setGeneratingChatId("");
      setMessageBeingGenerated("");
      setCurrentMessage("");
      setSelectedFiles(null);
      setNumPages(null);
      setUploadingFiles(false);
    }
  };


  const createNewChat = async () => {
    const url = API_BASE + "/api/chat/create";
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(url, {
          method: 'POST',
          headers: {
              "authorization": `Auth ${accessToken}`,
              'Content-Type': 'application/json',
          },
      });
  
      if (response.ok) {
          const data = await response.json();
          if (data["chat_data"] != null) {
              const updatedChats = [{ ...data["chat_data"], updated_at: new Date().toISOString() }, ...userChats];
              setUserChats(updatedChats);
              setActiveChatID({
                  "chatID": data["chat_data"]["chat_id"],
                  "skipUpdate": true,
              });
              setChatStatus((prevStatus) => ({
                  ...prevStatus,
                  [data["chat_data"]["chat_id"]]: false,
              }));
              return data["chat_data"]["chat_id"];
          }
      } else {
          toast.error("Desculpe, algo deu errado, tente novamente", TOAST_CONFIG);
      }
    } catch (error) {
      toast.error("Desculpe, algo deu errado, tente novamente", TOAST_CONFIG);
    }
    return null;
  };

  // Transform markdown-linked citations into [1][2] numbered footnotes with a
  // trailing "Referências" section, so pasted text reads cleanly outside the chat.
  const formatTextForCopy = (text: string, references?: Reference[]): string => {
    if (!text) return text;

    const urlToNumber = new Map<string, number>();
    const orderedRefs: { num: number; title: string; url: string }[] = [];

    const getOrAssignNumber = (url: string, title: string): number => {
      const existing = urlToNumber.get(url);
      if (existing) return existing;
      const num = urlToNumber.size + 1;
      urlToNumber.set(url, num);
      orderedRefs.push({ num, title, url });
      return num;
    };

    // Replace [text](url) with [N]
    let transformed = text.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_match, linkText, url) => {
      const num = getOrAssignNumber(url, linkText || url);
      return `[${num}]`;
    });

    // Replace legacy [REFERENCIA_N] with [N] (map to reference by index, then number sequentially by first use)
    transformed = transformed.replace(/\[REFERENCIA_(\d+)(?:\s*,\s*REFERENCIA_\d+)*\]/g, (match) => {
      const nums = [...match.matchAll(/REFERENCIA_(\d+)/g)].map(m => parseInt(m[1], 10));
      const newNums = nums.map(refIdx => {
        const ref = references?.find(r => r.index === refIdx);
        if (!ref?.link) return `[${refIdx}]`;
        const n = getOrAssignNumber(ref.link, ref.title || ref.link);
        return `[${n}]`;
      });
      return newNums.join("");
    });

    // Replace remaining raw URLs (not already captured inside [text](url)) with [N]
    transformed = transformed.replace(/(https?:\/\/[^\s<>)\]]+)/g, (match, url) => {
      // Skip if already numbered (this URL was part of a markdown link replaced above)
      if (urlToNumber.has(url)) {
        const matchedRef = references?.find(r => r.link === url);
        const title = matchedRef?.title || url;
        const num = getOrAssignNumber(url, title);
        return `[${num}]`;
      }
      const matchedRef = references?.find(r => r.link === url);
      const title = matchedRef?.title || url;
      const num = getOrAssignNumber(url, title);
      return `[${num}]`;
    });

    if (orderedRefs.length === 0) return transformed;

    const refsSection = orderedRefs
      .map(r => `[${r.num}] ${r.title} - ${r.url}`)
      .join("\n");

    return `${transformed}\n\n## Referências\n\n${refsSection}`;
  };

  // Removed fixed examples; landing shows a central search bar
  const handleCopyToClipboard = (text: string, index: number, references?: Reference[]) => {
    const formatted = formatTextForCopy(text, references);
    navigator.clipboard.writeText(formatted).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000); // reset the copied index after 2 seconds
    }).catch(() => {
      // Silently fail if clipboard is unavailable
    });
  };

  const handleSaveEditedMessage = async (event: any) => {
    event.preventDefault();
    setIsEditing(false);
    setEditingMessageId(null);
    // Clear references of the last assistant message
    if (messageHistory.length > 0) {
      const lastAssistantMessage = messageHistory[messageHistory.length - 1] as ChatMessage;
      if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
        lastAssistantMessage.references = [];
      }
    }

    if (editingMessageId === null) return;
  
    const requestBody: Record<string, any> = {
      chat_id: activeChatID.chatID,
      message_id: editingMessageId,
      new_content: messageBeingEdited,
      is_user: false,
      function_used: "edit",
      web_search: webSearch,
      code_execution: codeExecution,
      data_ocean: dataOcean,
      reasoning: reasoningRef.current,  // fresh value (state closure can be stale at send time)
      ...(usePreciseLocationRef.current && preciseLocationRef.current ? { user_location: preciseLocationRef.current } : {}),
    };

    // Add personality_id if an active model is selected
    if (activeModelId) {
      requestBody.personality_id = activeModelId;
    }
  
    const access_token = await getAccessTokenSilently();
    const url = `${API_BASE}/api/chat/edit_and_regenerate_message`;
  
    const response: any = new SSE(url, {
      headers: {
        "authorization": `Auth ${access_token}`,
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
      },
      payload: JSON.stringify(requestBody),
      method: 'POST'
    });
  
    response.addEventListener('start', () => {
      const lastMessage: any = messageHistory[editingMessageId];
      if (lastMessage) {
        lastMessage.content = requestBody.new_content;
      }
      const assistantMessage: any = messageHistory[editingMessageId + 1];
      if (assistantMessage) {
        assistantMessage.content = [];
        assistantMessage.references = [];
      }
      setMessageHistory([...messageHistory]);
      setCurrentMessage("");
      setGeneratingChatId(activeChatID.chatID);
      setLoadingMessage(reasoningRef.current ? "Pensando..." : "Gerando resposta...");
      setMessageBeingGenerated("");
    });

    response.addEventListener('web_search', handleWebTrigger);
    response.addEventListener('data_ocean_query', handleDataOceanQuery);
    response.addEventListener('data_ocean_result', handleDataOceanResult);
    response.addEventListener('buscar_conversas', handleBuscarConversas);
    response.addEventListener('tool_result', handleToolResult);
    response.addEventListener('code_exec_bash', handleCodeExecBash);
    response.addEventListener('code_exec_python', handleCodeExecPython);
    response.addEventListener('code_exec_write', handleCodeExecWrite);
    response.addEventListener('code_exec_read', handleCodeExecRead);
    response.addEventListener('code_exec_show', handleCodeExecShow);
    response.addEventListener('code_exec_delta', handleCodeExecDelta);
    response.addEventListener('code_exec_result', handleCodeExecResult);
    // Align with the normal-send flow so regenerated/edited messages render the
    // same tool blocks live (these were missing → tools invisible on regenerate).
    response.addEventListener('reasoning', handleReasoning);
    response.addEventListener('page_scraping', handlePageScraping);
    response.addEventListener('datajud_query', handleDataJudQuery);
    response.addEventListener('erp_query', handleERPQuery);
    response.addEventListener('erp_create_invoice', handleERPCreateInvoice);

    response.addEventListener('message', (e: any) => {
      handleIncomingEditedMessage(e, editingMessageId);
    });

    response.addEventListener('end', handleEndResponse);
    setIsEditing(false);
    response.addEventListener('error', handleError);
  
    response.stream();
    storeSseConnection(response);
  };
  
  // Handle incoming edited message.
  const handleIncomingEditedMessage = (e: any, editingMessageId: number) => {
    const rawText = e.data;
    if (!rawText.length) return;
    const payload = parseStreamData(rawText);
    if (payload == null) return;
    const incoming = payload["text"];
    if (!incoming) return;
    const assistantIndex = editingMessageId + 1;

    // Same flag as handleMessage uses: pendingNewCycleRef goes true when a
    // tool fires (web_search / page_scraping / code_exec_*), and on the
    // next text event we push a NEW text item instead of appending. This
    // preserves interleaving: text → tool → text → tool → text in order.
    const isNewCycle = pendingNewCycleRef.current;
    if (isNewCycle) {
      pendingNewCycleRef.current = false;
    }

    let runningText: string | null = null;
    setMessageHistory((prevHistory: ChatMessage[]) => {
      const updatedHistory = [...prevHistory];
      let existing: any = updatedHistory[assistantIndex];
      if (!existing || existing.role !== 'assistant') {
        existing = { role: 'assistant', content: [] as ContentItem[], references: [] };
        updatedHistory.splice(assistantIndex, 0, existing);
      }
      // Always work with a ContentItem[] so tool_action items pushed by
      // the parallel tool handlers (pushToolAction / appendPendingCodeExec)
      // survive. Previously this handler reset to a plain string and ate
      // those tool_action entries on every chunk.
      let content: ContentItem[] = Array.isArray(existing.content)
        ? [...existing.content]
        : (typeof existing.content === 'string' && existing.content.length > 0
          ? [{ type: 'text', text: existing.content }]
          : []);

      // Flip preceding tool_actions to done now that text is arriving.
      if (isNewCycle) {
        content = content.map((it: any) => (it?.type === 'tool_action' && it.pending)
          ? { ...it, pending: false }
          : it);
      }

      if (isNewCycle || content.length === 0) {
        content.push({ type: 'text', text: incoming });
      } else {
        let appended = false;
        for (let i = content.length - 1; i >= 0; i--) {
          if (content[i].type === 'text') {
            const prevText = (content[i] as TextContentItem).text || '';
            const next = deepResearch ? incoming : prevText + incoming;
            content[i] = { ...(content[i] as TextContentItem), text: next };
            appended = true;
            break;
          }
          // Stop walking back when we hit a non-text item (tool_action,
          // code_execution) — that means the most recent text cycle is
          // finished and the new text should start a fresh item.
          if (content[i].type !== 'text') break;
        }
        if (!appended) content.push({ type: 'text', text: incoming });
      }

      updatedHistory[assistantIndex] = { ...existing, content };
      const lastText = (content[content.length - 1] as any)?.text;
      if (typeof lastText === 'string') runningText = lastText;
      return updatedHistory;
    });
    if (runningText !== null) {
      setMessageBeingGenerated(runningText);
    }
  };

  const handleCancelEdit  = () => {
    setIsEditing(false);
    setEditingMessageId(null);
  };

  const handleEditMessage = (messageId: number, messageContent: string) => {
    setIsEditing(true);
    setEditingMessageId(messageId);
    setMessageBeingEdited(messageContent);
    setLoadingMessage("");
  };

  const handleRetryMessage = async () => {
    setChatErrorMessage(""); // Clear error message
    setShowPlanUpgradeOption(false);
    
    // Find the last user message to retry
    let lastUserMessageIndex = -1;
    for (let i = messageHistory.length - 1; i >= 0; i--) {
      if ((messageHistory[i] as any).role === "user") {
        lastUserMessageIndex = i;
        break;
      }
    }
    
    if (lastUserMessageIndex !== -1) {
      await handleRegenerateMessage(lastUserMessageIndex);
    }
  };
    
  const handleRegenerateMessage = async (messageId: number) => {
    if (messageId === null) return;
    
    const _regenMsg = messageHistory[messageId] as any;
    const originalMessage = _regenMsg?.content ?? "";
    // A file-only turn has empty text content but still has attachments; allow
    // regenerating it (the stored file context is reused server-side). Only bail
    // when there is truly nothing to regenerate (no text AND no files).
    const _hasFiles = (Array.isArray(_regenMsg?.filenames) && _regenMsg.filenames.length > 0)
      || (Array.isArray(_regenMsg?.files) && _regenMsg.files.length > 0);
    if (!originalMessage && !_hasFiles) return;
  
    const requestBody: Record<string, any> = {
      chat_id: activeChatID.chatID,
      message_id: messageId,
      new_content: originalMessage,
      is_user: false,
      function_used: "regenerate",
      web_search: webSearch,
      code_execution: codeExecution,
      data_ocean: dataOcean,
      reasoning: reasoningRef.current,  // fresh value (state closure can be stale at send time)
      ...(usePreciseLocationRef.current && preciseLocationRef.current ? { user_location: preciseLocationRef.current } : {}),
    };

    // Add personality_id if an active model is selected
    if (activeModelId) {
      requestBody.personality_id = activeModelId;
    }
  
    const access_token = await getAccessTokenSilently();
    const url = `${API_BASE}/api/chat/edit_and_regenerate_message`;
  
    const response: any = new SSE(url, {
      headers: {
        "authorization": `Auth ${access_token}`,
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
      },
      payload: JSON.stringify(requestBody),
      method: 'POST'
    });
  
    // Set event listeners for the entire life cycle of a message call.
    response.addEventListener('start', () => {
      const lastMessage: any = messageHistory[messageId];
      if (lastMessage) {
        lastMessage.content = originalMessage;
      }

      const assistantMessage: any = messageHistory[messageId + 1];
      if (assistantMessage) {
        // Reset to ContentItem[] (not "") so tool_action / code_execution
        // items pushed during this regen survive into the saved message.
        assistantMessage.content = [];
        assistantMessage.references = [];
      }

      // Clear references of the last assistant message
      if (messageHistory.length > 0) {
        const lastAssistantMessage = messageHistory[messageHistory.length - 1] as ChatMessage;
        if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
          lastAssistantMessage.references = [];
        }
      }

      setMessageHistory([...messageHistory]); // Trigger re-render
      setCurrentMessage("");
      setGeneratingChatId(activeChatID.chatID);
      setLoadingMessage(reasoningRef.current ? "Pensando..." : "Gerando resposta...");
      setMessageBeingGenerated("");
    });

    // Reuse the same handler set as the foreground chat send so the
    // regenerate flow doesn't miss file attachments, page_scraping,
    // erp_* / datajud / api_sse_error / message_composer / signal /
    // deep_research_* / ocr_start. Keeping a partial subset here was the
    // source of bugs like "o show foi chamado mas o anexo do PDF não
    // apareceu".
    setupResponseHandlers(response);

    response.stream();
    storeSseConnection(response);
  };

  const handleFeedback = async (messageId: number, feedback: string, reason: string) => {
    if (messageFeedback[messageId] === feedback) {
      return; // don't allow two lines with the same feedback
    }
  
    try {
      let chatId = activeChatID["chatID"];
      const url = `${API_BASE}/api/chat/message/feedback`;
      const access_token = await getAccessTokenSilently();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          "authorization": `Auth ${access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          message_idx: messageId,
          feedback: feedback,
          reason: reason,
        }),
      });
  
      if (response.ok) {
        setMessageFeedback((prev) => ({
          ...prev,
          [messageId]: feedback, 
        }));
      } else {
        throw new Error('Falha ao enviar feedback');
      }
    } catch (error) {
      console.error(error);
    }
  };
  
  
  const handleLikeMessage = (messageId: number) => {
    if (loadingGeneration) {
      return;
    }
    const message = messageHistory[messageId] as any;
    message.feedback = 'like';
    setMessageHistory([...messageHistory]);
    handleFeedback(messageId, 'like', '');
    setMessageFeedback((prev) => ({
      ...prev,
      [messageId]: 'like',
    }));
  };


  const abortCurrentGeneration = () => {
    if (sseConnectionRef.current) {
      sseConnectionRef.current.close();
      sseConnectionRef.current = null;
    }
    // Track that this chat continues generating in the background
    if (generatingChatId) {
      setBackgroundGeneratingChatIds(prev => new Set(prev).add(generatingChatId));
    }
    setGeneratingChatId("");
    // Clear the ref SYNCHRONOUSLY too: an SSE event dispatched just before the
    // close above can still run after we switch chats, and the state-backed ref
    // lags a render — a stale generatingChatId would let a late deep_research
    // event re-set the global loadingMessage onto the chat we just opened.
    generatingChatIdRef.current = "";
    setLoadingMessage("");
    setMessageBeingGenerated("");
    setInOcrPhase(false);
    setCurrentUploadHasPdf(false);
    setUploadingFiles(false);
  };

  const handleStopClick = async () => {
    const chatId = activeChatID.chatID;
    if (!chatId) return;

    setCancellingBackground(true);

    // Call cancel FIRST — this sets bg_gen:done in Redis so the backend
    // won't spawn a new background task when the SSE disconnects
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/background-cancel/${chatId}`, {
        method: 'POST',
        headers: { authorization: `Auth ${token}` },
      });
    } catch {}

    // NOW close the SSE — backend's finally block will see bg_gen:done and skip spawning
    const sseConnection = sseConnectionRef.current;
    if (sseConnection) {
      sseConnection.close();
      sseConnectionRef.current = null;
      setGeneratingChatId("");
      setLoadingMessage("");
      setIsEditing(false);
    }

    // Clean up frontend state
    setBackgroundGeneratingChatIds(prev => {
      if (!prev.has(chatId)) return prev;
      const next = new Set(prev);
      next.delete(chatId);
      return next;
    });

    // Freeze the live activity indicators: flip every pending item to done so
    // the thinking box stops shimmering "Pensando" forever, and mark the
    // message as interrupted so the trailing group renders
    // "Pensou · interrompido" instead of looking like a completed turn.
    setMessageHistory(prev => {
      if (prev.length === 0) return prev;
      const last: any = prev[prev.length - 1];
      if (last.role !== 'assistant' || !Array.isArray(last.content)) return prev;
      const content = last.content.map((it: any) =>
        it && it.pending ? { ...it, pending: false, streaming: false } : it,
      );
      return [...prev.slice(0, -1), { ...last, content, interrupted: true }];
    });
    // Remove o card de progresso AO VIVO do Panorama: ao interromper, ele ficava
    // preso numa fase intermediaria (ex.: "resolvendo os precedentes citados").
    // O fim normal do stream ja limpa em endedChatId; o interrupt precisa do mesmo.
    clearPanoramaForChat(chatId);
    setCancellingBackground(false);
  };

  const handleNewChat = (overrideModel?: string) => {
    // The file upload aborts if we tear down the SSE before the backend has
    // received the POST body, losing the message entirely. Block navigation
    // ONLY during the pure upload phase; once OCR starts (inOcrPhase) the
    // backend already has the request and persisted the user turn, so
    // switching is safe and the run resumes from the durable background log.
    if (uploadingFiles && !inOcrPhase) {
      toast.info("Aguarde o upload do arquivo terminar.", TOAST_CONFIG);
      return;
    }
    setShowProjectsView(false);
    const wasGenerating = !!generatingChatId;
    if (generatingChatId) {
      abortCurrentGeneration();
    }
    setIsFirstMessage(true);
    //stop any active STT recording
    if (isRecording) stopStreamingSTT();
    setActiveChatID({
        "chatID": ""
    });
    activeChatIDRef.current = "";
    setLoadingMessage("");
    setMessageHistory([]);
    setMessageBeingGenerated("");
    setIsEditing(false);
    setAnimatedTitle("");
    // clean pdf files
    setSelectedFiles(null);
    setNumPages(null);
    // clear error message
    setChatErrorMessage("");
    setShowPlanUpgradeOption(false);
    // Reset model to default (or override if switching model)
    setModelChat(overrideModel || DEFAULT_CHAT_MODEL);

    // Remove the chat from the menu if it has less than two messages with non-empty content
    // But don't remove if generation was in progress (message already saved in backend)
    const nonEmptyMessages = messageHistory.filter(
      (msg: any) => {
        if (!msg.content) return false;
        if (typeof msg.content === 'string') return msg.content.trim().length > 0;
        if (Array.isArray(msg.content)) return msg.content.length > 0;
        return false;
      }
    );
    if (nonEmptyMessages.length < 2 && !wasGenerating) {
      // delete the chat from menu
      const updatedChats = userChats.filter((chat: any) => chat.chat_id !== activeChatID["chatID"]);
      setUserChats(updatedChats);
      setChatStatus((prevStatus) => {
        const newStatus = { ...prevStatus };
        delete newStatus[activeChatID["chatID"]];
        return newStatus;
      });
    }
  };

  const handleCloseFeedback = (messageId: number) => {
    const updatedHistory = [...messageHistory];
    (updatedHistory[messageId] as any).feedback = 'dislike';
    setMessageHistory(updatedHistory);
    setShowFeedback(false);
    handleFeedback(messageId, 'dislike', '');

  };

  const handleChatItemClick = (chat_id:string) => {
    // The file upload aborts if we tear down the SSE before the backend has
    // received the POST body, losing the message entirely. Block navigation
    // ONLY during the pure upload phase; once OCR starts (inOcrPhase) the
    // backend already has the request and persisted the user turn, so
    // switching is safe and the run resumes from the durable background log.
    if (uploadingFiles && !inOcrPhase) {
      toast.info("Aguarde o upload do arquivo terminar.", TOAST_CONFIG);
      return;
    }
    setShowProjectsView(false);
    if (generatingChatId) {
      abortCurrentGeneration();
    }
    // Switching chats: clear the transient loading message and sync the
    // active-chat ref SYNCHRONOUSLY, so a late in-flight bg-stream/SSE event
    // (guarded by activeChatIDRef) can't paint "Pesquisando..." onto this chat.
    // abortCurrentGeneration only runs for a FOREGROUND generation; a chat
    // resuming in the background wouldn't otherwise clear it.
    setLoadingMessage("");
    activeChatIDRef.current = chat_id;
    const chat = userChats.find((chat:any) => chat["chat_id"] === chat_id)
      || (searchResults && searchResults.find((chat:any) => chat["chat_id"] === chat_id));
    if (chat) {
      setAnimatedTitle((chat as { title: string }).title);
    }
    setMessageBeingGenerated("");
    setIsFirstMessage(false);
    setActiveChatID(
      {
        "chatID": chat_id
      }
    )
    instantScrollToEnd();

    setIsEditing(false); // reset the editing state
    setChatErrorMessage(""); // clear error message
    // Clear "completed" badge when user visits the chat
    setCompletedBackgroundChatIds(prev => {
      if (!prev.has(chat_id)) return prev;
      const next = new Set(prev);
      next.delete(chat_id);
      return next;
    });
    setShowPlanUpgradeOption(false);
  };

  const handlePlatformButton = ()=>{
    // redirect user to platform website
    window.open(PLATFORM_WEBSITE, "_blank");
  }

  const handleClearConversationButton= async ()=>{
    setIsFirstMessage(true);
    // redirect user to COMPANY_WEBSITE
    if (loadingGeneration){
      toast("Por favor, espere a mensagem atual ser gerada antes de limpar a conversa", TOAST_CONFIG)
    }

    if (window.confirm("Você tem certeza que quer apagar todas as conversas?")){
    
      const url = API_BASE + "/api/chat/clear";
      try {
        const access_token=await getAccessTokenSilently()
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            "authorization": `Auth ${access_token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok){
          setUserChats([]);
          setActiveChatID({
            "chatID": ""
          });
          setMessageBeingGenerated("");
          setChatStatus({});
        } else {
          toast.error("Não foi possível limpar as conversas agora.", TOAST_CONFIG);
        }
      } catch (error) {
        toast.error("Não foi possível limpar as conversas agora.", TOAST_CONFIG);
      }
    }
  }

  const handleRenameConversation= async (chatId: string, message: string)=>{
    
    if (!message.trim()) {
      return; // Do nothing if message is empty
    }
    if (message.length > 100) {
      toast.warn("O título da conversa não pode exceder 100 caracteres.", TOAST_CONFIG);
      return;
    }

    const url = `${API_BASE}/api/chat/rename`; 
    const data = {
      chat_id: chatId,
      name: message,
    };
    try {
      const acessToken = await getAccessTokenSilently();
    
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Auth ${acessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
  
      if (!response.ok) {
        toast.error("Não foi possível renomear a conversa.", TOAST_CONFIG);
        return;
      }
  
      let find = false;
  
      const updatedChats = userChats.map((chat: any) => {
        if (chat.chat_id === chatId) {
          find = true;
          return { chat_id: chatId, title: message, updated_at: new Date().toISOString() };
        }
        return chat;
      });
      if (find === false) {
        updatedChats.splice(0, 0, { chat_id: chatId, title: message, updated_at: new Date().toISOString() });
      }  
      
      setUserChats(updatedChats);
    } catch (error) {
      toast.error("Não foi possível renomear a conversa.", TOAST_CONFIG);
    }
  }

  const handleDeleteConversation= async (chat_id:string)=>{
    // redirect user to COMPANY_WEBSITE
    if (loadingGeneration){
      toast.warn("Por favor, espere a mensagem atual ser gerada antes de deletar uma conversa", TOAST_CONFIG)
    }
    
    const url = API_BASE + `/api/chat/delete/${chat_id}`;
    try {
      const access_token=await getAccessTokenSilently()
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          "authorization": `Auth ${access_token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok){
        // Don't replace userChats with the server response: /api/chat/delete returns
        // the *full* user history (unpaginated), while /api/chat/list initially loads
        // only the first 50. Using the server payload makes older chats from below
        // the scroll fold suddenly appear when you delete one. Filter locally instead.
        await response.json();  // drain the body
        setUserChats((prev: any[]) => prev.filter((c: any) => c?.chat_id !== chat_id));
        if (chat_id === activeChatID["chatID"]){
          setActiveChatID({
            "chatID": ""
          });
        }
  
        setChatStatus((prevStatus) => {
          delete prevStatus[chat_id];
          return prevStatus;
        });
        setMessageBeingGenerated("");
      } else {
        toast.error("Não foi possível deletar a conversa.", TOAST_CONFIG);
      }
    } catch (error) {
      toast.error("Não foi possível deletar a conversa.", TOAST_CONFIG);
    }
  }
  

  const handleLogoutButton= ()=>{
    // redirect user to COMPANY_WEBSITE
    logout({ logoutParams: { returnTo: window.location.origin +"/auth" }})
  }

  const handleSidebarToggle= (state:boolean | undefined =undefined)=>{
    if (state == undefined){
      setOpenSidebar(!openSidebar);
    }else{
      setOpenSidebar(state);
    }
  }

  const handleButtonDownTextInput = (e: React.KeyboardEvent) => {
    if (isMobile) return;
      const hasSomethingToSend =
        currentMessage.trim().length > 0 ||
        (selectedFiles && selectedFiles.length > 0);

    if (!loadingGeneration && e.key === 'Enter' && !e.shiftKey && hasSomethingToSend) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSelectOption = (option: string) => {

    // Don't change the inference model if one generation is already in progress.
    if (loadingGeneration)  {
      toast.error("Por favor, espere a mensagem atual ser gerada antes de trocar o modelo", TOAST_CONFIG)
      return;
    }
    else {
      if(modelChat == option)
        return;
      else{
        setOpenDropdownMenu(false);

        // If the chat window is populated, start a new chat window when selecting a new model.
        if (messageHistory.length !== 0) {
          setIsFirstMessage(false);
          handleNewChat(option);  // Pass the selected model so it's not overwritten by DEFAULT
          setShowFeedback(false);
        } else {
          setModelChat(option);
        }
      }
    }
  }

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileInputKey((prev) => prev + 1);
  };

  const handleFileUpload = (event: any) => {
    // Validate every selected file (not just the first) so one unsupported
    // file doesn't silently block the whole batch — and we can name it.
    const allowedMimeTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "text/plain",
      "text/markdown",
      "text/csv",
      "application/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/html",
      "application/xhtml+xml",
      "text/xml",
      "application/xml",
      "application/rtf",
      "text/rtf",
      "application/zip",
      "application/x-zip-compressed",
      "application/zip-compressed",
      "multipart/x-zip",
      "application/json",
      "application/x-ndjson",
      "application/jsonl",
    ];
    const allowedExtensions = [".pdf", ".png", ".jpeg", ".jpg", ".csv", ".txt", ".md", ".docx", ".xlsx", ".html", ".htm", ".xml", ".rtf", ".zip", ".json", ".jsonl"];
    const isAllowed = (file: File) => {
      const lowerName = file.name.toLowerCase();
      // Files with no extension (graph JSON exports etc.) come through with an
      // empty file.type — the browser can't infer a MIME without an extension.
      // Let those pass and let the backend content-validate (python-magic);
      // only files the browser positively typed as something unsupported get
      // the instant client-side toast.
      return !file.type || allowedMimeTypes.includes(file.type) || allowedExtensions.some((ext) => lowerName.endsWith(ext));
    };
    const incoming: File[] = Array.from(event.target.files || []);
    const rejected = incoming.filter((file) => !isAllowed(file));
    if (rejected.length > 0) {
      const names = rejected.map((f) => f.name).join(", ");
      toast.error(
        `Tipo não suportado: ${names}. Aceitos: PDF, imagens, .txt, .md, .csv, .docx, .xlsx, .html, .xml, .rtf, .json, .jsonl, .zip.`,
        TOAST_CONFIG,
      );
      resetFileInput();
      return;
    }

    // Size limits — reject early with a clear message instead of failing late at
    // the backend (which only caps the 1 GB aggregate).
    const oversize = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversize.length > 0) {
      const names = oversize.map((f) => `${f.name} (${(f.size / 1024 / 1024).toFixed(0)} MB)`).join(", ");
      toast.error(
        `Arquivo muito grande: ${names}. Máximo ${MAX_FILE_BYTES / 1024 / 1024} MB por arquivo.`,
        TOAST_CONFIG,
      );
      resetFileInput();
      return;
    }
    const existingBytes = (selectedFiles || []).reduce((sum, f) => sum + (f.size || 0), 0);
    const incomingBytes = incoming.reduce((sum, f) => sum + (f.size || 0), 0);
    if (existingBytes + incomingBytes > MAX_TOTAL_FILES_BYTES) {
      toast.error(
        `Tamanho total dos anexos excede ${MAX_TOTAL_FILES_BYTES / 1024 / 1024} MB. Remova alguns arquivos.`,
        TOAST_CONFIG,
      );
      resetFileInput();
      return;
    }

    // Convert the existing selected files to a map with a unique key (name + lastModified)
    const fileMap = new Map<string, File>(
      (selectedFiles || []).map((file) => [`${file.name}-${file.lastModified}`, file])
    );

    // Helper to generate previews
    const generatePreview = async (file: File) => {
      try {
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          setFilePreviews((prev) => ({ ...prev, [file.name]: url }));
        } else if (isTextLikeFile(file)) {
          const lowerName = file.name.toLowerCase();
          if (lowerName.endsWith('.docx') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.rtf')) {
            // Binary/control-code formats — extracted text comes from the backend.
            return;
          }
          const rawText = await file.text();
          const content = rawText.slice(0, TEXT_PREVIEW_CHAR_LIMIT);
          const truncated = rawText.length > TEXT_PREVIEW_CHAR_LIMIT;
          setFileTextPreviews((prev) => ({
            ...prev,
            [file.name]: { content, truncated },
          }));
        } else if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          setFileOriginalUrls((prev) => {
            const next = { ...prev };
            const existing = next[file.name];
            if (existing && existing.startsWith('blob:')) {
              try {
                URL.revokeObjectURL(existing);
              } catch {}
            }
            next[file.name] = URL.createObjectURL(file);
            return next;
          });
          // Generate a thumbnail of the first page using pdfjs
          const buffer = await file.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: buffer }).promise;
          setNumPages((prevNumPages) => ({
            ...(prevNumPages ?? {}),
            [file.name]: pdf.numPages,
          }));
          const page = await pdf.getPage(1);
          const initialViewport = page.getViewport({ scale: 1 });
          const targetWidth = 220; // px (higher res for clearer thumbnails)
          const scale = targetWidth / initialViewport.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          if (context) {
            await page.render({ canvasContext: context, viewport }).promise;
            const dataUrl = canvas.toDataURL('image/png');
            setFilePreviews((prev) => ({ ...prev, [file.name]: dataUrl }));
          }
        }
      } catch (e) {
        // Fallback: no preview generated
      }
    };

    // Iterate over the newly selected files
    let limitReached = false;
    Array.from(event.target.files as File[]).forEach((file) => {
      const uniqueKey = `${file.name}-${file.lastModified}`;
      if (!fileMap.has(uniqueKey)) {
        if (fileMap.size >= MAX_ATTACHMENT_ITEMS) {
          limitReached = true;
          return;
        }
        fileMap.set(uniqueKey, file);
        if (isTextLikeFile(file) || isPdfFile(file.name) || file.type.startsWith("image/")) {
          setNumPages((prevNumPages) => ({
            ...(prevNumPages ?? {}),
            [file.name]: 1,
          }));
        }
        // Generate previews for images and PDFs
        generatePreview(file);
      }
    });

    if (limitReached) {
      toast.warn(`É possível anexar no máximo ${MAX_ATTACHMENT_ITEMS} arquivos por vez.`, TOAST_CONFIG);
    }

    setSelectedFiles(Array.from(fileMap.values()));

    resetFileInput();
  };

  const handleFileDelete = (file: File) => {
    setSelectedFiles((selectedFiles || []).filter((selectedFile) => selectedFile !== file));
    // Remove the number of pages for the deleted file
    setNumPages((prevNumPages) => {
      if (!prevNumPages) {
        return prevNumPages;
      }
      const newNumPages = { ...prevNumPages };
      delete newNumPages[file.name];
      return newNumPages;
    });
    // Cleanup preview
    setFilePreviews((prev) => {
      const next = { ...prev };
      const url = next[file.name];
      if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      delete next[file.name];
      return next;
    });
    setFileTextPreviews((prev) => {
      if (!prev[file.name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[file.name];
      return next;
    });
    setFileOriginalUrls((prev) => {
      const next = { ...prev };
      const url = next[file.name];
      if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      delete next[file.name];
      return next;
    });
  }

  // Controls logic to show "hovered" effect when chat is hovered.
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  // Controls logic to show "hovered" effect when moreIcon of a chat is clicked.
  const [chatMenuClickedChatId, setChatMenuClickedChatId] = useState<string | null>(null);

  // Anchor for popup menu.
  const [anchorElChatMenu, setAnchorElChatMenu] = useState<null | HTMLElement>(null);

  // Save current state of chat to be edited.
  const [currentChatId, setCurrentChatId] = useState<string>("");
  const [currentChatTitle, setCurrentChatTitle] = useState<string>("");

  // Save state of editing chat.
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editedChatTitle, setEditedChatTitle] = useState<string>("");

  // Open and close AccountModal
  const [openAccountModal, setOpenAccountModal] = useState<boolean>(false);

  // Reference to the chat rename styledTextField
  const styledTextFieldRef = useRef<HTMLInputElement>(null);

  // State for SubscriptionModal
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState<boolean>(false);

  // State for ERP Connection Modal
  const [openERPModal, setOpenERPModal] = useState<boolean>(false);

  const openChatMenu = Boolean(anchorElChatMenu);

  const handleOpenChatMenu = (
    event: React.MouseEvent<HTMLElement>,
    chatId: string,
    chatTitle: string,
  ) => {
    event.stopPropagation();
    setAnchorElChatMenu(event.currentTarget as HTMLElement);
    setChatMenuClickedChatId(chatId);
    setCurrentChatId(chatId);
    setCurrentChatTitle(chatTitle);
  };

  const handleCloseChatMenu = () => {
    setAnchorElChatMenu(null);
    setChatMenuClickedChatId(null);
    setHoveredChatId(null);
  };

  const focusRenameField = () => {
    setTimeout(() => {
      styledTextFieldRef.current?.focus();
      styledTextFieldRef.current?.select();
    }, 0);
  };

  const handleRenameClick = () => {
    if (!currentChatId) return;
    setEditingChatId(currentChatId);
    setEditedChatTitle(currentChatTitle);
    focusRenameField();
  };

  const handleDeleteClick = () => {
    if (!currentChatId) return;
    handleDeleteConversation(currentChatId);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    action: 'rename' | 'delete',
  ) => {
    event.stopPropagation();
    handleCloseChatMenu();

    if (action === 'rename') {
      handleRenameClick();
    } else {
      handleDeleteClick();
    }
  };

  const handleCancelEditCancelMenu = () => {
    setEditingChatId(null); // Exit rename chat without saving
  };

  const handleDislikeClick = (event: React.MouseEvent<HTMLButtonElement>, messageId: number) => {
    if (loadingGeneration) return;
    setSelectedMessageId(messageId);
    setShowFeedback(true);
  };

  const handleSendFeedback = (messageId: number, reason: string) => {
    const updatedHistory = [...messageHistory];
    (updatedHistory[messageId] as any).feedback = 'dislike';
    setMessageHistory(updatedHistory);
    setShowFeedback(false);
    handleFeedback(messageId, 'dislike', reason);
  };

  useEffect(() => {
    if (!isEditing || editingMessageId === null) return;

    const last = messageHistory.length - 1;
    const penult = messageHistory.length - 2;
    const isStillEditable = editingMessageId === last || editingMessageId === penult;

    if (!isStillEditable) {
      setIsEditing(false);
      setEditingMessageId(null);
    }
  }, [messageHistory, isEditing, editingMessageId]);


  useEffect(() => {
    const chatId = activeChatID?.chatID;
    if (!chatId) {
      return;
    }

    fetchChatOcrFiles(chatId).catch(() => {});
  }, [activeChatID?.chatID, fetchChatOcrFiles]);

  // Auto-download original files when opening an old chat
  useEffect(() => {
    const chatId = activeChatID?.chatID;
    if (!chatId) return;
    const extractedFiles = chatExtractedFiles[chatId];
    if (!extractedFiles || extractedFiles.length === 0) return;

    const filesToDownload = extractedFiles.filter((f) => {
      if (!f.file_id || !f.filename) return false;
      if (filePreviews[f.filename] || fileOriginalUrls[f.filename]) return false;
      if (fileDownloadProgress[f.filename] !== undefined) return false;
      return true;
    });

    if (filesToDownload.length === 0) return;

    let canceled = false;

    const downloadFile = async (file: { file_id: string; filename: string }) => {
      const { file_id, filename } = file;
      if (canceled) return;

      setFileDownloadProgress((prev) => ({ ...prev, [filename]: 0 }));

      try {
        const token = await getAccessTokenSilently();
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API_BASE}/api/chat/files/${chatId}/${file_id}/blob`, true);
        xhr.setRequestHeader('Authorization', `Auth ${token}`);
        xhr.responseType = 'blob';

        await new Promise<void>((resolve, reject) => {
          xhr.onprogress = (event) => {
            if (canceled) { xhr.abort(); reject(new Error('canceled')); return; }
            if (event.lengthComputable && event.total > 0) {
              const pct = Math.round((event.loaded / event.total) * 100);
              setFileDownloadProgress((prev) => ({ ...prev, [filename]: Math.min(pct, 99) }));
            }
          };
          xhr.onload = () => {
            if (canceled) { reject(new Error('canceled')); return; }
            if (xhr.status === 200) {
              const blob = xhr.response as Blob;
              const blobUrl = URL.createObjectURL(blob);
              const isPdf = isPdfFile(filename);
              const isImage = blob.type.startsWith('image/');

              if (isPdf) {
                setFileOriginalUrls((prev) => ({ ...prev, [filename]: blobUrl }));
                (async () => {
                  try {
                    const buffer = await blob.arrayBuffer();
                    const pdf = await pdfjs.getDocument({ data: buffer }).promise;
                    setNumPages((prev) => ({ ...(prev ?? {}), [filename]: pdf.numPages }));
                    const page = await pdf.getPage(1);
                    const initialViewport = page.getViewport({ scale: 1 });
                    const scale = 220 / initialViewport.width;
                    const viewport = page.getViewport({ scale });
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    if (ctx) {
                      await page.render({ canvasContext: ctx, viewport }).promise;
                      setFilePreviews((prev) => ({ ...prev, [filename]: canvas.toDataURL('image/png') }));
                    }
                  } catch {}
                })();
              } else if (isImage) {
                setFilePreviews((prev) => ({ ...prev, [filename]: blobUrl }));
              } else if (
                filename.toLowerCase().endsWith('.docx')
                || filename.toLowerCase().endsWith('.xlsx')
                || filename.toLowerCase().endsWith('.rtf')
              ) {
                // Binary/control-code formats — reading raw bytes as text dumps the
                // ZIP/OOXML garbage into the preview. The readable text already comes
                // from the backend extraction (the "Texto extraído (OCR)" tab); don't
                // build a raw-text preview here. Mirrors the upload guard above.
              } else {
                blob.text().then((text) => {
                  const content = text.slice(0, 5000);
                  setFileTextPreviews((prev) => ({
                    ...prev,
                    [filename]: { content, truncated: text.length > 5000 },
                  }));
                }).catch(() => {});
              }

              setFileDownloadProgress((prev) => ({ ...prev, [filename]: 100 }));
              resolve();
            } else {
              setFileDownloadProgress((prev) => ({ ...prev, [filename]: 100 }));
              resolve();
            }
          };
          xhr.onerror = () => {
            setFileDownloadProgress((prev) => ({ ...prev, [filename]: 100 }));
            resolve();
          };
          xhr.send();
        });
      } catch {
        if (!canceled) {
          setFileDownloadProgress((prev) => ({ ...prev, [filename]: 100 }));
        }
      }
    };

    const downloadAll = async () => {
      const queue = [...filesToDownload];
      const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
        while (queue.length > 0 && !canceled) {
          const file = queue.shift();
          if (file) await downloadFile(file);
        }
      });
      await Promise.all(workers);
    };

    downloadAll();

    return () => { canceled = true; };
  }, [activeChatID?.chatID, chatExtractedFiles, getAccessTokenSilently]);

  // Clear download progress when switching chats
  useEffect(() => {
    setFileDownloadProgress({});
  }, [activeChatID?.chatID]);

  useEffect(() => {
    if (!pendingOcrRefresh || inOcrPhase || uploadingFiles) {
      return;
    }

    const chatId = activeChatID?.chatID;
    if (!chatId) {
      setPendingOcrRefresh(false);
      return;
    }

    let canceled = false;

    const refresh = async () => {
      try {
        await fetchChatOcrFiles(chatId, true);
      } catch (error) {
        // fetchChatOcrFiles already reports the failure to the user
      } finally {
        if (!canceled) {
          setPendingOcrRefresh(false);
        }
      }
    };

    refresh();

    return () => {
      canceled = true;
    };
  }, [pendingOcrRefresh, inOcrPhase, uploadingFiles, activeChatID?.chatID, fetchChatOcrFiles]);

  // Checks for clicks outside of the rename chat menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (styledTextFieldRef.current && !styledTextFieldRef.current.contains(event.target as Node)) {
        handleCancelEditCancelMenu();
      }
    };


    // Attach event listener to document
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup event listener on component unmount
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Old batch audio upload useEffect removed — now using streaming STT via WebSocket

  // Poll background generation status for chats NOT being viewed
  useEffect(() => {
    if (backgroundGeneratingChatIds.size === 0) return;

    const interval = setInterval(async () => {
      try {
        const token = await getAccessTokenSilently();
        for (const chatId of Array.from(backgroundGeneratingChatIds)) {
          if (chatId === activeChatID.chatID) continue; // Handled by SSE below
          const res = await fetch(`${API_BASE}/api/chat/background-status/${chatId}`, {
            headers: { authorization: `Auth ${token}` },
          });
          if (!res.ok) continue;
          const data = await res.json();
          if (data.status === "done" || data.status === "cancelled" || data.status === "error") {
            setBackgroundGeneratingChatIds(prev => {
              const next = new Set(prev);
              next.delete(chatId);
              return next;
            });
            setCompletedBackgroundChatIds(prev => new Set(prev).add(chatId));
          }
        }
      } catch {}
    }, 3000);

    return () => clearInterval(interval);
  }, [backgroundGeneratingChatIds, activeChatID.chatID]);

  // Fresh tab / reload: backgroundGeneratingChatIds is in-memory PER TAB, so a
  // tab that didn't start the generation has no idea the chat is still running
  // server-side — the resume effect below would never fire and the chat looks
  // blank. On opening a chat, ask the backend; if it's generating, mark it so
  // the resume effect reconnects and replays the durable log from the start.
  useEffect(() => {
    const chatId = activeChatID.chatID;
    if (!chatId || backgroundGeneratingChatIds.has(chatId)) return;
    // This tab IS the live generator for this chat: the send's POST SSE is
    // attached and rendering. background-status says "generating" the moment
    // the run starts, so without this guard every send marked its own chat as
    // background and attached the bg-stream resume as a second consumer of
    // the same run (duplicate frame application; transient duplicate
    // "Pensando" blocks). Reload and switch-away/back are unaffected:
    // both clear generatingChatId / close the live SSE first.
    if (generatingChatIdRef.current === chatId && sseConnectionRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_BASE}/api/chat/background-status/${chatId}`, {
          headers: { authorization: `Auth ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || data.status !== "generating") return;
        // Re-check at resolve time: the fetch raced a send in this tab.
        if (generatingChatIdRef.current === chatId && sseConnectionRef.current) return;
        setBackgroundGeneratingChatIds(prev => new Set(prev).add(chatId));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [activeChatID.chatID]);

  // Resume a background-generating chat the user is currently viewing: the
  // backend keeps the SAME run going after a disconnect and tees every frame
  // into a durable event log. We replay that log from the start and apply each
  // frame with the SAME semantics as the live stream — text deltas,
  // deep_research_* progress, quota — then refresh from Mongo when it ends.
  // A transient drop reconnects with bounded backoff; the run never restarts.
  useEffect(() => {
    const chatId = activeChatID.chatID;
    if (!chatId || !backgroundGeneratingChatIds.has(chatId)) return;

    let cancelled = false;
    let attempt = 0;
    let retryTimer: any = null;
    setLoadingMessage("Gerando resposta...");

    const finishAndRefresh = async () => {
      setLoadingMessage("");
      clearDeepResearchForChat(chatId);
      setBackgroundGeneratingChatIds(prev => {
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
      // Refresh from Mongo for the authoritative final state (tool chips,
      // content_items, references, deep_research_metadata).
      try {
        const histToken = await getAccessTokenSilently();
        const histRes = await fetch(`${API_BASE}/api/chat/history/${chatId}`, {
          headers: { authorization: `Auth ${histToken}` },
        });
        if (histRes.ok) {
          const histData = await histRes.json();
          if (histData.messages && histData.messages.length > 0) {
            histData.messages.forEach((message: any) => {
              if (message.files != null) {
                message.filenames = message.files.map((file: any) => file.filename);
              }
            });
            setMessageHistory(histData.messages);
          }
        }
      } catch {}
      // The resume path must also refresh the OCR-extracted files so the
      // "ver texto extraido" lupa shows. The live send path does this via
      // pendingOcrRefresh; a resumed / fresh-tab completion never sets it,
      // leaving chatExtractedFiles cached empty -> hasOcrContentForMessage false.
      fetchChatOcrFiles(chatId, true).catch(() => {});
    };

    const connectOnce = async () => {
      // We always replay from the start of the log and SET (not append) the
      // assistant content, so a reconnect can never double the text regardless
      // of what was already shown. Deep research emits full-text frames
      // (replace); normal chat emits deltas (concatenate).
      let acc = "";
      let isDR = false;
      // We always replay from cursor 0, so rebuild DR state from scratch to
      // avoid duplicating gap-fill plan steps that append on each reconnect.
      clearDeepResearchForChat(chatId);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(buildBgStreamUrl(API_BASE, chatId, 0), {
          headers: { authorization: `Auth ${token}`, Accept: "text/event-stream" },
        });
        if (!response.ok || !response.body) {
          throw new Error(`bg-stream HTTP ${response.status}`);
        }
        attempt = 0; // a successful connect refills the backoff budget
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { frames, rest } = parseSSEFrames(buffer);
          buffer = rest;
          for (const frame of frames) {
            if (cancelled) break;
            const evt = frame.event;
            if (evt === "end") {
              await finishAndRefresh();
              cancelled = true;
              break;
            } else if (evt === "user_message") {
              // Render the user's question immediately on resume — it may not be
              // persisted to Mongo yet during slow file extraction. Dedup by
              // position so we don't double it once history already has it.
              try {
                const u = JSON.parse(frame.data);
                setMessageHistory(prev => {
                  if (prev.some((m: any) => m.role === "user" && m.position === u.position)) return prev;
                  // chat_id is required: the history-fetch effect blanks the chat when
                  // messageHistory[0].chat_id != activeChatID, so a user bubble without
                  // it gets clobbered to empty by a late (empty) history fetch.
                  const um: any = { role: "user", content: u.content, position: u.position, chat_id: chatId };
                  if (Array.isArray(u.filenames) && u.filenames.length) um.filenames = u.filenames;
                  return [...prev, um];
                });
              } catch {}
            } else if (evt === "message") {
              let chunk = "";
              try {
                const p = JSON.parse(frame.data);
                chunk = p.text ?? p.answer ?? p.content ?? "";
              } catch { chunk = ""; }
              if (!chunk) continue;
              acc = isDR ? chunk : acc + chunk;
              setLoadingMessage("");
              const text = acc;
              setMessageHistory(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  // Preserve non-text items the live stream already built
                  // (reasoning "Raciocínio", tool_action, code_execution); only
                  // (re)set the trailing text to the replayed full text.
                  // Reconstructing as pure [{text}] here was WIPING the
                  // reasoning/tool boxes mid-stream — they reappeared only after
                  // the end-of-turn Mongo refresh. The authoritative interleaving
                  // still comes from finishAndRefresh.
                  const items = Array.isArray((last as any).content) ? [...(last as any).content] : [];
                  let replaced = false;
                  for (let i = items.length - 1; i >= 0; i--) {
                    if ((items[i] as any).type === "text") { items[i] = { ...(items[i] as any), text }; replaced = true; break; }
                  }
                  if (!replaced) items.push({ type: "text", text } as any);
                  updated[updated.length - 1] = { ...last, content: items } as any;
                } else {
                  updated.push({ role: "assistant", content: [{ type: "text", text }] } as any);
                }
                return updated;
              });
            } else if (isDeepResearchEvent(evt)) {
              isDR = true;
              try {
                const payload = JSON.parse(frame.data);
                if (evt === "deep_research_status") {
                  updateDeepResearchForChat(chatId, prev => ({
                    ...prev,
                    phase: payload.phase || "",
                    progress: payload.progress || 0,
                    tokens: payload.input_tokens !== undefined
                      ? { input: payload.input_tokens, output: payload.output_tokens || 0 }
                      : prev.tokens,
                  }));
                  if (chatId === activeChatIDRef.current) {
                    const labels: Record<string, string> = {
                      planning: "Criando plano de pesquisa...",
                      researching: "Pesquisando em multiplas fontes...",
                      reviewing: "Verificando completude...",
                      synthesizing: "Escrevendo relatorio...",
                      done: "Pesquisa concluida!",
                    };
                    setLoadingMessage(labels[payload.phase] || "Pesquisando...");
                  }
                } else if (evt === "deep_research_plan") {
                  updateDeepResearchForChat(chatId, prev => ({
                    ...prev,
                    plan: payload.is_gap_fill ? [...prev.plan, ...(payload.steps || [])] : (payload.steps || []),
                  }));
                } else if (evt === "deep_research_step") {
                  updateDeepResearchForChat(chatId, prev => ({
                    ...prev,
                    steps: { ...prev.steps, [payload.step_id]: payload },
                  }));
                } else if (evt === "deep_research_metadata") {
                  const refs = payload.references;
                  setMessageHistory(prev => {
                    if (prev.length === 0) return prev;
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    const lastMsg = { ...updated[lastIdx] } as any;
                    lastMsg.deep_research_metadata = payload;
                    if (Array.isArray(refs) && refs.length > 0) lastMsg.references = refs;
                    updated[lastIdx] = lastMsg;
                    return updated;
                  });
                }
              } catch {}
            } else if (evt === "quota") {
              // Replay the free-user quota so the UI doesn't under-report usage
              // when a turn completes via the background-resume path.
              try {
                const q = JSON.parse(frame.data);
                setFreeQuota({
                  messages: Number(q.messages ?? 0),
                });
              } catch {}
            } else if (evt === "ocr_start") {
              // File OCR/extraction can be long; without this the resumed view
              // shows a generic spinner (looks stuck) while text is extracted.
              setInOcrPhase(true);
              setUploadingFiles(false);
              setLoadingMessage("Extraindo texto dos arquivos...");
            } else if (evt === "start") {
              // Generation began (OCR/extraction done) — switch the indicator.
              setInOcrPhase(false);
              setUploadingFiles(false);
              setLoadingMessage("Gerando resposta...");
            } else if (evt === "reasoning_delta") {
              // Replay the thinking stream with the same semantics as the live
              // SSE handler (it creates the assistant scaffold if needed), so a
              // resumed chat shows "Pensando" + the growing draft instead of a
              // bare spinner during the pre-answer phase.
              handleReasoningDelta({ data: frame.data });
            } else if (evt === "reasoning") {
              handleReasoning({ data: frame.data });
            }
            // tool/code frames are restored by the Mongo refresh on end;
            // we intentionally don't reconstruct those chips mid-resume.
          }
        }
        try { reader.cancel(); } catch {}
      } catch {
        // Transient drop: the run is still going server-side, so reconnect with
        // bounded backoff before giving up.
        if (cancelled) return;
        if (attempt < MAX_RECONNECT_ATTEMPTS) {
          const delay = getReconnectDelayMs(attempt);
          attempt += 1;
          retryTimer = setTimeout(() => { if (!cancelled) connectOnce(); }, delay);
        } else {
          setLoadingMessage("");
        }
      }
    };

    connectOnce();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      // Leaving a resuming chat: drop its transient loading message so it can't
      // bleed into the chat we switch to (loadingMessage is global state).
      setLoadingMessage("");
    };
    // Depend on the active chat's MEMBERSHIP, not the Set identity, so an
    // unrelated chat finishing/starting doesn't tear down + replay this stream.
  }, [activeChatID.chatID, backgroundGeneratingChatIds.has(activeChatID.chatID)]);

  const fetchMoreChats = async () => {
    if (!hasMoreChats || loadingChatHistory) {
      return;
    }
    setLoadingChatHistory(true);
    try {
      const url = API_BASE + "/api/chat/list";
      const access_token = await getAccessTokenSilently();
      const offset = userChats.length;

      const response = await fetch(`${url}?offset=${offset}`, {
        method: 'GET',
        headers: {
          Authorization: `Auth ${access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data["chats"].length === 0) {
          setHasMoreChats(false);
        }
        // Pages can overlap at the boundary (the server filters out empty
        // chats after slicing, so offset=userChats.length lags the raw
        // positions it consumed). Dedupe by chat_id so the same conversation
        // can't render twice / share a delete handler.
        setUserChats(mergeUniqueChats(userChats, data["chats"]));
      }
    } catch (error) {
      toast.error("Desculpe, algo deu errado, tente novamente", TOAST_CONFIG);
    } finally {
      setLoadingChatHistory(false);
    }
  };

  // Search chats function
  const searchChats = async (query: string) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const access_token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE}/api/chat/search?q=${encodeURIComponent(query.trim())}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Auth ${access_token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data["chats"]);
      }
    } catch (error) {
      console.error('Error searching chats:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounce (500ms)
    searchTimeoutRef.current = setTimeout(() => {
      searchChats(query);
    }, 500);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Create context value with all shared state and handlers
  const playgroundContextValue: PlaygroundContextValue = {
    // Theme & Layout
    dark,
    toggleDark,
    isMobile,
    sidebarWidth,
    sidebarLogosLoaded,
    inputBottomOffset,

    // Sidebar State
    openSidebar,
    effectiveSidebarCollapsed,
    handleSidebarToggle,
    toggleSidebarCollapse,

    // Chat State (from ChatControlContext)
    activeChatID,
    setActiveChatID,
    messageHistory,
    setMessageHistory,
    userChats,
    setUserChats,
    modelChat,
    setModelChat,
    loadingHistory,
    loadingInitialChats,
    loadingChatHistory,

    // Chat List Management
    editingChatId,
    setEditingChatId,
    editedChatTitle,
    setEditedChatTitle,
    hoveredChatId,
    setHoveredChatId,
    chatMenuClickedChatId,
    anchorElChatMenu,
    openChatMenu,
    handleOpenChatMenu,
    handleCloseChatMenu,
    handleMenuItemClick,
    handleChatItemClick,
    handleRenameConversation,
    handleNewChat,
    fetchMoreChats,
    formatChatTitle,

    // Chat Search
    searchQuery,
    searchResults,
    isSearching,
    handleSearchChange,
    clearSearch,

    // Model Selection
    openDropdownMenu,
    setOpenDropdownMenu,
    dropdownMenuOptions,
    modelDescriptions,
    handleSelectOption,

    // User Menu
    openUserMenu,
    setOpenUserMenu,
    userPicture,
    userEmail: user?.email,
    displayedName,
    handleLogoutButton,
    handlePlatformButton,

    // Modals
    setOpenHelpModal,
    setOpenAccountModal,
    setOpenSubscriptionModal,
    setOpenERPModal,
    handleClearConversationButton,
    promptBankOpen,
    setPromptBankOpen,

    // Message Generation
    messageBeingGenerated,
    loadingGeneration,
    loadingMessage,
    extractingFiles,
    chatErrorMessage,
    allowErrorRetry,
    showPlanUpgradeOption,
    backgroundGeneratingChatIds,
    completedBackgroundChatIds,
    cancellingBackground,

    // Debug Mode
    debugMode,
    currentToolCalls,

    // Message Editing
    isEditing,
    editingMessageId,
    messageBeingEdited,
    handleEditMessage,
    handleSaveEditedMessage,
    handleCancelEdit,
    handleEditMessageInputChange,

    // Message Actions
    copiedIndex,
    handleCopyToClipboard,
    handleLikeMessage,
    handleDislikeClick,
    handleRegenerateMessage,
    handleRetryMessage,

    // File Handling
    filePreviews,
    fileTextPreviews,
    numPages,
    fileDownloadProgress,
    selectedFiles,
    setSelectedFiles,
    fileInputKey,
    fileInputRef,
    handleFileUpload,
    handleOpenPreview,
    renderSelectedFilesPreview,
    modelsWithFileUploadAllowed,

    // OCR
    loadingOcrModal,
    handleOpenOcrModal,
    hasOcrContentForMessage,

    // Quota & Plan
    planStatus,
    freeQuota,
    quotaBadgeIndex,
    renderQuotaInfoTrigger,

    // Message Formatting
    formatAssistantMessage,
    getVisibleReferences,
    getCitedReferencesWithHistory,

    // Input Area
    currentMessage,
    setCurrentMessage,
    handleNewMessageInputChange,
    handleButtonDownTextInput,
    handleSubmit,
    isAtBottom,
    smoothScrollToEnd,

    // Audio Recording
    isRecording,
    status,
    sendingAudio,
    audioTooltipOpen,
    setAudioTooltipOpen,
    handleAudioButtonClick,
    handleStopClick,
    partialTranscript,
    sttTranscribing,

    // Web Search
    webSearch,
    setWebSearch,
    modelsWithWebSearchAllowed,

    // Data Ocean
    dataOcean,
    setDataOcean,
    modelsWithDataOceanToggle: Array.from(DATA_OCEAN_TOGGLE_MODELS),

    // Usar competidor (gated by REACT_APP_ENABLE_COMPETITOR build flag)
    useCompetitor,
    setUseCompetitor,

    // Reasoning
    reasoning,
    setReasoning,

    // Precise location (opt-in)
    usePreciseLocation,
    togglePreciseLocation,
    locatingUser,

    // Code Execution
    codeExecution,
    setCodeExecution,
    modelsWithCodeExecutionAllowed,

    // Deep Research
    deepResearch,
    setDeepResearch,
    deepResearchMode,
    setDeepResearchMode,
    deepResearchPhase,
    deepResearchProgress,
    deepResearchPlan,
    deepResearchSteps,
    deepResearchTokens,
    panoramaPhase,
    panoramaProgress,
    panoramaNSearches,
    panoramaNDocs,
    panoramaNTheses,
    panoramaInputTokens,
    panoramaOutputTokens,
    panoramaDetail,

    // Refs
    endOfChatMessageMarkerRef,
  };

  return (
    <>
      <MuiThemeProvider theme={dark ? muiDark : muiLight}>
        <CssBaseline enableColorScheme />
        <StyledThemeProvider theme={dark ? scDark : scLight}>
          <PlaygroundProvider value={playgroundContextValue}>
          {/* GradientBackdrop removed */}
          <ToastContainer />
          <LocationPromptModal
            open={showLocationPrompt}
            dark={dark}
            accentColor={getModelColor(modelChat, dark).primary}
            onAllow={handleLocationPromptAllow}
            onDismiss={handleLocationPromptDismiss}
          />
          <HiddenLogoPreload aria-hidden="true">
            <img
              src={maritacaLogoPreta}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <img
              src={maritacaLogoBranca}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </HiddenLogoPreload>

          <SideMenu />

            {showProjectsView ? (
              <ProjectsView
                onClose={() => setShowProjectsView(false)}
                onNavigateToChat={(chatId) => { setShowProjectsView(false); handleChatItemClick(chatId); }}
                onNewChatInProject={() => { setShowProjectsView(false); handleNewChat(); }}
                userChats={userChats}
              />
            ) : (<>
          <Content ref={MessageAreaDivRef} $isMobile={isMobile} $sidebarWidth={sidebarWidth}>
            {shouldShowHistoryOverlay && (
              <HistoryLoadingOverlay role="status" aria-live="polite" aria-label="Carregando conversa">
                <SkeletonInner>
                  <SkeletonUserBubble />
                  <SkeletonAssistantBlock>
                    <SkeletonBar style={{ width: '78%' }} />
                    <SkeletonBar style={{ width: '70%' }} />
                    <SkeletonBar style={{ width: '82%' }} />
                    <SkeletonBar style={{ width: '60%' }} />
                    <SkeletonBar style={{ width: '74%' }} />
                    <SkeletonBar style={{ width: '52%' }} />
                    <SkeletonBar style={{ width: '66%' }} />
                    <SkeletonBar style={{ width: '40%' }} />
                  </SkeletonAssistantBlock>
                </SkeletonInner>
              </HistoryLoadingOverlay>
            )}
            <ChatHeader />
            <MessagesArea
              $isMobile={isMobile}
              $sidebarWidth={sidebarWidth}
              $isLanding={shouldShowLandingPrompt}
            >
              <HelpForm openModal={openHelpModal} setOpenModal={setOpenHelpModal} />
              {!shouldShowHistoryOverlay && <MessageList />}
            </MessagesArea>
            {PromptSuggestionsComponent()}
            {isMobile && (
              <Popover
                id={quotaPopoverId}
                open={isQuotaInfoOpen}
                anchorEl={quotaInfoAnchorEl}
                onClose={handleQuotaInfoClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.16)' } }}
              >
                {quotaInfoContent(quotaInfoVariant)}
              </Popover>
            )}

            {(!loadingHistory && isAuthenticated && !shouldShowLandingPrompt) && (
              <ChatInputArea />
            )}
          </Content>
          </>)}
      <FilePreviewModal
        open={previewOpen && (previewType === 'text' || !!previewSrc || previewOcrAvailable)}
        src={previewSrc}
        title={previewTitle}
        type={previewType}
        textContent={previewTextContent}
        textTruncated={previewTextTruncated}
        textPreviewLimitLabel={textPreviewLimitLabel}
        ocrText={previewOcrText}
        ocrAvailable={previewOcrAvailable}
        ocrLoading={previewOcrLoading}
        onClose={handleClosePreview}
      />
      {selectedMessageId !== null && (
        <FeedbackModalExtracted
          open={showFeedback}
          messageId={selectedMessageId}
          onClose={handleCloseFeedback}
          onSubmit={handleSendFeedback}
        />
      )}
      <AccountModal openModal={openAccountModal} setOpenModal={setOpenAccountModal} />
      <SubscriptionModal openModal={openSubscriptionModal} setOpenModal={setOpenSubscriptionModal} onSubscriptionChange={fetchPlanStatus} />
      <ERPConnectionModal openModal={openERPModal} setOpenModal={setOpenERPModal} />

      {/* Prompt Bank Modal for landing page */}
      <PromptBankModal
        open={promptBankOpen}
        onClose={() => {
          setPromptBankOpen(false);
          setPromptBankInitialCategory(undefined);
        }}
        onSelectPrompt={handleSelectPrompt}
        initialCategoryId={promptBankInitialCategory}
        onPromptsChange={() => setPromptsRefreshTrigger((prev) => prev + 1)}
        modelName={modelChat}
      />

      {/* Prompt Filler View */}
      {selectedPrompt && (
        <PromptFillerView
          prompt={selectedPrompt}
          onClose={handleClosePromptFiller}
          onBack={handleBackToPromptBank}
          onSubmit={handlePromptSubmit}
        />
      )}
          </PlaygroundProvider>
        </StyledThemeProvider>
      </MuiThemeProvider>
    </>
  );
}

export default App;

const StyledKatexContainer = styled.div`
  & .katex { font-size: 1.2rem; }
  & .katex-centered { text-align: center; margin: 15px; }
  .katex-centered .katex { font-size: 1.6rem; }
`;

export const StyledReactMarkdown = styled(ReactMarkdown)`
  max-width: 100%;
  font-family: inherit;
  @media (max-width: 600px) { max-width: 100%; }

  /* Markdown headings */
  h1 { font-size: 1.5rem; font-weight: 700; margin: 1.2em 0 0.5em; line-height: 1.3; }
  h2 { font-size: 1.25rem; font-weight: 600; margin: 1em 0 0.4em; line-height: 1.3; }
  h3 { font-size: 1.1rem; font-weight: 600; margin: 0.8em 0 0.3em; line-height: 1.4; }
  h4 { font-size: 1rem; font-weight: 600; margin: 0.6em 0 0.2em; line-height: 1.4; }
  h5, h6 { font-size: 0.9rem; font-weight: 600; margin: 0.5em 0 0.2em; line-height: 1.4; }

  /* Markdown tables */
  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    margin: 12px 0;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.table_border};
  }

  thead {
    background: ${({ theme }) => theme.table_header_bg};
  }

  thead th {
    color: ${({ theme }) => theme.table_header_color};
    font-weight: 600;
    text-align: left;
    padding: 10px 14px;
    border-bottom: 2px solid ${({ theme }) => theme.table_border};
    white-space: nowrap;
  }

  tbody tr {
    transition: background-color 0.15s ease;
  }

  tbody tr:nth-child(even) {
    background: ${({ theme }) => theme.table_row_alt_bg};
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.table_row_hover_bg};
  }

  tbody td {
    padding: 8px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.table_border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

// Stable table rendered outside ReactMarkdown to prevent flickering during streaming.
const StableTableBlock = React.memo(({ markdown }: { markdown: string }) => (
  <TableBlock>
    <StyledReactMarkdown remarkPlugins={[remarkGfm]} components={{
      table: ({ node, ...props }: any) => <table {...props} />,
    }}>
      {markdown}
    </StyledReactMarkdown>
  </TableBlock>
));
StableTableBlock.displayName = 'StableTableBlock';

const TableLoadingPlaceholder = () => (
  <TableLoadingContainer>
    <TableLoadingSpinner />
    <TableLoadingText>Gerando tabela...</TableLoadingText>
  </TableLoadingContainer>
);

const TableLoadingContainer = styled.div`
  background: ${({ theme }) => theme.chart_bg};
  border: 1px solid ${({ theme }) => theme.chart_border};
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 100%;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const TableLoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.chart_border};
  border-top-color: ${({ theme }) => theme.blue_color};
  border-radius: 50%;
  animation: table-spin 0.8s linear infinite;

  @keyframes table-spin {
    to { transform: rotate(360deg); }
  }
`;

const TableLoadingText = styled.span`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.875rem;
`;

const StyledMaritacaLogo = styled.img`
  width: 94px;
  height: 101px;
  opacity: 1;
  margin: 0px;
  margin-bottom: 0px;
`;

const getAttachmentThumbSize = (count: number) => {
  if (count <= 1) return 160;
  if (count <= 2) return 148;
  if (count <= 4) return 128;
  if (count <= 6) return 108;
  return 92;
};

const AllFilesContainer = styled.div<{ fileCount: number; $isMobile?: boolean }>`
  --thumb-size: ${({ fileCount, $isMobile }) => {
    if ($isMobile) {
      return '120px'; // Tamanho menor para mobile
    }
    return `${getAttachmentThumbSize(fileCount)}px`;
  }};
  --thumb-height: var(--thumb-size);
  display: grid;
  grid-template-columns: ${({ $isMobile }) =>
    $isMobile
      ? 'repeat(auto-fill, minmax(120px, max-content))'
      : 'repeat(auto-fill, minmax(var(--thumb-size), max-content))'
  };
  gap: 12px;
  width: 100%;
  margin: 10px 0 0;
  padding: 0 4px 4px;
  justify-content: flex-start;
  justify-items: start;
  align-items: flex-start;
  max-width: ${({ $isMobile }) => $isMobile ? 'calc(100vw - 40px)' : '100%'};
  ${({ $isMobile, fileCount }) => $isMobile && fileCount > 6 && `
    max-height: 400px;
    overflow-y: auto;
    grid-template-columns: repeat(2, 1fr);
  `}
  /* When there are many files on non-mobile, constrain height and allow scrolling */
  ${({ $isMobile, fileCount }) => !$isMobile && fileCount > 8 && `
    max-height: 360px;
    overflow-y: auto;
  `}
`;

const Clickable = styled.span`
  display: inline-flex;
  cursor: pointer;
`;

// Input thumbnail (square, larger)
const FileThumb = styled.img`
  width: var(--thumb-size, 160px);
  height: var(--thumb-height, var(--thumb-size, 160px));
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.file_thumb_border};
`;

// Helper function to get placeholder colors based on variant
const getPlaceholderColors = (theme: any, variant: PlaceholderVariant) => {
  const colorMap = {
    pdf: {
      cardBg: variant === 'pdf' ? theme.file_pdf_card_bg : theme.file_pdf_card_bg,
      border: theme.file_pdf_border,
      iconBg: theme.file_pdf_icon_bg,
      iconColor: theme.file_pdf_icon_color,
      nameColor: theme.file_pdf_name_color,
      typeColor: theme.file_pdf_type_color,
    },
    image: {
      cardBg: theme.file_image_card_bg,
      border: theme.file_image_border,
      iconBg: theme.file_image_icon_bg,
      iconColor: theme.file_image_icon_color,
      nameColor: theme.file_image_name_color,
      typeColor: theme.file_image_type_color,
    },
    file: {
      cardBg: theme.file_generic_card_bg,
      border: theme.file_generic_border,
      iconBg: theme.file_generic_icon_bg,
      iconColor: theme.file_generic_icon_color,
      nameColor: theme.file_generic_name_color,
      typeColor: theme.file_generic_type_color,
    },
    textCsv: {
      cardBg: theme.file_csv_card_bg,
      border: theme.file_csv_border,
      iconBg: theme.file_csv_icon_bg,
      iconColor: theme.file_csv_icon_color,
      nameColor: theme.file_csv_name_color,
      typeColor: theme.file_csv_type_color,
    },
    textTxt: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    textMd: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    docx: {
      cardBg: theme.file_generic_card_bg,
      border: theme.file_generic_border,
      iconBg: theme.file_generic_icon_bg,
      iconColor: theme.file_generic_icon_color,
      nameColor: theme.file_generic_name_color,
      typeColor: theme.file_generic_type_color,
    },
    xlsx: {
      cardBg: theme.file_csv_card_bg,
      border: theme.file_csv_border,
      iconBg: theme.file_csv_icon_bg,
      iconColor: theme.file_csv_icon_color,
      nameColor: theme.file_csv_name_color,
      typeColor: theme.file_csv_type_color,
    },
  };
  return colorMap[variant];
};

const FileTextPreviewThumb = styled.div<{ $variant: PlaceholderVariant }>`
  width: var(--thumb-size, 160px);
  height: var(--thumb-height, var(--thumb-size, 160px));
  border-radius: 6px;
  border: 1px solid ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).border};
  background: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).cardBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconBg};
  padding: 16px;
  text-align: center;
  & svg {
    font-size: 2.25rem;
  }
`;

const FileTextPreviewName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileTextPreviewType = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.7;
`;

const TextPlaceholderIcon = MUIStyled(ArticleIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const AttachmentPreviewStack = styled.div<{ $context: 'landing' | 'input' }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: ${({ $context }) => ($context === 'landing' ? '8px 0 0' : '0')};
`;

const AttachmentSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AttachmentSummaryText = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const AttachmentSummaryHint = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.secondary_text_color};
`;

const CondensedAttachmentList = styled.div<{ $context: 'landing' | 'input'; fileCount?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ $context }) => ($context === 'landing' ? '0 4px 4px' : '0 6px 6px')};
  /* If there are many condensed items, limit height and enable scrolling */
  ${({ fileCount }) => fileCount && fileCount > 6 && `
    max-height: 300px;
    overflow-y: auto;
  `}
`;

const CondensedAttachmentRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.file_generic_border};
  border-radius: 10px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.file_generic_card_bg};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.file_generic_icon_bg};
    transform: translateY(-1px);
  }
`;

const CondensedAttachmentMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const CondensedAttachmentIcon = styled.div<{ $variant: PlaceholderVariant }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconBg};
  color: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconColor};
`;

const CondensedAttachmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const CondensedAttachmentName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CondensedAttachmentMeta = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.secondary_text_color};
`;

const CondensedAttachmentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AttachmentPagePill = styled.span`
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.file_generic_border};
  padding: 2px 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font_color};
  background: ${({ theme }) => theme.file_generic_card_bg};
`;

const CondensedAttachmentRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.secondary_text_color};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.font_color};
  }
`;

const FileContainer = styled.span`
  display: flex;
  background-color: transparent;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  margin-right: 0;
  width: var(--thumb-size, 160px);
  flex: 0 0 var(--thumb-size, 160px);
`;

const ThumbWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: var(--thumb-size, 160px);
  height: var(--thumb-height, var(--thumb-size, 160px));
  align-items: center;
  justify-content: center;
`;

const PageCountBadge = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  line-height: 1;
  color: white;
  background: ${({ theme }) => theme.file_page_badge_bg};
  border: 1px solid ${({ theme }) => theme.file_page_badge_border};
  border-radius: 999px;
  pointer-events: none;
`;

const RemoveOverlayButton = styled.button`
  position: absolute;
  top: 6px;
  right: 10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.file_remove_overlay_border};
  background: ${({ theme }) => theme.file_remove_overlay_bg};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: ${({ theme }) => theme.file_remove_overlay_hover}; }
`;

const RemoveXIcon = styled(CloseIcon)`
  font-size: 0.95rem;
`;


const Content = styled.div<{ $isMobile: boolean; $sidebarWidth: number }>`
  position: absolute;
  top: 0;
  left: ${({ $isMobile, $sidebarWidth }) => 
    $isMobile ? '0' : `${$sidebarWidth}px`};
  width: ${({ $isMobile, $sidebarWidth }) => 
    $isMobile ? '100vw' : `calc(100vw - ${$sidebarWidth}px)`};
  height: 100vh;
  padding: 0;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  transition: left 0.18s ease-out, width 0.18s ease-out;
  overscroll-behavior: contain;
`;

const HiddenLogoPreload = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

const GradientBackdrop = styled.div<{ $modelChat: string }>`
  position: fixed;
  top: 0;
  left: 0;
  width: calc(100vw);
  height: ${({ theme }) => (theme.mode === 'dark' ? '50px' : '50px')};
  pointer-events: none;
  z-index: 5;
  opacity: 1;
  background-color: transparent;
  transition: background-image 0.4s ease, opacity 0.4s ease;
  background-image: ${({ theme, $modelChat }) => {
    // Cores da marca: #1a3c34, #70d44b, #ff7f40, #e6f57b, #bddfc5
    if (theme.mode === 'dark') {
      const modelPalettes: { [key: string]: { strong: string; soft: string; fade: string } } = {
        'sabia-4-pro': {
          strong: 'rgba(112, 212, 75, 0.22)',   // #70d44b verde
          soft: 'rgba(112, 212, 75, 0.11)',
          fade: 'rgba(112, 212, 75, 0)',
        },
        'sabia-4': {
          strong: 'rgba(112, 212, 75, 0.22)',   // #70d44b verde
          soft: 'rgba(112, 212, 75, 0.11)',
          fade: 'rgba(112, 212, 75, 0)',
        },
        'sabiazinho-4': {
          strong: 'rgba(120, 170, 135, 0.32)',  // #bddfc5 verde água/menta (mais escuro)
          soft: 'rgba(120, 170, 135, 0.16)',
          fade: 'rgba(120, 170, 135, 0)',
        },
        'sabia-3.1': {
          strong: 'rgba(26, 60, 52, 0.20)',     // #1a3c34 verde escuro (mais claro)
          soft: 'rgba(26, 60, 52, 0.10)',
          fade: 'rgba(26, 60, 52, 0)',
        },
        'sabia-3': {
          strong: 'rgba(230, 245, 123, 0.22)',  // #e6f57b lima
          soft: 'rgba(230, 245, 123, 0.11)',
          fade: 'rgba(230, 245, 123, 0)',
        },
        'sabiazinho-3': {
          strong: 'rgba(255, 127, 64, 0.24)',   // #ff7f40 laranja
          soft: 'rgba(255, 127, 64, 0.12)',
          fade: 'rgba(255, 127, 64, 0)',
        },
      };

      const fallbackPalette = {
        strong: 'rgba(112, 212, 75, 0.28)',     // #70d44b verde (fallback)
        soft: 'rgba(112, 212, 75, 0.14)',
        fade: 'rgba(112, 212, 75, 0)',
      };

      const { strong, soft, fade } = modelPalettes[$modelChat] || fallbackPalette;
      return `linear-gradient(180deg, ${strong} 0%, ${strong} 24%, ${soft} 62%, ${fade} 100%)`;
    }

    // Cores da marca: #1a3c34, #70d44b, #ff7f40, #e6f57b, #bddfc5
    const lightPalettes: { [key: string]: { color: string; fade: string } } = {
      'sabia-4-pro': {
        color: 'rgba(112, 212, 75, 0.35)',      // #70d44b verde
        fade: 'rgba(112, 212, 75, 0)',
      },
      'sabia-4': {
        color: 'rgba(112, 212, 75, 0.35)',      // #70d44b verde
        fade: 'rgba(112, 212, 75, 0)',
      },
      'sabiazinho-4': {
        color: 'rgba(120, 170, 135, 0.50)',     // #bddfc5 verde água/menta (mais escuro)
        fade: 'rgba(120, 170, 135, 0)',
      },
      'sabia-3.1': {
        color: 'rgba(26, 60, 52, 0.25)',        // #1a3c34 verde escuro (mais claro)
        fade: 'rgba(26, 60, 52, 0)',
      },
      'sabia-3': {
        color: 'rgba(230, 245, 123, 0.45)',     // #e6f57b lima
        fade: 'rgba(230, 245, 123, 0)',
      },
      'sabiazinho-3': {
        color: 'rgba(255, 127, 64, 0.35)',      // #ff7f40 laranja
        fade: 'rgba(255, 127, 64, 0)',
      },
    };

    const lightFallback = {
      color: 'rgba(112, 212, 75, 0.4)',         // #70d44b verde (fallback)
      fade: 'rgba(112, 212, 75, 0)',
    };

    const { color, fade } = lightPalettes[$modelChat] || lightFallback;
    return `linear-gradient(180deg, ${color} 0%, ${color} 28%, ${fade} 100%)`;
  }};
`;

const MessagesArea = styled.div<{ $isMobile: boolean; $sidebarWidth: number; $isLanding: boolean }>`
  display: ${({ $isLanding }) => ($isLanding ? 'none' : 'block')};
  width: 100%;
  max-width: ${({ $isMobile, $sidebarWidth }) =>
    $isMobile
      ? 'clamp(260px, calc(100vw - 40px), 1050px)'
      : `clamp(260px, calc(100vw - ${Math.round($sidebarWidth + SIDEBAR_CONTENT_GAP)}px), 1050px)`};
  margin-top: 50px;
  padding-top: ${({ $isMobile }) =>
    $isMobile ? `12px` : `24px`};
  padding-bottom: 20px;
  background-color: transparent;
  position: relative;
  z-index: 0;
  min-height: ${({ $isMobile }) =>
    $isMobile ? 'auto' : 'calc(100vh - 130px)'};
  max-height: calc(100vh - 82px);
  overflow-y: visible;
  
  & table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    margin: 0.5rem 0;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.table_border};
  }
  & thead {
    background: ${({ theme }) => theme.table_header_bg};
  }
  & thead th {
    color: ${({ theme }) => theme.table_header_color};
    font-weight: 600;
    text-align: left;
    padding: 10px 14px;
    border-bottom: 2px solid ${({ theme }) => theme.table_border};
  }
  & tbody tr:nth-child(even) {
    background: ${({ theme }) => theme.table_row_alt_bg};
  }
  & tbody tr:hover {
    background: ${({ theme }) => theme.table_row_hover_bg};
  }
  & tbody td {
    padding: 8px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.table_border};
  }
  & tbody tr:last-child td {
    border-bottom: none;
  }
  & ol {
    list-style-type: inside;
    padding: 0px;
    padding-left: 1.625rem;
    margin: 0.2rem;
    white-space: wrap;
  }
  & ul {
    white-space: wrap;
  }
  & li {
    margin: 0.2rem 0;
    white-space: wrap;
    &::marker { padding-left: 0.5rem; }
  }
  & p {
    margin: 0.5rem 0;
    padding-left: 0;
    white-space: wrap;
  }
  @media (max-width: 600px) {
    max-width: clamp(260px, calc(100vw - 40px), 1050px);
  }
`;

const shimmer = keyframes`
  0%   { opacity: 0.55; }
  50%  { opacity: 0.95; }
  100% { opacity: 0.55; }
`;

const HistoryLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  padding: 60px clamp(24px, 6vw, 72px) 120px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  /* Solid background so the chat layout behind isn't visible through. */
  background: ${({ theme }) => theme.background || theme.body_bg || (theme.mode === 'dark' ? '#0f1115' : '#ffffff')};
  z-index: 9000;
  overflow: hidden;
`;

const SkeletonInner = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 36px;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

const SkeletonUserBubble = styled.div`
  align-self: flex-end;
  width: 220px;
  height: 44px;
  border-radius: 22px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'};
`;

const SkeletonAssistantBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SkeletonBar = styled.div`
  height: 22px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'};
`;

const ActionIconButton = styled(IconButton)`
  && { color: ${({ theme }) => theme.action_icon_color}; }
`;

const LandingPromptWrapper = styled.div<{ $isMobile: boolean; $keyboardOffset?: number }>`
  flex: 1;
  width: 100%;
  min-height: calc(100vh - 160px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $isMobile, $keyboardOffset = 0 }) =>
    $isMobile ? `110px 16px calc(70px + ${$keyboardOffset}px)` : '150px 32px 90px'};
  box-sizing: border-box;

  ${({ $isMobile, $keyboardOffset = 0 }) =>
    $isMobile
      ? `
    @supports (padding-bottom: calc(1px + env(safe-area-inset-bottom))) {
      padding-bottom: calc(70px + ${$keyboardOffset}px + env(safe-area-inset-bottom));
    }
    @supports (padding-bottom: calc(1px + constant(safe-area-inset-bottom))) {
      padding-bottom: calc(70px + ${$keyboardOffset}px + constant(safe-area-inset-bottom));
    }
    @media (max-height: 740px) {
      padding-top: 90px;
      padding-bottom: calc(60px + ${$keyboardOffset}px);
    }
  `
      : `
    @media (max-height: 740px) {
      padding-top: 120px;
      padding-bottom: 60px;
    }
  `}
`;

const PromptSection = styled.section`
  width: clamp(260px, 100%, 1050px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;

  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    70% {
      opacity: 0.9;
      transform: translateY(-2px);
    }
    100% {
      opacity: 1;
      transform: none;
    }
  }
`;

// Landing (Google-like) search bar styles
const LandingSearchBar = styled.div`
  width: 100%;
`;

const LandingHeadline = styled.h1`
  margin: 0;
  margin-bottom: 40px;
  margin-top: 25px;
  font-size: 1.8rem;
  font-weight: 400;
  text-align: center;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Noka', 'Lexend';
`;
